import React, { FormEvent, useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  acknowledgeCarrierReservationInAPI,
  acknowledgeDeliveryReservationInAPI,
  CarrierReservationAcknowledgementDecision,
  CarrierReservationCategory,
  CarrierTakeoverCheckRequest,
  CarrierTakeoverCheckResult,
  CarrierTakeoverRequestedCheck,
  CarriageAccountingType,
  CarriageImpedimentInstructionTargetRole,
  CarriageImpedimentRequest,
  CarriageImpedimentSaleReason,
  CarriageImpedimentSaleProcedureBasis,
  CarriageImpedimentType,
  CarrierLiabilityArticle17_4cActorCapacity,
  CarrierLiabilityArticle17_4dSusceptibilityBasis,
  CarrierLiabilityArticle17_4eDeficiencyBasis,
  CarrierLiabilityIncidentRequest,
  CarrierLiabilityIncidentSpecialRisk,
  CarrierLiabilityIncidentType,
  Article18_2ClaimantRebuttalRequest,
  confirmDeliveryInAPI,
  DeliveryReservationAcknowledgementDecision,
  DeliveryReservationCategory,
  DeliveryTimingAssessmentRequest,
  DeliveryTimingAssessmentType,
  getSupplementaryDocumentDownloadUrlInAPI,
  listSupplementaryDocumentsInAPI,
  CodDeliveryOmissionType,
  CompensationPaymentEvidenceType,
  CompensationPaymentRequest,
  RecoveryDeliverySettlementRequest,
  RecoveredGoodsDispositionActionType,
  RecoveredGoodsDispositionBasis,
  RecoveredGoodsDispositionRequest,
  CompensationAssessmentRequest,
  CompensationAssessmentType,
  CompensationGoodsValueBasis,
  CompensationInterestRequest,
  CompensationInterestRecordType,
  CompensationInterestStartBasis,
  CompensationLiabilityLimitBasis,
  ConsignmentNoteCopyRecordType,
  ConsignmentNoteCopyRequest,
  EcmrReceiptAccessRequest,
  EcmrReceiptAccessSenderRequest,
  MultimodalCarriageRecordType,
  MultimodalCarriageRequest,
  SenderParticularsResponsibilityCategory,
  SenderParticularsResponsibilityRecordType,
  SenderParticularsResponsibilityRequest,
  ClaimReservationRecordType,
  ClaimReservationRequest,
  DisputeProcedureClaimCategory,
  DisputeProcedureLimitationPeriodBasis,
  DisputeProcedureLimitationStartBasis,
  DisputeProcedureRecordType,
  DisputeProcedureRequest,
  ProhibitedClauseRecordType,
  ProhibitedClauseRequest,
  SuccessiveCarrierAllocationBasis,
  SuccessiveCarrierRecordType,
  SuccessiveCarrierRecoveryLimitationStartBasis,
  SuccessiveCarrierRequest,
  LiabilityLimitationPersonRole,
  LiabilityLimitationRequest,
  LiabilityLimitationRecordType,
  DangerousGoodsCarrierActionType,
  DangerousGoodsNoticeRequest,
  DangerousGoodsNoticeType,
  ConsigneeDeliveryRequestType,
  ConsigneeDeliveryRequestRequest,
  DisposalInstructionRequest,
  DisposalInstructionType,
  DisposalExecutionNoticeTargetRole,
  recordCarrierTakeoverCheckInAPI,
  recordCarrierReservationInAPI,
  recordCarriageAccountingInAPI,
  recordCarriageImpedimentInAPI,
  recordCarrierLiabilityIncidentInAPI,
  recordArticle18_2ClaimantRebuttalInAPI,
  recordCodDeliveryOmissionInAPI,
  recordCompensationPaymentInAPI,
  recordRecoveryDeliverySettlementInAPI,
  recordRecoveredGoodsDispositionInAPI,
  recordCompensationAssessmentInAPI,
  recordCompensationInterestInAPI,
  recordClaimReservationInAPI,
  recordConsignmentNoteCopyInAPI,
  recordEcmrReceiptAccessInAPI,
  requestEcmrReceiptAccessInAPI,
  recordMultimodalCarriageInAPI,
  recordSenderParticularsResponsibilityInAPI,
  recordDisputeProcedureInAPI,
  recordProhibitedClauseInAPI,
  recordSuccessiveCarrierInAPI,
  recordLiabilityLimitationInAPI,
  recordConsigneeDeliveryRequestInAPI,
  recordDangerousGoodsNoticeInAPI,
  recordDeliveryTimingAssessmentInAPI,
  recordDisposalExecutionNoticeInAPI,
  recordDisposalInstructionInAPI,
  recordReplacementByOtherMeansInAPI,
  ReplacementByOtherMeansMedium,
  requestCarrierTakeoverCheckInAPI,
  SupplementaryDocumentEvidence,
  uploadSupplementaryDocumentInAPI,
} from '../services/eCMRService';
import { CmrArticle2OtherModeType } from '../types/eCMR';

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.includes(',') ? result.split(',')[1] : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Unable to read selected file'));
    reader.readAsDataURL(file);
  });

const carrierCategories: Array<{ value: CarrierReservationCategory; label: string }> = [
  { value: 'package_count', label: 'Package count' },
  { value: 'marks_numbers', label: 'Marks and numbers' },
  { value: 'condition', label: 'Condition' },
  { value: 'packaging', label: 'Packaging' },
  { value: 'unable_to_verify', label: 'Unable to verify' },
  { value: 'other', label: 'Other' },
];

const deliveryCategories: Array<{ value: DeliveryReservationCategory; label: string }> = [
  { value: 'damage', label: 'Damage' },
  { value: 'shortage', label: 'Shortage' },
  { value: 'late', label: 'Late delivery' },
  { value: 'unable_to_verify', label: 'Unable to verify' },
  { value: 'other', label: 'Other' },
];

const requestedCarrierChecks: Array<{ value: CarrierTakeoverRequestedCheck; label: string }> = [
  { value: 'gross_weight', label: 'Gross weight' },
  { value: 'quantity', label: 'Quantity' },
  { value: 'contents', label: 'Contents' },
];

const carrierTakeoverResults: Array<{ value: CarrierTakeoverCheckResult; label: string }> = [
  { value: 'accepted', label: 'Accepted' },
  { value: 'reservation_recorded', label: 'Reservation recorded' },
  { value: 'unable_to_verify', label: 'Unable to verify' },
];

const carriageImpedimentTypes: Array<{ value: CarriageImpedimentType; label: string }> = [
  { value: 'carriage_impossible_before_arrival', label: 'Carriage impossible before arrival' },
  { value: 'delivery_prevented_after_arrival', label: 'Delivery prevented after arrival' },
  { value: 'consignee_refusal', label: 'Consignee refusal' },
  { value: 'alternative_carriage_taken', label: 'Alternative carriage taken' },
  { value: 'unload_for_account', label: 'Unload for account' },
  { value: 'sale_of_goods', label: 'Sale of goods' },
];

const carriageInstructionRequestTypes = new Set<CarriageImpedimentType>([
  'carriage_impossible_before_arrival',
  'delivery_prevented_after_arrival',
  'consignee_refusal',
  'alternative_carriage_taken',
]);

const carriageInstructionTargets: Array<{ value: CarriageImpedimentInstructionTargetRole; label: string }> = [
  { value: 'article12_entitled_person', label: 'Article 12 entitled person' },
  { value: 'sender', label: 'Sender' },
  { value: 'consignee_as_sender', label: 'Consignee as sender' },
];

const carriageSaleReasons: Array<{ value: CarriageImpedimentSaleReason; label: string }> = [
  { value: 'perishable', label: 'Perishable goods' },
  { value: 'condition_warrants_sale', label: 'Condition warrants sale' },
  { value: 'storage_expenses_disproportionate', label: 'Storage expenses disproportionate' },
  { value: 'reasonable_period_without_instructions', label: 'Reasonable period without instructions' },
];

const carriageSaleProcedureBases: Array<{ value: CarriageImpedimentSaleProcedureBasis; label: string }> = [
  { value: 'applicable_law', label: 'Applicable law' },
  { value: 'local_custom', label: 'Local custom' },
  { value: 'applicable_law_and_local_custom', label: 'Applicable law and local custom' },
];

const carriageAccountingTypes: Array<{ value: CarriageAccountingType; label: string }> = [
  { value: 'instruction_expense', label: 'Instruction expense' },
  { value: 'goods_disposition_expense', label: 'Goods disposition expense' },
  { value: 'sale_proceeds', label: 'Sale proceeds' },
  { value: 'charge_shortfall', label: 'Charge shortfall' },
];

const carrierLiabilityIncidentTypes: Array<{ value: CarrierLiabilityIncidentType; label: string }> = [
  { value: 'total_loss', label: 'Total loss' },
  { value: 'partial_loss', label: 'Partial loss' },
  { value: 'damage', label: 'Damage' },
  { value: 'delay', label: 'Delay' },
  { value: 'article12_disposal_instruction_noncompliance', label: 'Article 12.7 instruction non-compliance' },
];

const carrierLiabilitySpecialRisks: Array<{ value: CarrierLiabilityIncidentSpecialRisk; label: string }> = [
  { value: 'open_unsheeted_vehicle', label: 'Open unsheeted vehicle' },
  { value: 'defective_or_missing_packing', label: 'Defective or missing packing' },
  { value: 'sender_loading_handling_stowage_unloading', label: 'Sender/consignee handling/loading/stowage/unloading' },
  { value: 'goods_nature', label: 'Goods nature' },
  { value: 'insufficient_marks_numbers', label: 'Insufficient marks or numbers' },
  { value: 'livestock', label: 'Livestock' },
];

const deliveryTimingAssessmentTypes: Array<{ value: DeliveryTimingAssessmentType; label: string }> = [
  { value: 'article19_delay', label: 'Article 19 delay' },
  { value: 'article20_loss_presumption', label: 'Article 20 loss presumption' },
  { value: 'article20_recovery_notice', label: 'Article 20 recovery notice' },
];

const compensationPaymentEvidenceTypes: Array<{ value: CompensationPaymentEvidenceType; label: string }> = [
  { value: 'bank_transfer_confirmation', label: 'Bank transfer confirmation' },
  { value: 'payment_provider_receipt', label: 'Payment provider receipt' },
  { value: 'signed_settlement_receipt', label: 'Signed settlement receipt' },
  { value: 'court_payment_record', label: 'Court payment record' },
  { value: 'other_documented_payment', label: 'Other documented payment' },
];

const recoveredGoodsDispositionBases: Array<{ value: RecoveredGoodsDispositionBasis; label: string }> = [
  { value: 'no_written_recovery_notification_request', label: 'No written recovery-notification request' },
  { value: 'no_instructions_after_thirty_days', label: 'No instructions after thirty days' },
  { value: 'recovered_more_than_one_year_after_compensation', label: 'Recovered more than one year after compensation' },
];

const recoveredGoodsDispositionActions: Array<{ value: RecoveredGoodsDispositionActionType; label: string }> = [
  { value: 'stored', label: 'Stored' },
  { value: 'sold', label: 'Sold' },
  { value: 'destroyed', label: 'Destroyed' },
  { value: 'transferred', label: 'Transferred' },
  { value: 'other', label: 'Other' },
];

const codDeliveryOmissionTypes: Array<{ value: CodDeliveryOmissionType; label: string }> = [
  { value: 'cod_uncollected_delivery', label: 'COD uncollected delivery' },
];

const dangerousGoodsNoticeTypes: Array<{ value: DangerousGoodsNoticeType; label: string }> = [
  { value: 'danger_information_notice', label: 'Danger information notice' },
  { value: 'carrier_unaware_dangerous_goods_action', label: 'Carrier-unaware action' },
];

const dangerousGoodsCarrierActionTypes: Array<{ value: DangerousGoodsCarrierActionType; label: string }> = [
  { value: 'unloaded', label: 'Unloaded' },
  { value: 'destroyed', label: 'Destroyed' },
  { value: 'rendered_harmless', label: 'Rendered harmless' },
];

const compensationAssessmentTypes: Array<{ value: CompensationAssessmentType; label: string }> = [
  { value: 'loss_compensation_limit', label: 'Loss compensation limit' },
  { value: 'damage_depreciation_compensation_limit', label: 'Damage depreciation limit' },
  { value: 'delay_compensation_limit', label: 'Delay compensation limit' },
  { value: 'declared_value_substitution', label: 'Declared value substitution' },
  { value: 'special_interest_additional_compensation', label: 'Special interest compensation' },
];

const compensationGoodsValueBases: Array<{ value: CompensationGoodsValueBasis; label: string }> = [
  { value: 'commodity_exchange_price', label: 'Commodity exchange price' },
  { value: 'current_market_price', label: 'Current market price' },
  { value: 'same_kind_quality_value', label: 'Same kind and quality value' },
];

const compensationLiabilityLimitBases: Array<{ value: CompensationLiabilityLimitBasis; label: string }> = [
  { value: 'sdr_8_33_per_kg', label: '8.33 SDR per kg' },
  { value: 'gold_franc_25_per_kg', label: '25 gold francs per kg' },
];

const compensationInterestRecordTypes: Array<{ value: CompensationInterestRecordType; label: string }> = [
  { value: 'interest_on_compensation', label: 'Interest on compensation' },
  { value: 'currency_conversion', label: 'Currency conversion' },
];

const compensationInterestStartBases: Array<{ value: CompensationInterestStartBasis; label: string }> = [
  { value: 'written_claim', label: 'Written claim' },
  { value: 'legal_proceedings', label: 'Legal proceedings' },
];

const liabilityLimitationRecordTypes: Array<{ value: LiabilityLimitationRecordType; label: string }> = [
  { value: 'extra_contractual_claim_defense', label: 'Extra-contractual claim defense' },
  { value: 'wilful_misconduct_limitation_exception', label: 'Wilful misconduct limitation exception' },
];

const liabilityLimitationPersonRoles: Array<{ value: LiabilityLimitationPersonRole; label: string }> = [
  { value: 'carrier', label: 'Carrier' },
  { value: 'article3_agent_or_servant', label: 'Article 3 agent or servant' },
  { value: 'article3_other_service_person', label: 'Article 3 other service person' },
];

const claimReservationRecordTypes: Array<{ value: ClaimReservationRecordType; label: string }> = [
  { value: 'apparent_loss_or_damage_reservation', label: 'Apparent loss/damage reservation' },
  { value: 'non_apparent_loss_or_damage_reservation', label: 'Non-apparent loss/damage reservation' },
  { value: 'delay_compensation_reservation', label: 'Delay compensation reservation' },
  { value: 'condition_check_facility', label: 'Condition check/facility' },
];

const disputeProcedureRecordTypes: Array<{ value: DisputeProcedureRecordType; label: string }> = [
  { value: 'jurisdiction_basis_notice', label: 'Jurisdiction basis notice' },
  { value: 'pending_or_judgment_bar_notice', label: 'Pending/judgment bar notice' },
  { value: 'limitation_period_record', label: 'Limitation period record' },
  { value: 'written_claim_suspension_record', label: 'Written claim suspension' },
  { value: 'arbitration_clause_record', label: 'Arbitration clause' },
];

const disputeProcedureClaimCategories: Array<{ value: DisputeProcedureClaimCategory; label: string }> = [
  { value: 'partial_loss', label: 'Partial loss' },
  { value: 'damage', label: 'Damage' },
  { value: 'delay', label: 'Delay' },
  { value: 'total_loss', label: 'Total loss' },
  { value: 'other', label: 'Other' },
];

const disputeProcedureLimitationPeriodBases: Array<{ value: DisputeProcedureLimitationPeriodBasis; label: string }> = [
  { value: 'one_year', label: 'One year' },
  { value: 'three_year_wilful_misconduct', label: 'Three-year wilful misconduct' },
];

const disputeProcedureLimitationStartBases: Array<{ value: DisputeProcedureLimitationStartBasis; label: string }> = [
  { value: 'delivery_date', label: 'Delivery date' },
  { value: 'thirty_days_after_agreed_time_limit', label: '30 days after agreed limit' },
  { value: 'sixty_days_after_takeover', label: '60 days after takeover' },
  { value: 'three_months_after_contract', label: 'Three months after contract' },
];

const successiveCarrierRecordTypes: Array<{ value: SuccessiveCarrierRecordType; label: string }> = [
  { value: 'successive_carrier_acceptance', label: 'Successive carrier acceptance' },
  { value: 'successive_carrier_action_scope', label: 'Action scope' },
  { value: 'successive_carrier_recovery_allocation', label: 'Recovery allocation' },
  { value: 'successive_carrier_recovery_proceeding', label: 'Recovery proceeding' },
  { value: 'successive_carrier_agreement_variation', label: 'Article 40 agreement variation' },
];

const multimodalCarriageRecordTypes: Array<{ value: MultimodalCarriageRecordType; label: string }> = [
  { value: 'loaded_vehicle_other_mode_carriage', label: 'Loaded vehicle carried by other mode' },
  { value: 'article14_unloading_impediment_basis', label: 'Article 14 unloading impediment basis' },
];

const article2OtherModeTypes: Array<{ value: CmrArticle2OtherModeType; label: string }> = [
  { value: 'rail', label: 'Rail' },
  { value: 'sea', label: 'Sea' },
  { value: 'inland_waterways', label: 'Inland waterways' },
  { value: 'air', label: 'Air' },
];

const consignmentNoteCopyRecordTypes: Array<{ value: ConsignmentNoteCopyRecordType; label: string }> = [
  { value: 'sender_first_copy_access_available', label: 'Sender first copy access' },
  { value: 'goods_accompanying_second_copy_access_available', label: 'Goods-accompanying second copy access' },
  { value: 'carrier_retained_third_copy_access_available', label: 'Carrier retained third copy access' },
];

const senderParticularsResponsibilityRecordTypes: Array<{ value: SenderParticularsResponsibilityRecordType; label: string }> = [
  { value: 'sender_article6_particular_inaccuracy', label: 'Article 6 particular inaccuracy' },
  { value: 'sender_optional_particular_inaccuracy', label: 'Optional particular inaccuracy' },
  { value: 'sender_instruction_inaccuracy', label: 'Sender instruction inaccuracy' },
  { value: 'carrier_entered_particulars_on_sender_behalf', label: 'Carrier entered particulars on sender behalf' },
  { value: 'carrier_cmr_statement_omission', label: 'CMR statement omission' },
];

const senderParticularsResponsibilityCategories: Array<{ value: SenderParticularsResponsibilityCategory; label: string }> = [
  { value: 'article6_1_b_sender', label: 'Article 6.1(b) sender' },
  { value: 'article6_1_d_taking_over_delivery', label: 'Article 6.1(d) taking over/delivery' },
  { value: 'article6_1_e_consignee', label: 'Article 6.1(e) consignee' },
  { value: 'article6_1_f_goods_packing', label: 'Article 6.1(f) goods/packing' },
  { value: 'article6_1_g_packages_marks', label: 'Article 6.1(g) packages/marks' },
  { value: 'article6_1_h_weight_quantity', label: 'Article 6.1(h) weight/quantity' },
  { value: 'article6_1_j_customs_formalities', label: 'Article 6.1(j) customs/formalities' },
  { value: 'article6_2_optional_particular', label: 'Article 6.2 optional particular' },
  { value: 'sender_other_particular_or_instruction', label: 'Other sender particular/instruction' },
  { value: 'article6_1_k_cmr_statement', label: 'Article 6.1(k) CMR statement' },
];

const successiveCarrierAllocationBases: Array<{ value: SuccessiveCarrierAllocationBasis; label: string }> = [
  { value: 'responsible_carrier', label: 'Responsible carrier' },
  { value: 'multiple_carriers_liability_share', label: 'Multiple carriers by liability share' },
  { value: 'multiple_carriers_payment_share', label: 'Multiple carriers by payment share' },
  { value: 'unknown_attribution_payment_share', label: 'Unknown attribution by payment share' },
  { value: 'insolvent_carrier_share_reallocated', label: 'Insolvent carrier share reallocated' },
];

const successiveCarrierRecoveryStartBases: Array<{ value: SuccessiveCarrierRecoveryLimitationStartBasis; label: string }> = [
  { value: 'final_judicial_decision', label: 'Final judicial decision' },
  { value: 'actual_payment', label: 'Actual payment' },
];

const prohibitedClauseRecordTypes: Array<{ value: ProhibitedClauseRecordType; label: string }> = [
  { value: 'convention_derogation_clause', label: 'Convention derogation clause' },
  { value: 'insurance_benefit_to_carrier_clause', label: 'Insurance benefit to carrier clause' },
  { value: 'burden_of_proof_shift_clause', label: 'Burden of proof shift clause' },
];

const replacementMedia: Array<{ value: ReplacementByOtherMeansMedium; label: string }> = [
  { value: 'paper', label: 'Paper' },
  { value: 'pdf_export', label: 'PDF export' },
  { value: 'other_electronic_system', label: 'Other electronic system' },
  { value: 'authority_request', label: 'Authority request' },
];

const consigneeDeliveryRequestTypes: Array<{ value: ConsigneeDeliveryRequestType; label: string }> = [
  { value: 'delivery_after_arrival', label: 'Delivery after arrival' },
  { value: 'rights_enforcement_notice', label: 'Rights enforcement notice' },
  { value: 'delivery_receipt_and_second_copy_handover', label: 'Delivery receipt and second-copy handover' },
];

const disposalInstructionTypes: Array<{ value: DisposalInstructionType; label: string }> = [
  { value: 'stop_in_transit', label: 'Stop in transit' },
  { value: 'change_delivery_place', label: 'Change delivery place' },
  { value: 'change_consignee', label: 'Change consignee' },
];

const disposalExecutionNoticeTargets: Array<{ value: DisposalExecutionNoticeTargetRole; label: string }> = [
  { value: 'article12_entitled_person', label: 'Article 12 entitled person' },
  { value: 'consignor', label: 'Consignor' },
  { value: 'consignee', label: 'Consignee' },
  { value: 'owner', label: 'Owner' },
];

const sanitizedOperationalErrorMessage = 'No se pudo registrar la evidencia operacional. Revisa permisos, estado del documento o disponibilidad del backend.';

const OperationalEvidenceWorkflows: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const documentId = id || '';
  const [carrierCategory, setCarrierCategory] = useState<CarrierReservationCategory>('condition');
  const [carrierGrounds, setCarrierGrounds] = useState('');
  const [carrierPackageReference, setCarrierPackageReference] = useState('');
  const [carrierObservedValue, setCarrierObservedValue] = useState('');
  const [carrierExpectedValue, setCarrierExpectedValue] = useState('');
  const [carrierAcknowledgementReservationId, setCarrierAcknowledgementReservationId] = useState('');
  const [carrierAcknowledgementDecision, setCarrierAcknowledgementDecision] = useState<CarrierReservationAcknowledgementDecision>('acknowledged');
  const [carrierAcknowledgementComment, setCarrierAcknowledgementComment] = useState('');
  const [senderRequestedChecks, setSenderRequestedChecks] = useState<CarrierTakeoverRequestedCheck[]>(['gross_weight']);
  const [senderCheckReason, setSenderCheckReason] = useState('');
  const [takeoverResult, setTakeoverResult] = useState<CarrierTakeoverCheckResult>('accepted');
  const [packageCountChecked, setPackageCountChecked] = useState(true);
  const [marksAndNumbersChecked, setMarksAndNumbersChecked] = useState(true);
  const [apparentConditionChecked, setApparentConditionChecked] = useState(true);
  const [grossWeightChecked, setGrossWeightChecked] = useState(false);
  const [quantityChecked, setQuantityChecked] = useState(false);
  const [contentsChecked, setContentsChecked] = useState(false);
  const [takeoverReservationId, setTakeoverReservationId] = useState('');
  const [takeoverComment, setTakeoverComment] = useState('');
  const [carriageImpedimentType, setCarriageImpedimentType] = useState<CarriageImpedimentType>('carriage_impossible_before_arrival');
  const [carriageInstructionsRequested, setCarriageInstructionsRequested] = useState(true);
  const [carriageInstructionTargetRole, setCarriageInstructionTargetRole] = useState<CarriageImpedimentInstructionTargetRole>('article12_entitled_person');
  const [carriageInstructionRequestSentAt, setCarriageInstructionRequestSentAt] = useState('');
  const [carriageInstructionRequestReceivedAt, setCarriageInstructionRequestReceivedAt] = useState('');
  const [carriageInstructionRequestEvidenceAttachmentId, setCarriageInstructionRequestEvidenceAttachmentId] = useState('');
  const [carriageInstructionsUnavailableInReasonableTime, setCarriageInstructionsUnavailableInReasonableTime] = useState(false);
  const [carriageBestInterestsActionTaken, setCarriageBestInterestsActionTaken] = useState(false);
  const [carriageArrivalAtDeliveryPlace, setCarriageArrivalAtDeliveryPlace] = useState(false);
  const [carriageConsigneeRefusedGoods, setCarriageConsigneeRefusedGoods] = useState(false);
  const [carriageArticle14Or15ImpedimentBasis, setCarriageArticle14Or15ImpedimentBasis] = useState(false);
  const [carriageGoodsUnloadedForAccount, setCarriageGoodsUnloadedForAccount] = useState(false);
  const [carriageGoodsHeldForEntitledPerson, setCarriageGoodsHeldForEntitledPerson] = useState(false);
  const [carriageThirdPartyCustodianUsed, setCarriageThirdPartyCustodianUsed] = useState(false);
  const [carriageThirdPartyCustodianSelectedWithReasonableCare, setCarriageThirdPartyCustodianSelectedWithReasonableCare] = useState(false);
  const [carriageSaleReason, setCarriageSaleReason] = useState<CarriageImpedimentSaleReason>('perishable');
  const [carriageGoodsLocationCountryCode, setCarriageGoodsLocationCountryCode] = useState('');
  const [carriageGoodsLocationDetail, setCarriageGoodsLocationDetail] = useState('');
  const [carriageSaleProcedureBasis, setCarriageSaleProcedureBasis] = useState<CarriageImpedimentSaleProcedureBasis>('applicable_law');
  const [carriageSaleProcedureReference, setCarriageSaleProcedureReference] = useState('');
  const [carriageSaleProcedureAssessedAt, setCarriageSaleProcedureAssessedAt] = useState('');
  const [carriageSaleProcedureEvidenceAttachmentId, setCarriageSaleProcedureEvidenceAttachmentId] = useState('');
  const [carriageProceedsPlacedAtDisposal, setCarriageProceedsPlacedAtDisposal] = useState(false);
  const [carriageSaleProcedureLawOrCustomAcknowledged, setCarriageSaleProcedureLawOrCustomAcknowledged] = useState(false);
  const [carriageExpensesRecoverableClaimed, setCarriageExpensesRecoverableClaimed] = useState(false);
  const [carriageReason, setCarriageReason] = useState('');
  const [carriageInstructionRequestDetail, setCarriageInstructionRequestDetail] = useState('');
  const [carriageAlternativeCarriageConditions, setCarriageAlternativeCarriageConditions] = useState('');
  const [carriageStorageOrSaleDetail, setCarriageStorageOrSaleDetail] = useState('');
  const [carriageAccountingImpedimentId, setCarriageAccountingImpedimentId] = useState('');
  const [carriageAccountingType, setCarriageAccountingType] = useState<CarriageAccountingType>('sale_proceeds');
  const [carriageAccountingAmount, setCarriageAccountingAmount] = useState('');
  const [carriageAccountingCurrency, setCarriageAccountingCurrency] = useState('EUR');
  const [carriageAccountingAt, setCarriageAccountingAt] = useState('');
  const [carriageAccountingEvidenceAttachmentId, setCarriageAccountingEvidenceAttachmentId] = useState('');
  const [carriageAccountingCarrierNotAtFault, setCarriageAccountingCarrierNotAtFault] = useState(false);
  const [carriageAccountingChargeableAgainstGoods, setCarriageAccountingChargeableAgainstGoods] = useState(false);
  const [carriageAccountingProceedsPlaced, setCarriageAccountingProceedsPlaced] = useState(false);
  const [carriageAccountingChargesExceedProceeds, setCarriageAccountingChargesExceedProceeds] = useState(false);
  const [carriageAccountingGrossSaleProceeds, setCarriageAccountingGrossSaleProceeds] = useState('');
  const [carriageAccountingDeductedSaleExpenses, setCarriageAccountingDeductedSaleExpenses] = useState('');
  const [carriageAccountingSaleProceedsReference, setCarriageAccountingSaleProceedsReference] = useState('');
  const [carriageAccountingTotalChargesDue, setCarriageAccountingTotalChargesDue] = useState('');
  const [carriageAccountingDetail, setCarriageAccountingDetail] = useState('');
  const [carrierLiabilityIncidentType, setCarrierLiabilityIncidentType] = useState<CarrierLiabilityIncidentType>('damage');
  const [carrierLiabilityTakingOverAt, setCarrierLiabilityTakingOverAt] = useState('');
  const [carrierLiabilityIncidentAt, setCarrierLiabilityIncidentAt] = useState('');
  const [carrierLiabilityDeliveryAt, setCarrierLiabilityDeliveryAt] = useState('');
  const [carrierLiabilityIncidentEvidenceAttachmentId, setCarrierLiabilityIncidentEvidenceAttachmentId] = useState('');
  const [carrierLiabilityCarrierProofEvidenceAttachmentId, setCarrierLiabilityCarrierProofEvidenceAttachmentId] = useState('');
  const [carrierLiabilityLossObserved, setCarrierLiabilityLossObserved] = useState(false);
  const [carrierLiabilityDamageObserved, setCarrierLiabilityDamageObserved] = useState(true);
  const [carrierLiabilityDelayObserved, setCarrierLiabilityDelayObserved] = useState(false);
  const [carrierLiabilityClaimantFault, setCarrierLiabilityClaimantFault] = useState(false);
  const [carrierLiabilityClaimantInstruction, setCarrierLiabilityClaimantInstruction] = useState(false);
  const [carrierLiabilityInherentVice, setCarrierLiabilityInherentVice] = useState(false);
  const [carrierLiabilityUnavoidableCircumstances, setCarrierLiabilityUnavoidableCircumstances] = useState(false);
  const [carrierLiabilitySpecialRiskClaimed, setCarrierLiabilitySpecialRiskClaimed] = useState(false);
  const [carrierLiabilitySpecialRiskType, setCarrierLiabilitySpecialRiskType] = useState<CarrierLiabilityIncidentSpecialRisk>('defective_or_missing_packing');
  const [carrierLiabilitySpecialRiskPresumption, setCarrierLiabilitySpecialRiskPresumption] = useState(false);
  const [carrierLiabilityArticle18_2CircumstancesEstablished, setCarrierLiabilityArticle18_2CircumstancesEstablished] = useState(false);
  const [carrierLiabilityArticle18_2AssessmentDetail, setCarrierLiabilityArticle18_2AssessmentDetail] = useState('');
  const [carrierLiabilityArticle18_2EvidenceAttachmentId, setCarrierLiabilityArticle18_2EvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4aOpenVehicleUsed, setCarrierLiabilityArticle17_4aOpenVehicleUsed] = useState(false);
  const [carrierLiabilityArticle17_4aAssessmentDetail, setCarrierLiabilityArticle17_4aAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4aEvidenceAttachmentId, setCarrierLiabilityArticle17_4aEvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4bPackingAbsentOrDefective, setCarrierLiabilityArticle17_4bPackingAbsentOrDefective] = useState(false);
  const [carrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking, setCarrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking] = useState(false);
  const [carrierLiabilityArticle17_4bAssessmentDetail, setCarrierLiabilityArticle17_4bAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4bEvidenceAttachmentId, setCarrierLiabilityArticle17_4bEvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4cHandlingPerformed, setCarrierLiabilityArticle17_4cHandlingPerformed] = useState(false);
  const [carrierLiabilityArticle17_4cLoadingPerformed, setCarrierLiabilityArticle17_4cLoadingPerformed] = useState(false);
  const [carrierLiabilityArticle17_4cStowagePerformed, setCarrierLiabilityArticle17_4cStowagePerformed] = useState(false);
  const [carrierLiabilityArticle17_4cUnloadingPerformed, setCarrierLiabilityArticle17_4cUnloadingPerformed] = useState(false);
  const [carrierLiabilityArticle17_4cActorCapacity, setCarrierLiabilityArticle17_4cActorCapacity] = useState<CarrierLiabilityArticle17_4cActorCapacity>('consignor');
  const [carrierLiabilityArticle17_4cAssessmentDetail, setCarrierLiabilityArticle17_4cAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4cEvidenceAttachmentId, setCarrierLiabilityArticle17_4cEvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4dGoodsParticularlyExposed, setCarrierLiabilityArticle17_4dGoodsParticularlyExposed] = useState(false);
  const [carrierLiabilityArticle17_4dSusceptibilityBasis, setCarrierLiabilityArticle17_4dSusceptibilityBasis] = useState<CarrierLiabilityArticle17_4dSusceptibilityBasis>('breakage');
  const [carrierLiabilityArticle17_4dAssessmentDetail, setCarrierLiabilityArticle17_4dAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4dEvidenceAttachmentId, setCarrierLiabilityArticle17_4dEvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4ePackageMarksDeficient, setCarrierLiabilityArticle17_4ePackageMarksDeficient] = useState(false);
  const [carrierLiabilityArticle17_4ePackageNumbersDeficient, setCarrierLiabilityArticle17_4ePackageNumbersDeficient] = useState(false);
  const [carrierLiabilityArticle17_4eDeficiencyBasis, setCarrierLiabilityArticle17_4eDeficiencyBasis] = useState<CarrierLiabilityArticle17_4eDeficiencyBasis>('insufficiency');
  const [carrierLiabilityArticle17_4eAssessmentDetail, setCarrierLiabilityArticle17_4eAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4eEvidenceAttachmentId, setCarrierLiabilityArticle17_4eEvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_4fLivestockCarriageConfirmed, setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed] = useState(false);
  const [carrierLiabilityArticle17_4fAssessmentDetail, setCarrierLiabilityArticle17_4fAssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_4fEvidenceAttachmentId, setCarrierLiabilityArticle17_4fEvidenceAttachmentId] = useState('');
  const [carrierLiabilityTemperatureControlled, setCarrierLiabilityTemperatureControlled] = useState(false);
  const [carrierLiabilityEquipmentStepsEvidence, setCarrierLiabilityEquipmentStepsEvidence] = useState(false);
  const [carrierLiabilityLivestockCareEvidence, setCarrierLiabilityLivestockCareEvidence] = useState(false);
  const [carrierLiabilityArticle18_4ChoiceSteps, setCarrierLiabilityArticle18_4ChoiceSteps] = useState(false);
  const [carrierLiabilityArticle18_4MaintenanceSteps, setCarrierLiabilityArticle18_4MaintenanceSteps] = useState(false);
  const [carrierLiabilityArticle18_4UseSteps, setCarrierLiabilityArticle18_4UseSteps] = useState(false);
  const [carrierLiabilityArticle18_4InstructionsIssued, setCarrierLiabilityArticle18_4InstructionsIssued] = useState(false);
  const [carrierLiabilityArticle18_4InstructionsComplied, setCarrierLiabilityArticle18_4InstructionsComplied] = useState(false);
  const [carrierLiabilityArticle18_4AssessmentDetail, setCarrierLiabilityArticle18_4AssessmentDetail] = useState('');
  const [carrierLiabilityArticle18_4EvidenceAttachmentId, setCarrierLiabilityArticle18_4EvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle18_5NormallyIncumbentSteps, setCarrierLiabilityArticle18_5NormallyIncumbentSteps] = useState(false);
  const [carrierLiabilityArticle18_5InstructionsIssued, setCarrierLiabilityArticle18_5InstructionsIssued] = useState(false);
  const [carrierLiabilityArticle18_5InstructionsComplied, setCarrierLiabilityArticle18_5InstructionsComplied] = useState(false);
  const [carrierLiabilityArticle18_5AssessmentDetail, setCarrierLiabilityArticle18_5AssessmentDetail] = useState('');
  const [carrierLiabilityArticle18_5EvidenceAttachmentId, setCarrierLiabilityArticle18_5EvidenceAttachmentId] = useState('');
  const [carrierLiabilityAbnormalShortageObserved, setCarrierLiabilityAbnormalShortageObserved] = useState(false);
  const [carrierLiabilityAnyPackageLost, setCarrierLiabilityAnyPackageLost] = useState(false);
  const [carrierLiabilityVehicleDefectContributed, setCarrierLiabilityVehicleDefectContributed] = useState(false);
  const [carrierLiabilityHiredVehicleProviderContribution, setCarrierLiabilityHiredVehicleProviderContribution] = useState(false);
  const [carrierLiabilityArticle17_3AssessmentDetail, setCarrierLiabilityArticle17_3AssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_3EvidenceAttachmentId, setCarrierLiabilityArticle17_3EvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle18_1CauseProofEstablished, setCarrierLiabilityArticle18_1CauseProofEstablished] = useState(false);
  const [carrierLiabilityArticle18_1ProofAssessmentDetail, setCarrierLiabilityArticle18_1ProofAssessmentDetail] = useState('');
  const [carrierLiabilityArticle18_1EvidenceAttachmentId, setCarrierLiabilityArticle18_1EvidenceAttachmentId] = useState('');
  const [carrierLiabilityArticle17_2CauseContributed, setCarrierLiabilityArticle17_2CauseContributed] = useState(false);
  const [carrierLiabilityArticle17_5CarrierShare, setCarrierLiabilityArticle17_5CarrierShare] = useState('');
  const [carrierLiabilityArticle17_5ReliefShare, setCarrierLiabilityArticle17_5ReliefShare] = useState('');
  const [carrierLiabilityArticle17_5AssessmentDetail, setCarrierLiabilityArticle17_5AssessmentDetail] = useState('');
  const [carrierLiabilityArticle17_5EvidenceAttachmentId, setCarrierLiabilityArticle17_5EvidenceAttachmentId] = useState('');
  const [carrierLiabilityDisposalInstructionId, setCarrierLiabilityDisposalInstructionId] = useState('');
  const [carrierLiabilityInstructionNotCarriedOut, setCarrierLiabilityInstructionNotCarriedOut] = useState(false);
  const [carrierLiabilityInstructionWithoutFirstCopy, setCarrierLiabilityInstructionWithoutFirstCopy] = useState(false);
  const [carrierLiabilityClaimantEntitlement, setCarrierLiabilityClaimantEntitlement] = useState(false);
  const [carrierLiabilityLossOrDamageCaused, setCarrierLiabilityLossOrDamageCaused] = useState(false);
  const [carrierLiabilityIncidentDetail, setCarrierLiabilityIncidentDetail] = useState('');
  const [carrierLiabilityClaimantStatementDetail, setCarrierLiabilityClaimantStatementDetail] = useState('');
  const [carrierLiabilityCarrierProofDetail, setCarrierLiabilityCarrierProofDetail] = useState('');
  const [article18_2RebuttalIncidentId, setArticle18_2RebuttalIncidentId] = useState('');
  const [article18_2RebuttalCounterEvidenceDetail, setArticle18_2RebuttalCounterEvidenceDetail] = useState('');
  const [article18_2RebuttalEvidenceAttachmentId, setArticle18_2RebuttalEvidenceAttachmentId] = useState('');
  const [deliveryTimingAssessmentType, setDeliveryTimingAssessmentType] = useState<DeliveryTimingAssessmentType>('article20_loss_presumption');
  const [deliveryTimingTakingOverAt, setDeliveryTimingTakingOverAt] = useState('');
  const [deliveryTimingActualDeliveryAt, setDeliveryTimingActualDeliveryAt] = useState('');
  const [deliveryTimingAssessmentAt, setDeliveryTimingAssessmentAt] = useState('');
  const [deliveryTimingRecoveryNotificationAt, setDeliveryTimingRecoveryNotificationAt] = useState('');
  const [deliveryTimingRecoveryNotificationReceivedAt, setDeliveryTimingRecoveryNotificationReceivedAt] = useState('');
  const [deliveryTimingDeliveryRequestAt, setDeliveryTimingDeliveryRequestAt] = useState('');
  const [deliveryTimingGoodsNotDelivered, setDeliveryTimingGoodsNotDelivered] = useState(true);
  const [deliveryTimingAgreedLimitPresent, setDeliveryTimingAgreedLimitPresent] = useState(false);
  const [deliveryTimingAgreedLimitExpired, setDeliveryTimingAgreedLimitExpired] = useState(false);
  const [deliveryTimingDiligentCarrierAllowedDurationMinutes, setDeliveryTimingDiligentCarrierAllowedDurationMinutes] = useState('');
  const [deliveryTimingPartialLoadConsidered, setDeliveryTimingPartialLoadConsidered] = useState(false);
  const [deliveryTimingPartialLoadAssemblyDurationMinutes, setDeliveryTimingPartialLoadAssemblyDurationMinutes] = useState('');
  const [deliveryTimingThirtyDaysExpired, setDeliveryTimingThirtyDaysExpired] = useState(false);
  const [deliveryTimingSixtyDaysExpired, setDeliveryTimingSixtyDaysExpired] = useState(true);
  const [deliveryTimingRecoveryRequested, setDeliveryTimingRecoveryRequested] = useState(false);
  const [deliveryTimingWrittenAcknowledgement, setDeliveryTimingWrittenAcknowledgement] = useState(false);
  const [deliveryTimingGoodsRecovered, setDeliveryTimingGoodsRecovered] = useState(false);
  const [deliveryTimingRecoveryNotificationSent, setDeliveryTimingRecoveryNotificationSent] = useState(false);
  const [deliveryTimingDeliveryRequested, setDeliveryTimingDeliveryRequested] = useState(false);
  const [deliveryTimingChargesRefundAcknowledged, setDeliveryTimingChargesRefundAcknowledged] = useState(false);
  const [deliveryTimingDetail, setDeliveryTimingDetail] = useState('');
  const [deliveryTimingClaimDetail, setDeliveryTimingClaimDetail] = useState('');
  const [deliveryTimingRecoveryDetail, setDeliveryTimingRecoveryDetail] = useState('');
  const [compensationPaymentEvidenceType, setCompensationPaymentEvidenceType] = useState<CompensationPaymentEvidenceType>('bank_transfer_confirmation');
  const [compensationPaymentPaidAt, setCompensationPaymentPaidAt] = useState('');
  const [compensationPaymentAmount, setCompensationPaymentAmount] = useState('');
  const [compensationPaymentCurrency, setCompensationPaymentCurrency] = useState('EUR');
  const [compensationPaymentAttachmentId, setCompensationPaymentAttachmentId] = useState('');
  const [compensationPaymentForMissingGoods, setCompensationPaymentForMissingGoods] = useState(true);
  const [compensationPaymentSettled, setCompensationPaymentSettled] = useState(true);
  const [compensationPaymentRecipientAcknowledged, setCompensationPaymentRecipientAcknowledged] = useState(true);
  const [compensationPaymentDetail, setCompensationPaymentDetail] = useState('');
  const [recoverySettlementChargeLines, setRecoverySettlementChargeLines] = useState([
    { amount: '', currency: 'EUR' },
  ]);
  const [recoverySettlementChargesPaidAt, setRecoverySettlementChargesPaidAt] = useState('');
  const [recoverySettlementRefundAmount, setRecoverySettlementRefundAmount] = useState('');
  const [recoverySettlementRefundCurrency, setRecoverySettlementRefundCurrency] = useState('EUR');
  const [recoverySettlementIncludedCharges, setRecoverySettlementIncludedCharges] = useState('0');
  const [recoverySettlementRefundedAt, setRecoverySettlementRefundedAt] = useState('');
  const [recoverySettlementChargesAttachmentId, setRecoverySettlementChargesAttachmentId] = useState('');
  const [recoverySettlementRefundAttachmentId, setRecoverySettlementRefundAttachmentId] = useState('');
  const [recoverySettlementChargesSettled, setRecoverySettlementChargesSettled] = useState(true);
  const [recoverySettlementRefundSettled, setRecoverySettlementRefundSettled] = useState(true);
  const [recoverySettlementDelayRightsPreserved, setRecoverySettlementDelayRightsPreserved] = useState(true);
  const [recoverySettlementDetail, setRecoverySettlementDetail] = useState('');
  const [recoveredGoodsDispositionBasis, setRecoveredGoodsDispositionBasis] = useState<RecoveredGoodsDispositionBasis>('no_written_recovery_notification_request');
  const [recoveredGoodsDispositionAction, setRecoveredGoodsDispositionAction] = useState<RecoveredGoodsDispositionActionType>('stored');
  const [recoveredGoodsRecoveredAt, setRecoveredGoodsRecoveredAt] = useState('');
  const [recoveredGoodsDispositionAt, setRecoveredGoodsDispositionAt] = useState('');
  const [recoveredGoodsNotificationReceivedAt, setRecoveredGoodsNotificationReceivedAt] = useState('');
  const [recoveredGoodsInstructionsAssessedAt, setRecoveredGoodsInstructionsAssessedAt] = useState('');
  const [recoveredGoodsLocationCountryCode, setRecoveredGoodsLocationCountryCode] = useState('');
  const [recoveredGoodsLocationDetail, setRecoveredGoodsLocationDetail] = useState('');
  const [recoveredGoodsApplicableLawDetail, setRecoveredGoodsApplicableLawDetail] = useState('');
  const [recoveredGoodsDispositionDetail, setRecoveredGoodsDispositionDetail] = useState('');
  const [recoveredGoodsRecoveryAttachmentId, setRecoveredGoodsRecoveryAttachmentId] = useState('');
  const [recoveredGoodsDispositionAttachmentId, setRecoveredGoodsDispositionAttachmentId] = useState('');
  const [recoveredGoodsRecovered, setRecoveredGoodsRecovered] = useState(true);
  const [recoveredGoodsWrittenRequestAbsent, setRecoveredGoodsWrittenRequestAbsent] = useState(true);
  const [recoveredGoodsInstructionsAbsent, setRecoveredGoodsInstructionsAbsent] = useState(true);
  const [recoveredGoodsLocalLawIdentified, setRecoveredGoodsLocalLawIdentified] = useState(true);
  const [recoveredGoodsDispositionUnderLaw, setRecoveredGoodsDispositionUnderLaw] = useState(true);
  const [codOmissionType, setCodOmissionType] = useState<CodDeliveryOmissionType>('cod_uncollected_delivery');
  const [codDeliveryAt, setCodDeliveryAt] = useState('');
  const [codGoodsDeliveredToConsignee, setCodGoodsDeliveredToConsignee] = useState(true);
  const [codChargeUncollected, setCodChargeUncollected] = useState(true);
  const [codConsignorCompensationClaimed, setCodConsignorCompensationClaimed] = useState(false);
  const [codCarrierActionReserved, setCodCarrierActionReserved] = useState(false);
  const [codOmissionDetail, setCodOmissionDetail] = useState('');
  const [codCompensationClaimDetail, setCodCompensationClaimDetail] = useState('');
  const [codConsigneeActionDetail, setCodConsigneeActionDetail] = useState('');
  const [dangerNoticeType, setDangerNoticeType] = useState<DangerousGoodsNoticeType>('danger_information_notice');
  const [dangerNoticeAt, setDangerNoticeAt] = useState('');
  const [dangerActionAt, setDangerActionAt] = useState('');
  const [dangerousGoodsHandedToCarrier, setDangerousGoodsHandedToCarrier] = useState(true);
  const [dangerPrecautionsRequired, setDangerPrecautionsRequired] = useState(false);
  const [dangerCarrierKnowledgeProved, setDangerCarrierKnowledgeProved] = useState(false);
  const [dangerCarrierKnowledgeProofAttachmentId, setDangerCarrierKnowledgeProofAttachmentId] = useState('');
  const [dangerCarrierDidNotKnow, setDangerCarrierDidNotKnow] = useState(false);
  const [dangerCarrierActionType, setDangerCarrierActionType] = useState<DangerousGoodsCarrierActionType>('unloaded');
  const [dangerCarrierActionTaken, setDangerCarrierActionTaken] = useState(false);
  const [dangerWithoutCompensationBasisRecorded, setDangerWithoutCompensationBasisRecorded] = useState(false);
  const [dangerSenderExpenseLiabilityClaimed, setDangerSenderExpenseLiabilityClaimed] = useState(false);
  const [dangerNatureDetail, setDangerNatureDetail] = useState('');
  const [dangerPrecautionsDetail, setDangerPrecautionsDetail] = useState('');
  const [dangerCarrierKnowledgeProofDetail, setDangerCarrierKnowledgeProofDetail] = useState('');
  const [dangerCarrierActionDetail, setDangerCarrierActionDetail] = useState('');
  const [dangerExpenseClaimDetail, setDangerExpenseClaimDetail] = useState('');
  const [compensationAssessmentType, setCompensationAssessmentType] = useState<CompensationAssessmentType>('loss_compensation_limit');
  const [compensationAssessmentAt, setCompensationAssessmentAt] = useState('');
  const [compensationLinkedIncidentId, setCompensationLinkedIncidentId] = useState('');
  const [compensationGoodsValueBasis, setCompensationGoodsValueBasis] = useState<CompensationGoodsValueBasis>('current_market_price');
  const [compensationLiabilityLimitBasis, setCompensationLiabilityLimitBasis] = useState<CompensationLiabilityLimitBasis>('sdr_8_33_per_kg');
  const [compensationGoodsValueAmount, setCompensationGoodsValueAmount] = useState('');
  const [compensationGoodsValueCurrency, setCompensationGoodsValueCurrency] = useState('EUR');
  const [compensationGrossWeightShortKg, setCompensationGrossWeightShortKg] = useState('');
  const [compensationClaimAmount, setCompensationClaimAmount] = useState('');
  const [compensationClaimCurrency, setCompensationClaimCurrency] = useState('EUR');
  const [compensationCarriageChargesAmount, setCompensationCarriageChargesAmount] = useState('');
  const [compensationCarriageChargesCurrency, setCompensationCarriageChargesCurrency] = useState('EUR');
  const [compensationDeclaredValueAmount, setCompensationDeclaredValueAmount] = useState('');
  const [compensationDeclaredValueCurrency, setCompensationDeclaredValueCurrency] = useState('EUR');
  const [compensationSpecialInterestAmount, setCompensationSpecialInterestAmount] = useState('');
  const [compensationSpecialInterestCurrency, setCompensationSpecialInterestCurrency] = useState('EUR');
  const [compensationDiminishedValueAmount, setCompensationDiminishedValueAmount] = useState('');
  const [compensationDiminishedValueCurrency, setCompensationDiminishedValueCurrency] = useState('EUR');
  const [compensationCarrierLiabilityBasisRecorded, setCompensationCarrierLiabilityBasisRecorded] = useState(true);
  const [compensationArticle23LimitApplied, setCompensationArticle23LimitApplied] = useState(true);
  const [compensationCarriageChargesRefundConsidered, setCompensationCarriageChargesRefundConsidered] = useState(true);
  const [compensationDelayDamageProved, setCompensationDelayDamageProved] = useState(false);
  const [compensationCarriageChargesLimitApplied, setCompensationCarriageChargesLimitApplied] = useState(false);
  const [compensationDamageAffectedWholeConsignment, setCompensationDamageAffectedWholeConsignment] = useState(false);
  const [compensationArticle25LossLimitApplied, setCompensationArticle25LossLimitApplied] = useState(false);
  const [compensationDeclaredValueEntered, setCompensationDeclaredValueEntered] = useState(false);
  const [compensationDeclaredValueSurchargeAgreed, setCompensationDeclaredValueSurchargeAgreed] = useState(false);
  const [compensationDeclaredValueExceedsArticle23Limit, setCompensationDeclaredValueExceedsArticle23Limit] = useState(false);
  const [compensationDeclaredValueLimitSubstituted, setCompensationDeclaredValueLimitSubstituted] = useState(false);
  const [compensationSpecialInterestEntered, setCompensationSpecialInterestEntered] = useState(false);
  const [compensationSpecialInterestSurchargeAgreed, setCompensationSpecialInterestSurchargeAgreed] = useState(false);
  const [compensationAdditionalLossOrDamageProved, setCompensationAdditionalLossOrDamageProved] = useState(false);
  const [compensationSpecialInterestLimitApplied, setCompensationSpecialInterestLimitApplied] = useState(false);
  const [compensationAgreedTimeLimitExceeded, setCompensationAgreedTimeLimitExceeded] = useState(false);
  const [compensationValuationDetail, setCompensationValuationDetail] = useState('');
  const [compensationValuationEvidenceAttachmentId, setCompensationValuationEvidenceAttachmentId] = useState('');
  const [compensationCalculationDetail, setCompensationCalculationDetail] = useState('');
  const [compensationClaimDetail, setCompensationClaimDetail] = useState('');
  const [compensationInterestRecordType, setCompensationInterestRecordType] = useState<CompensationInterestRecordType>('interest_on_compensation');
  const [compensationInterestStartBasis, setCompensationInterestStartBasis] = useState<CompensationInterestStartBasis>('written_claim');
  const [compensationInterestStartAt, setCompensationInterestStartAt] = useState('');
  const [compensationInterestCalculatedAt, setCompensationInterestCalculatedAt] = useState('');
  const [compensationInterestPaymentAt, setCompensationInterestPaymentAt] = useState('');
  const [compensationInterestLinkedAssessmentId, setCompensationInterestLinkedAssessmentId] = useState('');
  const [compensationInterestCompensationAmount, setCompensationInterestCompensationAmount] = useState('');
  const [compensationInterestCompensationCurrency, setCompensationInterestCompensationCurrency] = useState('EUR');
  const [compensationInterestAmount, setCompensationInterestAmount] = useState('');
  const [compensationInterestCurrency, setCompensationInterestCurrency] = useState('EUR');
  const [compensationInterestSourceAmount, setCompensationInterestSourceAmount] = useState('');
  const [compensationInterestSourceCurrency, setCompensationInterestSourceCurrency] = useState('EUR');
  const [compensationInterestTargetCurrency, setCompensationInterestTargetCurrency] = useState('EUR');
  const [compensationInterestConvertedAmount, setCompensationInterestConvertedAmount] = useState('');
  const [compensationInterestConvertedCurrency, setCompensationInterestConvertedCurrency] = useState('EUR');
  const [compensationInterestPayableBasisRecorded, setCompensationInterestPayableBasisRecorded] = useState(true);
  const [compensationInterestClaimSentInWriting, setCompensationInterestClaimSentInWriting] = useState(true);
  const [compensationInterestLegalProceedingsInstituted, setCompensationInterestLegalProceedingsInstituted] = useState(false);
  const [compensationInterestFivePercentApplied, setCompensationInterestFivePercentApplied] = useState(true);
  const [compensationInterestAmountsNotPaymentCurrency, setCompensationInterestAmountsNotPaymentCurrency] = useState(false);
  const [compensationInterestPaymentClaimCurrencyCountryBasis, setCompensationInterestPaymentClaimCurrencyCountryBasis] = useState(false);
  const [compensationInterestPaymentPlaceBasis, setCompensationInterestPaymentPlaceBasis] = useState(false);
  const [compensationInterestExchangeRateApplicable, setCompensationInterestExchangeRateApplicable] = useState(false);
  const [compensationInterestCalculationDetail, setCompensationInterestCalculationDetail] = useState('');
  const [compensationInterestExchangeRateDetail, setCompensationInterestExchangeRateDetail] = useState('');
  const [compensationInterestClaimDetail, setCompensationInterestClaimDetail] = useState('');
  const [liabilityLimitationRecordType, setLiabilityLimitationRecordType] = useState<LiabilityLimitationRecordType>('extra_contractual_claim_defense');
  const [liabilityLimitationPersonRole, setLiabilityLimitationPersonRole] = useState<LiabilityLimitationPersonRole>('carrier');
  const [liabilityLimitationLinkedIncidentId, setLiabilityLimitationLinkedIncidentId] = useState('');
  const [liabilityClaimArisesFromCarriage, setLiabilityClaimArisesFromCarriage] = useState(true);
  const [liabilityLossDamageOrDelayClaim, setLiabilityLossDamageOrDelayClaim] = useState(true);
  const [liabilityExtraContractualClaim, setLiabilityExtraContractualClaim] = useState(true);
  const [liabilityCmrLimitInvoked, setLiabilityCmrLimitInvoked] = useState(true);
  const [liabilityArticle3PersonInvolved, setLiabilityArticle3PersonInvolved] = useState(false);
  const [liabilityArticle3ScopeRecorded, setLiabilityArticle3ScopeRecorded] = useState(false);
  const [liabilityActingWithinScope, setLiabilityActingWithinScope] = useState(false);
  const [liabilityLimitationProvisionInvoked, setLiabilityLimitationProvisionInvoked] = useState(true);
  const [liabilityWilfulMisconductAlleged, setLiabilityWilfulMisconductAlleged] = useState(true);
  const [liabilityEquivalentDefaultAlleged, setLiabilityEquivalentDefaultAlleged] = useState(false);
  const [liabilityCourtLawBasisRecorded, setLiabilityCourtLawBasisRecorded] = useState(false);
  const [liabilityDisapplicationBasisRecorded, setLiabilityDisapplicationBasisRecorded] = useState(true);
  const [liabilityClaimDetail, setLiabilityClaimDetail] = useState('');
  const [liabilityApplicableLawDetail, setLiabilityApplicableLawDetail] = useState('');
  const [liabilityArticle3PersonDetail, setLiabilityArticle3PersonDetail] = useState('');
  const [liabilityMisconductDetail, setLiabilityMisconductDetail] = useState('');
  const [liabilityCourtLawDetail, setLiabilityCourtLawDetail] = useState('');
  const [claimReservationRecordType, setClaimReservationRecordType] = useState<ClaimReservationRecordType>('non_apparent_loss_or_damage_reservation');
  const [claimReservationLinkedDeliveryId, setClaimReservationLinkedDeliveryId] = useState('');
  const [claimReservationLinkedClaimId, setClaimReservationLinkedClaimId] = useState('');
  const [claimReservationLinkedConditionCheckId, setClaimReservationLinkedConditionCheckId] = useState('');
  const [claimReservationDeliveryAt, setClaimReservationDeliveryAt] = useState('');
  const [claimReservationConditionCheckedAt, setClaimReservationConditionCheckedAt] = useState('');
  const [claimReservationGoodsAvailableAt, setClaimReservationGoodsAvailableAt] = useState('');
  const [claimReservationSentAt, setClaimReservationSentAt] = useState('');
  const [claimReservationExcludedPublicHolidayDates, setClaimReservationExcludedPublicHolidayDates] = useState('');
  const [claimLossOrDamageApparent, setClaimLossOrDamageApparent] = useState(false);
  const [claimLossOrDamageNotApparent, setClaimLossOrDamageNotApparent] = useState(true);
  const [claimDelayCompensationClaim, setClaimDelayCompensationClaim] = useState(false);
  const [claimReservationSentAtDelivery, setClaimReservationSentAtDelivery] = useState(false);
  const [claimReservationSentInWriting, setClaimReservationSentInWriting] = useState(true);
  const [claimReservationWithinSevenDays, setClaimReservationWithinSevenDays] = useState(true);
  const [claimConditionCheckedWithCarrier, setClaimConditionCheckedWithCarrier] = useState(false);
  const [claimContraryEvidenceReservationWritten, setClaimContraryEvidenceReservationWritten] = useState(false);
  const [claimDelayReservationWritten, setClaimDelayReservationWritten] = useState(false);
  const [claimReservationWithinTwentyOneDays, setClaimReservationWithinTwentyOneDays] = useState(false);
  const [claimRelevantStartDateExcluded, setClaimRelevantStartDateExcluded] = useState(true);
  const [claimReasonableFacilitiesProvided, setClaimReasonableFacilitiesProvided] = useState(false);
  const [claimReservationDetail, setClaimReservationDetail] = useState('');
  const [claimConditionCheckDetail, setClaimConditionCheckDetail] = useState('');
  const [claimDelayClaimDetail, setClaimDelayClaimDetail] = useState('');
  const [claimInvestigationFacilityDetail, setClaimInvestigationFacilityDetail] = useState('');
  const [disputeProcedureRecordType, setDisputeProcedureRecordType] = useState<DisputeProcedureRecordType>('limitation_period_record');
  const [disputeClaimCategory, setDisputeClaimCategory] = useState<DisputeProcedureClaimCategory>('damage');
  const [disputeLimitationPeriodBasis, setDisputeLimitationPeriodBasis] = useState<DisputeProcedureLimitationPeriodBasis>('one_year');
  const [disputeLimitationStartBasis, setDisputeLimitationStartBasis] = useState<DisputeProcedureLimitationStartBasis>('delivery_date');
  const [disputeLinkedClaimId, setDisputeLinkedClaimId] = useState('');
  const [disputeLinkedProceedingId, setDisputeLinkedProceedingId] = useState('');
  const [disputeLinkedJudgmentId, setDisputeLinkedJudgmentId] = useState('');
  const [disputeLinkedWrittenClaimId, setDisputeLinkedWrittenClaimId] = useState('');
  const [disputeLinkedArbitrationClauseId, setDisputeLinkedArbitrationClauseId] = useState('');
  const [disputeActionFiledAt, setDisputeActionFiledAt] = useState('');
  const [disputeJudgmentEnteredAt, setDisputeJudgmentEnteredAt] = useState('');
  const [disputeJudgmentEnforceableAt, setDisputeJudgmentEnforceableAt] = useState('');
  const [disputeDeliveryAt, setDisputeDeliveryAt] = useState('');
  const [disputeTakeoverAt, setDisputeTakeoverAt] = useState('');
  const [disputeAgreedTimeLimitExpiresAt, setDisputeAgreedTimeLimitExpiresAt] = useState('');
  const [disputeContractMadeAt, setDisputeContractMadeAt] = useState('');
  const [disputeLimitationStartedAt, setDisputeLimitationStartedAt] = useState('');
  const [disputeLimitationExpiresAt, setDisputeLimitationExpiresAt] = useState('');
  const [disputeWrittenClaimReceivedAt, setDisputeWrittenClaimReceivedAt] = useState('');
  const [disputeClaimRejectedAt, setDisputeClaimRejectedAt] = useState('');
  const [disputeDocumentsReturnedAt, setDisputeDocumentsReturnedAt] = useState('');
  const [disputePartyDesignatedCourt, setDisputePartyDesignatedCourt] = useState(false);
  const [disputeDefendantResidenceBasis, setDisputeDefendantResidenceBasis] = useState(false);
  const [disputeContractBranchBasis, setDisputeContractBranchBasis] = useState(false);
  const [disputeTakeoverPlaceBasis, setDisputeTakeoverPlaceBasis] = useState(false);
  const [disputeDeliveryPlaceBasis, setDisputeDeliveryPlaceBasis] = useState(false);
  const [disputeContractingCountryCourt, setDisputeContractingCountryCourt] = useState(false);
  const [disputeSecurityForCostsNotRequired, setDisputeSecurityForCostsNotRequired] = useState(false);
  const [disputeSamePartiesAndGrounds, setDisputeSamePartiesAndGrounds] = useState(false);
  const [disputePendingCompetentProceeding, setDisputePendingCompetentProceeding] = useState(false);
  const [disputeJudgmentEnteredByCompetentCourt, setDisputeJudgmentEnteredByCompetentCourt] = useState(false);
  const [disputeFreshProceedingOnlyIfNotEnforceable, setDisputeFreshProceedingOnlyIfNotEnforceable] = useState(false);
  const [disputeJudgmentFormalitiesOnly, setDisputeJudgmentFormalitiesOnly] = useState(false);
  const [disputeMeritsReopeningProhibited, setDisputeMeritsReopeningProhibited] = useState(false);
  const [disputeWilfulMisconductEquivalent, setDisputeWilfulMisconductEquivalent] = useState(false);
  const [disputeLimitationPeriodRecorded, setDisputeLimitationPeriodRecorded] = useState(true);
  const [disputeLimitationStartDateExcluded, setDisputeLimitationStartDateExcluded] = useState(true);
  const [disputeClaimBarredNoCounterclaim, setDisputeClaimBarredNoCounterclaim] = useState(false);
  const [disputeCourtLawExtensionBasis, setDisputeCourtLawExtensionBasis] = useState(false);
  const [disputeWrittenClaimReceived, setDisputeWrittenClaimReceived] = useState(false);
  const [disputeCarrierRejectedClaimInWriting, setDisputeCarrierRejectedClaimInWriting] = useState(false);
  const [disputeDocumentsReturned, setDisputeDocumentsReturned] = useState(false);
  const [disputeClaimPartlyAdmitted, setDisputeClaimPartlyAdmitted] = useState(false);
  const [disputeRemainingPartStillInDispute, setDisputeRemainingPartStillInDispute] = useState(false);
  const [disputeReceiptReplyReturnProofBurden, setDisputeReceiptReplyReturnProofBurden] = useState(false);
  const [disputeFurtherSameObjectNoSuspend, setDisputeFurtherSameObjectNoSuspend] = useState(false);
  const [disputeArbitrationClausePresent, setDisputeArbitrationClausePresent] = useState(false);
  const [disputeArbitrationTribunalCompetence, setDisputeArbitrationTribunalCompetence] = useState(false);
  const [disputeTribunalAppliesCmr, setDisputeTribunalAppliesCmr] = useState(false);
  const [disputeJurisdictionDetail, setDisputeJurisdictionDetail] = useState('');
  const [disputePartyAgreementDetail, setDisputePartyAgreementDetail] = useState('');
  const [disputeTakeoverDeliveryPlaceDetail, setDisputeTakeoverDeliveryPlaceDetail] = useState('');
  const [disputePendingActionDetail, setDisputePendingActionDetail] = useState('');
  const [disputeJudgmentDetail, setDisputeJudgmentDetail] = useState('');
  const [disputeLimitationCalculationDetail, setDisputeLimitationCalculationDetail] = useState('');
  const [disputeWrittenClaimDetail, setDisputeWrittenClaimDetail] = useState('');
  const [disputeRejectionDetail, setDisputeRejectionDetail] = useState('');
  const [disputeArbitrationClauseDetail, setDisputeArbitrationClauseDetail] = useState('');
  const [disputeCourtOrTribunalDetail, setDisputeCourtOrTribunalDetail] = useState('');
  const [multimodalRecordType, setMultimodalRecordType] = useState<MultimodalCarriageRecordType>('loaded_vehicle_other_mode_carriage');
  const [multimodalOtherModeType, setMultimodalOtherModeType] = useState<CmrArticle2OtherModeType>('rail');
  const [multimodalLinkedOtherModeDocumentId, setMultimodalLinkedOtherModeDocumentId] = useState('');
  const [multimodalLinkedArticle14ImpedimentId, setMultimodalLinkedArticle14ImpedimentId] = useState('');
  const [multimodalLinkedDeclarationId, setMultimodalLinkedDeclarationId] = useState('');
  const [multimodalVehicleCarriedByOtherMode, setMultimodalVehicleCarriedByOtherMode] = useState(true);
  const [multimodalGoodsRemainedLoaded, setMultimodalGoodsRemainedLoaded] = useState(true);
  const [multimodalGoodsUnloaded, setMultimodalGoodsUnloaded] = useState(false);
  const [multimodalArticle14BasisRecorded, setMultimodalArticle14BasisRecorded] = useState(false);
  const [multimodalOtherModeEvidencePresent, setMultimodalOtherModeEvidencePresent] = useState(true);
  const [multimodalOtherModeDetail, setMultimodalOtherModeDetail] = useState('');
  const [multimodalArticle14ImpedimentDetail, setMultimodalArticle14ImpedimentDetail] = useState('');
  const [copyRecordType, setCopyRecordType] = useState<ConsignmentNoteCopyRecordType>('sender_first_copy_access_available');
  const [copyLinkedAccessRecordId, setCopyLinkedAccessRecordId] = useState('');
  const [copyLinkedHandoverRecordId, setCopyLinkedHandoverRecordId] = useState('');
  const [copyElectronicAccessAvailable, setCopyElectronicAccessAvailable] = useState(true);
  const [copyEntitledPartyAccessAvailable, setCopyEntitledPartyAccessAvailable] = useState(true);
  const [copySenderFirstCopyAvailable, setCopySenderFirstCopyAvailable] = useState(true);
  const [copyGoodsAccompanyingSecondCopyAvailable, setCopyGoodsAccompanyingSecondCopyAvailable] = useState(false);
  const [copyCarrierRetainedThirdCopyAvailable, setCopyCarrierRetainedThirdCopyAvailable] = useState(false);
  const [copyAccessDetail, setCopyAccessDetail] = useState('');
  const [ecmrSenderRequestDetail, setEcmrSenderRequestDetail] = useState('');
  const [ecmrReceiptHandoverAt, setEcmrReceiptHandoverAt] = useState('');
  const [ecmrLinkedGoodsReceiptId, setEcmrLinkedGoodsReceiptId] = useState('');
  const [ecmrLinkedShipmentIdentificationRecordId, setEcmrLinkedShipmentIdentificationRecordId] = useState('');
  const [ecmrLinkedAccessInformationRecordId, setEcmrLinkedAccessInformationRecordId] = useState('');
  const [ecmrSenderRequestReceived, setEcmrSenderRequestReceived] = useState(true);
  const [ecmrReceiptForGoodsProvided, setEcmrReceiptForGoodsProvided] = useState(true);
  const [ecmrShipmentIdentificationInformationProvided, setEcmrShipmentIdentificationInformationProvided] = useState(true);
  const [ecmrElectronicConsignmentNoteAccessInformationProvided, setEcmrElectronicConsignmentNoteAccessInformationProvided] = useState(true);
  const [ecmrReceiptDetail, setEcmrReceiptDetail] = useState('');
  const [ecmrShipmentIdentificationDetail, setEcmrShipmentIdentificationDetail] = useState('');
  const [ecmrAccessInformationDetail, setEcmrAccessInformationDetail] = useState('');
  const [senderParticularsRecordType, setSenderParticularsRecordType] = useState<SenderParticularsResponsibilityRecordType>('sender_article6_particular_inaccuracy');
  const [senderParticularsCategory, setSenderParticularsCategory] = useState<SenderParticularsResponsibilityCategory>('article6_1_f_goods_packing');
  const [senderParticularsLinkedParticularRecordId, setSenderParticularsLinkedParticularRecordId] = useState('');
  const [senderParticularsLinkedInstructionId, setSenderParticularsLinkedInstructionId] = useState('');
  const [senderParticularsLinkedClaimId, setSenderParticularsLinkedClaimId] = useState('');
  const [senderParticularsLinkedExpenseRecordId, setSenderParticularsLinkedExpenseRecordId] = useState('');
  const [senderParticularsLinkedCarrierEntryRecordId, setSenderParticularsLinkedCarrierEntryRecordId] = useState('');
  const [senderParticularsLinkedReviewId, setSenderParticularsLinkedReviewId] = useState('');
  const [senderParticularsReviewedAt, setSenderParticularsReviewedAt] = useState('');
  const [senderParticularsInaccuracyRecorded, setSenderParticularsInaccuracyRecorded] = useState(true);
  const [senderParticularsExpenseLossDamageClaimed, setSenderParticularsExpenseLossDamageClaimed] = useState(true);
  const [senderParticularsCarrierEnteredAtRequest, setSenderParticularsCarrierEnteredAtRequest] = useState(false);
  const [senderParticularsSenderBehalfPresumption, setSenderParticularsSenderBehalfPresumption] = useState(false);
  const [senderParticularsCmrStatementOmissionRecorded, setSenderParticularsCmrStatementOmissionRecorded] = useState(false);
  const [senderParticularsCarrierLiabilityBasisRecorded, setSenderParticularsCarrierLiabilityBasisRecorded] = useState(false);
  const [senderParticularsParticularDetail, setSenderParticularsParticularDetail] = useState('');
  const [senderParticularsInstructionDetail, setSenderParticularsInstructionDetail] = useState('');
  const [senderParticularsExpenseLossDamageDetail, setSenderParticularsExpenseLossDamageDetail] = useState('');
  const [senderParticularsCarrierEntryDetail, setSenderParticularsCarrierEntryDetail] = useState('');
  const [senderParticularsCmrStatementOmissionDetail, setSenderParticularsCmrStatementOmissionDetail] = useState('');
  const [successiveCarrierRecordType, setSuccessiveCarrierRecordType] = useState<SuccessiveCarrierRecordType>('successive_carrier_acceptance');
  const [successiveAllocationBasis, setSuccessiveAllocationBasis] = useState<SuccessiveCarrierAllocationBasis>('unknown_attribution_payment_share');
  const [successiveRecoveryStartBasis, setSuccessiveRecoveryStartBasis] = useState<SuccessiveCarrierRecoveryLimitationStartBasis>('actual_payment');
  const [successiveLinkedSuccessiveCarrierId, setSuccessiveLinkedSuccessiveCarrierId] = useState('');
  const [successiveLinkedPreviousCarrierId, setSuccessiveLinkedPreviousCarrierId] = useState('');
  const [successiveLinkedNextCarrierId, setSuccessiveLinkedNextCarrierId] = useState('');
  const [successiveLinkedConsignmentNoteCopyId, setSuccessiveLinkedConsignmentNoteCopyId] = useState('');
  const [successiveLinkedReservationId, setSuccessiveLinkedReservationId] = useState('');
  const [successiveLinkedClaimId, setSuccessiveLinkedClaimId] = useState('');
  const [successiveLinkedCompensationPaymentId, setSuccessiveLinkedCompensationPaymentId] = useState('');
  const [successiveLinkedCompensationAssessmentId, setSuccessiveLinkedCompensationAssessmentId] = useState('');
  const [successiveLinkedProceedingId, setSuccessiveLinkedProceedingId] = useState('');
  const [successiveLinkedCarrierAgreementId, setSuccessiveLinkedCarrierAgreementId] = useState('');
  const [successiveCarrierAcceptedAt, setSuccessiveCarrierAcceptedAt] = useState('');
  const [successiveReceiptIssuedAt, setSuccessiveReceiptIssuedAt] = useState('');
  const [successiveCompensationPaidAt, setSuccessiveCompensationPaidAt] = useState('');
  const [successiveFinalJudicialDecisionAt, setSuccessiveFinalJudicialDecisionAt] = useState('');
  const [successiveRecoveryClaimFiledAt, setSuccessiveRecoveryClaimFiledAt] = useState('');
  const [successiveAgreementRecordedAt, setSuccessiveAgreementRecordedAt] = useState('');
  const [successiveArticle8ReservationsEntered, setSuccessiveArticle8ReservationsEntered] = useState(true);
  const [successiveActionAgainstFirstCarrier, setSuccessiveActionAgainstFirstCarrier] = useState(true);
  const [successiveActionAgainstLastCarrier, setSuccessiveActionAgainstLastCarrier] = useState(false);
  const [successiveActionAgainstEventCarrier, setSuccessiveActionAgainstEventCarrier] = useState(false);
  const [successiveCounterclaimOrSetoff, setSuccessiveCounterclaimOrSetoff] = useState(false);
  const [successiveMultipleCarriersJoined, setSuccessiveMultipleCarriersJoined] = useState(false);
  const [successivePaymentValidityDisputeBar, setSuccessivePaymentValidityDisputeBar] = useState(false);
  const [successiveAllConcernedCarriersMayBeDefendants, setSuccessiveAllConcernedCarriersMayBeDefendants] = useState(true);
  const [successiveReceiptDetail, setSuccessiveReceiptDetail] = useState('');
  const [successiveCarrierIdentityEntryDetail, setSuccessiveCarrierIdentityEntryDetail] = useState('');
  const [successiveReservationDetail, setSuccessiveReservationDetail] = useState('');
  const [successiveActionScopeDetail, setSuccessiveActionScopeDetail] = useState('');
  const [successiveAllocationDetail, setSuccessiveAllocationDetail] = useState('');
  const [successiveInsolvencyDetail, setSuccessiveInsolvencyDetail] = useState('');
  const [successiveProceedingDetail, setSuccessiveProceedingDetail] = useState('');
  const [successiveAgreementDetail, setSuccessiveAgreementDetail] = useState('');
  const [successiveNoticeOpportunityDetail, setSuccessiveNoticeOpportunityDetail] = useState('');
  const successiveAcceptanceRecordSelected = successiveCarrierRecordType === 'successive_carrier_acceptance';
  const successiveRecoveryRecordSelected =
    successiveCarrierRecordType === 'successive_carrier_recovery_allocation' ||
    successiveCarrierRecordType === 'successive_carrier_recovery_proceeding';
  const [prohibitedClauseRecordType, setProhibitedClauseRecordType] = useState<ProhibitedClauseRecordType>('convention_derogation_clause');
  const [prohibitedLinkedContractTermId, setProhibitedLinkedContractTermId] = useState('');
  const [prohibitedLinkedClauseReviewId, setProhibitedLinkedClauseReviewId] = useState('');
  const [prohibitedLinkedInsuranceRecordId, setProhibitedLinkedInsuranceRecordId] = useState('');
  const [prohibitedLinkedClaimId, setProhibitedLinkedClaimId] = useState('');
  const [prohibitedLinkedArticle40AgreementId, setProhibitedLinkedArticle40AgreementId] = useState('');
  const [prohibitedClauseReviewedAt, setProhibitedClauseReviewedAt] = useState('');
  const [prohibitedArticle40ExceptionAssessed, setProhibitedArticle40ExceptionAssessed] = useState(true);
  const [prohibitedArticle40ExceptionNotApplicable, setProhibitedArticle40ExceptionNotApplicable] = useState(true);
  const [prohibitedNullitySeverabilityRecorded, setProhibitedNullitySeverabilityRecorded] = useState(true);
  const [prohibitedReviewOutcomeRecorded, setProhibitedReviewOutcomeRecorded] = useState(true);
  const [prohibitedClauseDetail, setProhibitedClauseDetail] = useState('');
  const [prohibitedReviewDetail, setProhibitedReviewDetail] = useState('');
  const [prohibitedInsuranceBenefitDetail, setProhibitedInsuranceBenefitDetail] = useState('');
  const [prohibitedBurdenShiftDetail, setProhibitedBurdenShiftDetail] = useState('');
  const [prohibitedArticle40ExceptionDetail, setProhibitedArticle40ExceptionDetail] = useState('');
  const [replacementMedium, setReplacementMedium] = useState<ReplacementByOtherMeansMedium>('paper');
  const [replacementReason, setReplacementReason] = useState('');
  const [replacementReplacedAt, setReplacementReplacedAt] = useState('');
  const [replacementExternalReference, setReplacementExternalReference] = useState('');
  const [disposalInstructionType, setDisposalInstructionType] = useState<DisposalInstructionType>('stop_in_transit');
  const [disposalRightNotCeased, setDisposalRightNotCeased] = useState(true);
  const [disposalFirstCopyProduced, setDisposalFirstCopyProduced] = useState(true);
  const [disposalCarrierIndemnityAccepted, setDisposalCarrierIndemnityAccepted] = useState(true);
  const [disposalInstructionFeasible, setDisposalInstructionFeasible] = useState(true);
  const [disposalDoesNotPrejudiceOtherConsignments, setDisposalDoesNotPrejudiceOtherConsignments] = useState(true);
  const [disposalDoesNotDivideConsignment, setDisposalDoesNotDivideConsignment] = useState(true);
  const [disposalSuccessiveConsigneeInstructionFinal, setDisposalSuccessiveConsigneeInstructionFinal] = useState(false);
  const [disposalLinkedConsignmentNoteCopyRecordId, setDisposalLinkedConsignmentNoteCopyRecordId] = useState('');
  const [disposalRequestedExecutionAt, setDisposalRequestedExecutionAt] = useState('');
  const [disposalReason, setDisposalReason] = useState('');
  const [disposalNewDeliveryPlace, setDisposalNewDeliveryPlace] = useState('');
  const [disposalNewConsignee, setDisposalNewConsignee] = useState('');
  const [disposalNoticeInstructionId, setDisposalNoticeInstructionId] = useState('');
  const [disposalNoticeSentAt, setDisposalNoticeSentAt] = useState('');
  const [disposalNoticeTargetRole, setDisposalNoticeTargetRole] = useState<DisposalExecutionNoticeTargetRole>('consignor');
  const [disposalNoticeReason, setDisposalNoticeReason] = useState('');
  const [disposalNoticeDetail, setDisposalNoticeDetail] = useState('');
  const [consigneeDeliveryRequestType, setConsigneeDeliveryRequestType] = useState<ConsigneeDeliveryRequestType>('delivery_after_arrival');
  const [consigneeArrivalAtDeliveryPlace, setConsigneeArrivalAtDeliveryPlace] = useState(true);
  const [consigneeDeliveryRequested, setConsigneeDeliveryRequested] = useState(true);
  const [consigneeSecondCopyRequested, setConsigneeSecondCopyRequested] = useState(true);
  const [consigneeReceiptCommitment, setConsigneeReceiptCommitment] = useState(true);
  const [consigneeChargesPaymentAcknowledged, setConsigneeChargesPaymentAcknowledged] = useState(true);
  const [consigneeChargesDisputed, setConsigneeChargesDisputed] = useState(false);
  const [consigneeSecurityFurnished, setConsigneeSecurityFurnished] = useState(false);
  const [consigneeChargesPaidLines, setConsigneeChargesPaidLines] = useState([
    { amount: '', currency: 'EUR' },
  ]);
  const [consigneeChargesPaidAt, setConsigneeChargesPaidAt] = useState('');
  const [consigneeChargesPaymentEvidenceAttachmentId, setConsigneeChargesPaymentEvidenceAttachmentId] = useState('');
  const [consigneeSecurityFurnishedAt, setConsigneeSecurityFurnishedAt] = useState('');
  const [consigneeChargesSecurityEvidenceAttachmentId, setConsigneeChargesSecurityEvidenceAttachmentId] = useState('');
  const [consigneeRightsReservedNotice, setConsigneeRightsReservedNotice] = useState(false);
  const [consigneeLossEstablished, setConsigneeLossEstablished] = useState(false);
  const [consigneeArticle19DelayPeriodExpired, setConsigneeArticle19DelayPeriodExpired] = useState(false);
  const [consigneeLinkedDeliveryId, setConsigneeLinkedDeliveryId] = useState('');
  const [consigneeLinkedSecondCopyRecordId, setConsigneeLinkedSecondCopyRecordId] = useState('');
  const [consigneeGoodsReceived, setConsigneeGoodsReceived] = useState(false);
  const [consigneeSecondCopyReceived, setConsigneeSecondCopyReceived] = useState(false);
  const [consigneeReceiptIssued, setConsigneeReceiptIssued] = useState(false);
  const [consigneeReceiptDetail, setConsigneeReceiptDetail] = useState('');
  const [consigneeChargesSecurityDetail, setConsigneeChargesSecurityDetail] = useState('');
  const [consigneeEnforcementBasisDetail, setConsigneeEnforcementBasisDetail] = useState('');
  const [consigneeReason, setConsigneeReason] = useState('');
  const [deliveryDeliveredAt, setDeliveryDeliveredAt] = useState('');
  const [deliveryRecipientName, setDeliveryRecipientName] = useState('');
  const [deliveryLocationName, setDeliveryLocationName] = useState('');
  const [deliveryCity, setDeliveryCity] = useState('');
  const [deliveryCountry, setDeliveryCountry] = useState('');
  const [deliveryReservationCategory, setDeliveryReservationCategory] = useState<DeliveryReservationCategory>('damage');
  const [deliveryReservationGrounds, setDeliveryReservationGrounds] = useState('');
  const [deliveryPackageReference, setDeliveryPackageReference] = useState('');
  const [deliveryAcknowledgementDeliveryId, setDeliveryAcknowledgementDeliveryId] = useState('');
  const [deliveryAcknowledgementReservationId, setDeliveryAcknowledgementReservationId] = useState('');
  const [deliveryAcknowledgementDecision, setDeliveryAcknowledgementDecision] = useState<DeliveryReservationAcknowledgementDecision>('acknowledged');
  const [deliveryAcknowledgementComment, setDeliveryAcknowledgementComment] = useState('');
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const [attachmentType, setAttachmentType] = useState('');
  const [attachmentDescription, setAttachmentDescription] = useState('');
  const [attachments, setAttachments] = useState<SupplementaryDocumentEvidence[]>([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const loadAttachments = useCallback(async () => {
    if (!documentId) return;
    setLoadingAttachments(true);
    try {
      setAttachments(await listSupplementaryDocumentsInAPI(documentId));
    } catch (loadError) {
      setError(sanitizedOperationalErrorMessage);
    } finally {
      setLoadingAttachments(false);
    }
  }, [documentId]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  useEffect(() => {
    const article17_2CauseAlleged = carrierLiabilityClaimantFault
      || carrierLiabilityClaimantInstruction
      || carrierLiabilityInherentVice
      || carrierLiabilityUnavoidableCircumstances;
    if (!article17_2CauseAlleged) {
      setCarrierLiabilityVehicleDefectContributed(false);
      setCarrierLiabilityHiredVehicleProviderContribution(false);
      setCarrierLiabilityArticle17_3AssessmentDetail('');
      setCarrierLiabilityArticle17_3EvidenceAttachmentId('');
      setCarrierLiabilityArticle18_1CauseProofEstablished(false);
    }
  }, [
    carrierLiabilityClaimantFault,
    carrierLiabilityClaimantInstruction,
    carrierLiabilityInherentVice,
    carrierLiabilityUnavoidableCircumstances,
  ]);

  useEffect(() => {
    if (!carrierLiabilityArticle18_1CauseProofEstablished) {
      setCarrierLiabilityArticle18_1ProofAssessmentDetail('');
      setCarrierLiabilityArticle18_1EvidenceAttachmentId('');
      setCarrierLiabilityArticle17_2CauseContributed(false);
    }
  }, [carrierLiabilityArticle18_1CauseProofEstablished]);

  useEffect(() => {
    if (!carrierLiabilitySpecialRiskClaimed) {
      setCarrierLiabilityArticle18_2CircumstancesEstablished(false);
    }
    if (!carrierLiabilitySpecialRiskClaimed || !carrierLiabilityArticle18_2CircumstancesEstablished) {
      setCarrierLiabilityArticle18_2AssessmentDetail('');
      setCarrierLiabilityArticle18_2EvidenceAttachmentId('');
      setCarrierLiabilitySpecialRiskPresumption(false);
    }
  }, [
    carrierLiabilityArticle18_2CircumstancesEstablished,
    carrierLiabilitySpecialRiskClaimed,
  ]);

  useEffect(() => {
    const article17_3FactorReported = carrierLiabilityVehicleDefectContributed
      || carrierLiabilityHiredVehicleProviderContribution;
    if (!article17_3FactorReported) {
      setCarrierLiabilityArticle17_2CauseContributed(false);
    }
    if (!article17_3FactorReported || !carrierLiabilityArticle17_2CauseContributed) {
      setCarrierLiabilityArticle17_5CarrierShare('');
      setCarrierLiabilityArticle17_5ReliefShare('');
      setCarrierLiabilityArticle17_5AssessmentDetail('');
      setCarrierLiabilityArticle17_5EvidenceAttachmentId('');
    }
  }, [
    carrierLiabilityArticle17_2CauseContributed,
    carrierLiabilityHiredVehicleProviderContribution,
    carrierLiabilityVehicleDefectContributed,
  ]);

  const resetFeedback = () => {
    setMessage('');
    setError('');
  };

  const toggleSenderRequestedCheck = (checkType: CarrierTakeoverRequestedCheck) => {
    setSenderRequestedChecks((current) =>
      current.includes(checkType)
        ? current.filter((item) => item !== checkType)
        : [...current, checkType]
    );
  };

  const handleCarrierReservation = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      await recordCarrierReservationInAPI(documentId, {
        category: carrierCategory,
        grounds: carrierGrounds,
        ...(carrierPackageReference ? { packageReference: carrierPackageReference } : {}),
        ...(carrierObservedValue ? { observedValue: carrierObservedValue } : {}),
        ...(carrierExpectedValue ? { expectedValue: carrierExpectedValue } : {}),
      });
      setMessage('Carrier reservation recorded without exposing private evidence identifiers');
      setCarrierGrounds('');
      setCarrierPackageReference('');
      setCarrierObservedValue('');
      setCarrierExpectedValue('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarrierCheckRequest = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    if (senderRequestedChecks.length === 0) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    try {
      await requestCarrierTakeoverCheckInAPI(documentId, {
        requestedChecks: senderRequestedChecks,
        ...(senderCheckReason ? { reason: senderCheckReason } : {}),
      });
      setMessage('Carrier requested checks recorded without exposing private request details');
      setSenderRequestedChecks(['gross_weight']);
      setSenderCheckReason('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarrierTakeoverCheck = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const takeoverPayload: CarrierTakeoverCheckRequest = {
        result: takeoverResult,
        packageCountChecked,
        marksAndNumbersChecked,
        apparentConditionChecked,
        grossWeightChecked,
        quantityChecked,
        contentsChecked,
        ...(takeoverComment ? { comment: takeoverComment } : {}),
      };
      if (takeoverResult !== 'accepted' && takeoverReservationId) {
        takeoverPayload.reservationId = takeoverReservationId;
      }
      await recordCarrierTakeoverCheckInAPI(documentId, takeoverPayload);
      setMessage('Carrier takeover check recorded without exposing private check details');
      setTakeoverResult('accepted');
      setPackageCountChecked(true);
      setMarksAndNumbersChecked(true);
      setApparentConditionChecked(true);
      setGrossWeightChecked(false);
      setQuantityChecked(false);
      setContentsChecked(false);
      setTakeoverReservationId('');
      setTakeoverComment('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarriageImpedimentTypeChange = (nextType: CarriageImpedimentType) => {
    setCarriageImpedimentType(nextType);
    const instructionsRequired = carriageInstructionRequestTypes.has(nextType);
    setCarriageInstructionsRequested(instructionsRequired);
    setCarriageInstructionTargetRole(
      nextType === 'carriage_impossible_before_arrival' || nextType === 'alternative_carriage_taken'
        ? 'article12_entitled_person'
        : 'sender'
    );
    setCarriageInstructionRequestSentAt('');
    setCarriageInstructionRequestReceivedAt('');
    setCarriageInstructionRequestEvidenceAttachmentId('');
    setCarriageInstructionRequestDetail('');
    setCarriageGoodsLocationCountryCode('');
    setCarriageGoodsLocationDetail('');
    setCarriageSaleProcedureBasis('applicable_law');
    setCarriageSaleProcedureReference('');
    setCarriageSaleProcedureAssessedAt('');
    setCarriageSaleProcedureEvidenceAttachmentId('');
  };

  const handleCarriageImpediment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const carriageImpedimentPayload: CarriageImpedimentRequest = {
        impedimentType: carriageImpedimentType,
        ...(carriageReason ? { reason: carriageReason } : {}),
      };
      const appendInstructionRequest = () => {
        carriageImpedimentPayload.instructionsRequested = carriageInstructionsRequested;
        if (carriageInstructionsRequested) {
          if (
            !carriageInstructionRequestSentAt
            || !carriageInstructionRequestReceivedAt
            || !carriageInstructionRequestEvidenceAttachmentId.trim()
          ) {
            throw new Error('Instruction request evidence is incomplete');
          }
          carriageImpedimentPayload.instructionRequestTargetRole = carriageInstructionTargetRole;
          carriageImpedimentPayload.instructionRequestSentAt = carriageInstructionRequestSentAt;
          carriageImpedimentPayload.instructionRequestReceivedAt = carriageInstructionRequestReceivedAt;
          carriageImpedimentPayload.linkedInstructionRequestEvidenceAttachmentId = carriageInstructionRequestEvidenceAttachmentId.trim();
          if (carriageInstructionRequestDetail) {
            carriageImpedimentPayload.instructionRequestDetail = carriageInstructionRequestDetail;
          }
        }
      };

      if (carriageImpedimentType === 'carriage_impossible_before_arrival') {
        appendInstructionRequest();
      } else if (carriageImpedimentType === 'alternative_carriage_taken') {
        appendInstructionRequest();
        carriageImpedimentPayload.instructionsUnavailableInReasonableTime = carriageInstructionsUnavailableInReasonableTime;
        carriageImpedimentPayload.bestInterestsActionTaken = carriageBestInterestsActionTaken;
        if (carriageAlternativeCarriageConditions) {
          carriageImpedimentPayload.alternativeCarriageConditions = carriageAlternativeCarriageConditions;
        }
      } else if (
        carriageImpedimentType === 'delivery_prevented_after_arrival'
        || carriageImpedimentType === 'consignee_refusal'
      ) {
        appendInstructionRequest();
        carriageImpedimentPayload.arrivalAtDeliveryPlace = carriageArrivalAtDeliveryPlace;
        if (carriageImpedimentType === 'consignee_refusal') {
          carriageImpedimentPayload.consigneeRefusedGoods = carriageConsigneeRefusedGoods;
        }
      } else if (carriageImpedimentType === 'unload_for_account') {
        carriageImpedimentPayload.article14Or15ImpedimentBasis = carriageArticle14Or15ImpedimentBasis;
        carriageImpedimentPayload.goodsUnloadedForAccount = carriageGoodsUnloadedForAccount;
        carriageImpedimentPayload.goodsHeldForEntitledPerson = carriageGoodsHeldForEntitledPerson;
        carriageImpedimentPayload.thirdPartyCustodianUsed = carriageThirdPartyCustodianUsed;
        if (carriageThirdPartyCustodianUsed) {
          carriageImpedimentPayload.thirdPartyCustodianSelectedWithReasonableCare = carriageThirdPartyCustodianSelectedWithReasonableCare;
        }
        if (carriageStorageOrSaleDetail) {
          carriageImpedimentPayload.storageOrSaleDetail = carriageStorageOrSaleDetail;
        }
      } else if (carriageImpedimentType === 'sale_of_goods') {
        if (
          !carriageGoodsLocationCountryCode.trim()
          || !carriageGoodsLocationDetail.trim()
          || !carriageSaleProcedureReference.trim()
          || !carriageSaleProcedureAssessedAt
          || !carriageSaleProcedureEvidenceAttachmentId.trim()
        ) {
          throw new Error('Sale procedure evidence is incomplete');
        }
        carriageImpedimentPayload.article14Or15ImpedimentBasis = carriageArticle14Or15ImpedimentBasis;
        carriageImpedimentPayload.saleReason = carriageSaleReason;
        carriageImpedimentPayload.goodsLocationCountryCode = carriageGoodsLocationCountryCode.trim().toUpperCase();
        carriageImpedimentPayload.goodsLocationDetail = carriageGoodsLocationDetail;
        carriageImpedimentPayload.saleProcedureBasis = carriageSaleProcedureBasis;
        carriageImpedimentPayload.saleProcedureReference = carriageSaleProcedureReference;
        carriageImpedimentPayload.saleProcedureAssessedAt = carriageSaleProcedureAssessedAt;
        carriageImpedimentPayload.linkedSaleProcedureEvidenceAttachmentId = carriageSaleProcedureEvidenceAttachmentId.trim();
        carriageImpedimentPayload.proceedsPlacedAtDisposal = carriageProceedsPlacedAtDisposal;
        carriageImpedimentPayload.saleProcedureLawOrCustomAcknowledged = carriageSaleProcedureLawOrCustomAcknowledged;
        carriageImpedimentPayload.expensesRecoverableClaimed = carriageExpensesRecoverableClaimed;
        if (carriageStorageOrSaleDetail) {
          carriageImpedimentPayload.storageOrSaleDetail = carriageStorageOrSaleDetail;
        }
      }

      await recordCarriageImpedimentInAPI(documentId, carriageImpedimentPayload);
      setMessage('Carriage impediment recorded without exposing private Articles 14-16 details');
      setCarriageImpedimentType('carriage_impossible_before_arrival');
      setCarriageInstructionsRequested(true);
      setCarriageInstructionTargetRole('article12_entitled_person');
      setCarriageInstructionRequestSentAt('');
      setCarriageInstructionRequestReceivedAt('');
      setCarriageInstructionRequestEvidenceAttachmentId('');
      setCarriageInstructionsUnavailableInReasonableTime(false);
      setCarriageBestInterestsActionTaken(false);
      setCarriageArrivalAtDeliveryPlace(false);
      setCarriageConsigneeRefusedGoods(false);
      setCarriageArticle14Or15ImpedimentBasis(false);
      setCarriageGoodsUnloadedForAccount(false);
      setCarriageGoodsHeldForEntitledPerson(false);
      setCarriageThirdPartyCustodianUsed(false);
      setCarriageThirdPartyCustodianSelectedWithReasonableCare(false);
      setCarriageSaleReason('perishable');
      setCarriageGoodsLocationCountryCode('');
      setCarriageGoodsLocationDetail('');
      setCarriageSaleProcedureBasis('applicable_law');
      setCarriageSaleProcedureReference('');
      setCarriageSaleProcedureAssessedAt('');
      setCarriageSaleProcedureEvidenceAttachmentId('');
      setCarriageProceedsPlacedAtDisposal(false);
      setCarriageSaleProcedureLawOrCustomAcknowledged(false);
      setCarriageExpensesRecoverableClaimed(false);
      setCarriageReason('');
      setCarriageInstructionRequestDetail('');
      setCarriageAlternativeCarriageConditions('');
      setCarriageStorageOrSaleDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarriageAccounting = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const impedimentId = carriageAccountingImpedimentId.trim();
    const amount = carriageAccountingAmount.trim();
    const currencyID = carriageAccountingCurrency.trim().toUpperCase();
    const accountingAt = carriageAccountingAt.trim();
    const linkedAccountingEvidenceAttachmentId = carriageAccountingEvidenceAttachmentId.trim();
    const grossSaleProceedsAmount = carriageAccountingGrossSaleProceeds.trim();
    const deductedSaleExpensesAmount = carriageAccountingDeductedSaleExpenses.trim();
    const linkedSaleProceedsAccountingId = carriageAccountingSaleProceedsReference.trim();
    const totalChargesDueAmount = carriageAccountingTotalChargesDue.trim();
    if (!impedimentId || !amount || !currencyID || !accountingAt || !linkedAccountingEvidenceAttachmentId) {
      setError('Indica impedimento, importe, moneda, fecha y adjunto probatorio para registrar la contabilidad Article 16.');
      return;
    }
    const isInstructionExpense = carriageAccountingType === 'instruction_expense';
    const isGoodsDispositionExpense = carriageAccountingType === 'goods_disposition_expense';
    const isSaleProceeds = carriageAccountingType === 'sale_proceeds';
    const isChargeShortfall = carriageAccountingType === 'charge_shortfall';
    if (isSaleProceeds && (!grossSaleProceedsAmount || !deductedSaleExpensesAmount)) {
      setError('Indica producto bruto y gastos deducidos para reconciliar el producto neto Article 16.4.');
      return;
    }
    if (isChargeShortfall && (!linkedSaleProceedsAccountingId || !totalChargesDueAmount)) {
      setError('Indica el registro de producto de venta y los cargos totales para reconciliar la diferencia Article 16.4.');
      return;
    }
    try {
      await recordCarriageAccountingInAPI(documentId, impedimentId, {
        accountingType: carriageAccountingType,
        amount,
        currencyID,
        accountingAt,
        linkedAccountingEvidenceAttachmentId,
        ...((isInstructionExpense || isGoodsDispositionExpense || isChargeShortfall)
          ? { carrierWrongfulActOrNeglectNotAsserted: carriageAccountingCarrierNotAtFault }
          : {}),
        ...(isGoodsDispositionExpense ? { chargeableAgainstGoods: carriageAccountingChargeableAgainstGoods } : {}),
        ...(isSaleProceeds ? { proceedsPlacedAtDisposal: carriageAccountingProceedsPlaced } : {}),
        ...(isChargeShortfall ? { chargesExceedProceeds: carriageAccountingChargesExceedProceeds } : {}),
        ...(isSaleProceeds ? { grossSaleProceedsAmount, deductedSaleExpensesAmount } : {}),
        ...(isChargeShortfall ? { linkedSaleProceedsAccountingId, totalChargesDueAmount } : {}),
        ...(carriageAccountingDetail ? { accountingDetail: carriageAccountingDetail } : {}),
      });
      setMessage('Carriage accounting recorded without exposing private Article 16 amounts or details');
      setCarriageAccountingImpedimentId('');
      setCarriageAccountingType('sale_proceeds');
      setCarriageAccountingAmount('');
      setCarriageAccountingCurrency('EUR');
      setCarriageAccountingAt('');
      setCarriageAccountingEvidenceAttachmentId('');
      setCarriageAccountingCarrierNotAtFault(false);
      setCarriageAccountingChargeableAgainstGoods(false);
      setCarriageAccountingProceedsPlaced(false);
      setCarriageAccountingChargesExceedProceeds(false);
      setCarriageAccountingGrossSaleProceeds('');
      setCarriageAccountingDeductedSaleExpenses('');
      setCarriageAccountingSaleProceedsReference('');
      setCarriageAccountingTotalChargesDue('');
      setCarriageAccountingDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarrierLiabilityIncident = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const isArticle12Incident = carrierLiabilityIncidentType === 'article12_disposal_instruction_noncompliance';
    const article17_2CauseAlleged = !isArticle12Incident && (
      carrierLiabilityClaimantFault
      || carrierLiabilityClaimantInstruction
      || carrierLiabilityInherentVice
      || carrierLiabilityUnavoidableCircumstances
    );
    const article17_3FactorReported = article17_2CauseAlleged && (
      carrierLiabilityVehicleDefectContributed
      || carrierLiabilityHiredVehicleProviderContribution
    );
    const article17_5MixedCausationReported = article17_3FactorReported
      && carrierLiabilityArticle17_2CauseContributed;
    const carrierProofRequired = !isArticle12Incident && (
      article17_2CauseAlleged
      || carrierLiabilitySpecialRiskClaimed
      || carrierLiabilitySpecialRiskPresumption
    );
    const isOpenUnsheetedVehicleRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'open_unsheeted_vehicle';
    const isArticle17_4bPackingRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'defective_or_missing_packing';
    const isArticle17_4cOperationsRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'sender_loading_handling_stowage_unloading';
    const isArticle17_4dGoodsNatureRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'goods_nature';
    const isArticle17_4eMarkingRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'insufficient_marks_numbers';
    const isArticle17_4fLivestockRisk = !isArticle12Incident
      && carrierLiabilitySpecialRiskClaimed
      && carrierLiabilitySpecialRiskType === 'livestock';
    if (!isArticle12Incident && (
      !carrierLiabilityTakingOverAt
      || !carrierLiabilityIncidentAt
      || !carrierLiabilityIncidentEvidenceAttachmentId.trim()
      || !carrierLiabilityIncidentDetail.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (carrierProofRequired && (
      !carrierLiabilityCarrierProofDetail.trim()
      || !carrierLiabilityCarrierProofEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (article17_3FactorReported && (
      !carrierLiabilityArticle17_3AssessmentDetail.trim()
      || !carrierLiabilityArticle17_3EvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (article17_2CauseAlleged && carrierLiabilityArticle18_1CauseProofEstablished && (
      !carrierLiabilityArticle18_1ProofAssessmentDetail.trim()
      || !carrierLiabilityArticle18_1EvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (carrierLiabilitySpecialRiskClaimed && carrierLiabilityArticle18_2CircumstancesEstablished && (
      !carrierLiabilityArticle18_2AssessmentDetail.trim()
      || !carrierLiabilityArticle18_2EvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isOpenUnsheetedVehicleRisk && (
      !carrierLiabilityArticle17_4aOpenVehicleUsed
      || !carrierLiabilityArticle17_4aAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4aEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle17_4bPackingRisk && (
      !carrierLiabilityArticle17_4bPackingAbsentOrDefective
      || !carrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking
      || !carrierLiabilityArticle17_4bAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4bEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle17_4cOperationsRisk && (
      !(
        carrierLiabilityArticle17_4cHandlingPerformed
        || carrierLiabilityArticle17_4cLoadingPerformed
        || carrierLiabilityArticle17_4cStowagePerformed
        || carrierLiabilityArticle17_4cUnloadingPerformed
      )
      || !carrierLiabilityArticle17_4cActorCapacity
      || !carrierLiabilityArticle17_4cAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4cEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle17_4dGoodsNatureRisk && (
      !carrierLiabilityArticle17_4dGoodsParticularlyExposed
      || !carrierLiabilityArticle17_4dSusceptibilityBasis
      || !carrierLiabilityArticle17_4dAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4dEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle17_4eMarkingRisk && (
      !(carrierLiabilityArticle17_4ePackageMarksDeficient || carrierLiabilityArticle17_4ePackageNumbersDeficient)
      || !carrierLiabilityArticle17_4eDeficiencyBasis
      || !carrierLiabilityArticle17_4eAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4eEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle17_4fLivestockRisk && (
      !carrierLiabilityArticle17_4fLivestockCarriageConfirmed
      || !carrierLiabilityArticle17_4fAssessmentDetail.trim()
      || !carrierLiabilityArticle17_4fEvidenceAttachmentId.trim()
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (article17_5MixedCausationReported) {
      const carrierShare = Number(carrierLiabilityArticle17_5CarrierShare);
      const reliefShare = Number(carrierLiabilityArticle17_5ReliefShare);
      if (
        !carrierLiabilityArticle17_5CarrierShare.trim()
        || !carrierLiabilityArticle17_5ReliefShare.trim()
        || !Number.isFinite(carrierShare)
        || !Number.isFinite(reliefShare)
        || carrierShare <= 0
        || reliefShare <= 0
        || carrierShare > 100
        || reliefShare > 100
        || Math.abs(carrierShare + reliefShare - 100) > 1e-9
        || !carrierLiabilityArticle17_5AssessmentDetail.trim()
        || !carrierLiabilityArticle17_5EvidenceAttachmentId.trim()
      ) {
        setError(sanitizedOperationalErrorMessage);
        return;
      }
    }
    if (
      isOpenUnsheetedVehicleRisk
      && carrierLiabilitySpecialRiskPresumption
      && (carrierLiabilityAbnormalShortageObserved || carrierLiabilityAnyPackageLost)
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (!isArticle12Incident && (
      ((carrierLiabilityIncidentType === 'total_loss' || carrierLiabilityIncidentType === 'partial_loss') && !carrierLiabilityLossObserved) ||
      (carrierLiabilityIncidentType === 'damage' && !carrierLiabilityDamageObserved) ||
      (carrierLiabilityIncidentType === 'delay' && !carrierLiabilityDelayObserved)
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    if (isArticle12Incident && (
      !carrierLiabilityDisposalInstructionId.trim() ||
      (!carrierLiabilityInstructionNotCarriedOut && !carrierLiabilityInstructionWithoutFirstCopy) ||
      !carrierLiabilityClaimantEntitlement ||
      !carrierLiabilityLossOrDamageCaused ||
      (!carrierLiabilityLossObserved && !carrierLiabilityDamageObserved)
    )) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: CarrierLiabilityIncidentRequest = {
      incidentType: carrierLiabilityIncidentType,
      ...(carrierLiabilityIncidentAt ? { incidentAt: carrierLiabilityIncidentAt } : {}),
      ...(isArticle12Incident ? {
        linkedDisposalInstructionId: carrierLiabilityDisposalInstructionId.trim(),
        ...(carrierLiabilityInstructionNotCarriedOut ? { instructionNotCarriedOut: true } : {}),
        ...(carrierLiabilityInstructionWithoutFirstCopy ? { instructionCarriedOutWithoutFirstCopy: true } : {}),
        claimantEntitlementAsserted: true,
        lossOrDamageCaused: true,
        ...(carrierLiabilityLossObserved ? { lossObserved: true } : {}),
        ...(carrierLiabilityDamageObserved ? { damageObserved: true } : {}),
      } : {
        takingOverAt: carrierLiabilityTakingOverAt,
        ...(carrierLiabilityDeliveryAt ? { deliveryAt: carrierLiabilityDeliveryAt } : {}),
        linkedIncidentEvidenceAttachmentId: carrierLiabilityIncidentEvidenceAttachmentId.trim(),
        ...(carrierProofRequired ? {
          linkedCarrierProofEvidenceAttachmentId: carrierLiabilityCarrierProofEvidenceAttachmentId.trim(),
        } : {}),
        ...(carrierLiabilityIncidentType === 'total_loss' || carrierLiabilityIncidentType === 'partial_loss' ? { lossObserved: true } : {}),
        ...(carrierLiabilityIncidentType === 'damage' ? { damageObserved: true } : {}),
        ...(carrierLiabilityIncidentType === 'delay' ? { delayObserved: true } : {}),
        ...(carrierLiabilityClaimantFault ? { claimantFaultOrNeglectAlleged: true } : {}),
        ...(carrierLiabilityClaimantInstruction ? { claimantInstructionAlleged: true } : {}),
        ...(carrierLiabilityInherentVice ? { inherentViceAlleged: true } : {}),
        ...(carrierLiabilityUnavoidableCircumstances ? { unavoidableCircumstancesAlleged: true } : {}),
        ...(article17_2CauseAlleged ? {
          vehicleDefectContributed: carrierLiabilityVehicleDefectContributed,
          hiredVehicleProviderActOrNeglectContributed: carrierLiabilityHiredVehicleProviderContribution,
          article18_1CauseProofEstablished: carrierLiabilityArticle18_1CauseProofEstablished,
          ...(carrierLiabilityArticle18_1CauseProofEstablished ? {
            article18_1ProofAssessmentDetail: carrierLiabilityArticle18_1ProofAssessmentDetail,
            linkedArticle18_1EvidenceAttachmentId: carrierLiabilityArticle18_1EvidenceAttachmentId.trim(),
          } : {}),
        } : {}),
        ...(article17_3FactorReported ? {
          linkedArticle17_3EvidenceAttachmentId: carrierLiabilityArticle17_3EvidenceAttachmentId.trim(),
          article17_2CauseContributed: carrierLiabilityArticle17_2CauseContributed,
          ...(article17_5MixedCausationReported ? {
            article17_5CarrierLiableFactorSharePercent: carrierLiabilityArticle17_5CarrierShare.trim(),
            article17_5ReliefFactorSharePercent: carrierLiabilityArticle17_5ReliefShare.trim(),
            article17_5AssessmentDetail: carrierLiabilityArticle17_5AssessmentDetail,
            linkedArticle17_5EvidenceAttachmentId: carrierLiabilityArticle17_5EvidenceAttachmentId.trim(),
          } : {}),
        } : {}),
        ...(carrierLiabilitySpecialRiskClaimed ? {
          specialRiskClaimed: true,
          specialRiskType: carrierLiabilitySpecialRiskType,
          article18_2SpecialRiskCircumstancesEstablished: carrierLiabilityArticle18_2CircumstancesEstablished,
          ...(carrierLiabilityArticle18_2CircumstancesEstablished ? {
            article18_2CircumstancesAssessmentDetail: carrierLiabilityArticle18_2AssessmentDetail,
            linkedArticle18_2EvidenceAttachmentId: carrierLiabilityArticle18_2EvidenceAttachmentId.trim(),
          } : {}),
          ...(carrierLiabilitySpecialRiskPresumption ? { specialRiskPresumptionClaimed: true } : {}),
          ...(isOpenUnsheetedVehicleRisk ? {
            article17_4aOpenUnsheetedVehicleUsed: carrierLiabilityArticle17_4aOpenVehicleUsed,
            article17_4aAssessmentDetail: carrierLiabilityArticle17_4aAssessmentDetail,
            linkedArticle17_4aEvidenceAttachmentId: carrierLiabilityArticle17_4aEvidenceAttachmentId.trim(),
          } : {}),
          ...(isArticle17_4bPackingRisk ? {
            article17_4bPackingAbsentOrDefectiveObserved: carrierLiabilityArticle17_4bPackingAbsentOrDefective,
            article17_4bGoodsLiableWithoutProperPacking: carrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking,
            article17_4bAssessmentDetail: carrierLiabilityArticle17_4bAssessmentDetail,
            linkedArticle17_4bEvidenceAttachmentId: carrierLiabilityArticle17_4bEvidenceAttachmentId.trim(),
          } : {}),
          ...(isArticle17_4cOperationsRisk ? {
            article17_4cHandlingPerformed: carrierLiabilityArticle17_4cHandlingPerformed,
            article17_4cLoadingPerformed: carrierLiabilityArticle17_4cLoadingPerformed,
            article17_4cStowagePerformed: carrierLiabilityArticle17_4cStowagePerformed,
            article17_4cUnloadingPerformed: carrierLiabilityArticle17_4cUnloadingPerformed,
            article17_4cActorCapacity: carrierLiabilityArticle17_4cActorCapacity,
            article17_4cAssessmentDetail: carrierLiabilityArticle17_4cAssessmentDetail,
            linkedArticle17_4cEvidenceAttachmentId: carrierLiabilityArticle17_4cEvidenceAttachmentId.trim(),
          } : {}),
          ...(isArticle17_4dGoodsNatureRisk ? {
            article17_4dGoodsParticularlyExposedByNature: carrierLiabilityArticle17_4dGoodsParticularlyExposed,
            article17_4dSusceptibilityBasis: carrierLiabilityArticle17_4dSusceptibilityBasis,
            article17_4dAssessmentDetail: carrierLiabilityArticle17_4dAssessmentDetail,
            linkedArticle17_4dEvidenceAttachmentId: carrierLiabilityArticle17_4dEvidenceAttachmentId.trim(),
          } : {}),
          ...(isArticle17_4eMarkingRisk ? {
            article17_4ePackageMarksInsufficientOrInadequate: carrierLiabilityArticle17_4ePackageMarksDeficient,
            article17_4ePackageNumbersInsufficientOrInadequate: carrierLiabilityArticle17_4ePackageNumbersDeficient,
            article17_4eDeficiencyBasis: carrierLiabilityArticle17_4eDeficiencyBasis,
            article17_4eAssessmentDetail: carrierLiabilityArticle17_4eAssessmentDetail,
            linkedArticle17_4eEvidenceAttachmentId: carrierLiabilityArticle17_4eEvidenceAttachmentId.trim(),
          } : {}),
          ...(isArticle17_4fLivestockRisk ? {
            article17_4fLivestockCarriageConfirmed: carrierLiabilityArticle17_4fLivestockCarriageConfirmed,
            article17_4fAssessmentDetail: carrierLiabilityArticle17_4fAssessmentDetail,
            linkedArticle17_4fEvidenceAttachmentId: carrierLiabilityArticle17_4fEvidenceAttachmentId.trim(),
          } : {}),
          ...(carrierLiabilityTemperatureControlled ? { temperatureControlledVehicle: true } : {}),
          ...(carrierLiabilityEquipmentStepsEvidence ? { equipmentStepsEvidencePresent: true } : {}),
          ...(carrierLiabilityLivestockCareEvidence ? { livestockCareStepsEvidencePresent: true } : {}),
          ...(carrierLiabilityTemperatureControlled ? {
            article18_4EquipmentChoiceStepsTaken: carrierLiabilityArticle18_4ChoiceSteps,
            article18_4EquipmentMaintenanceStepsTaken: carrierLiabilityArticle18_4MaintenanceSteps,
            article18_4EquipmentUseStepsTaken: carrierLiabilityArticle18_4UseSteps,
            article18_4SpecialInstructionsIssued: carrierLiabilityArticle18_4InstructionsIssued,
            article18_4SpecialInstructionsComplied: carrierLiabilityArticle18_4InstructionsComplied,
            article18_4AssessmentDetail: carrierLiabilityArticle18_4AssessmentDetail,
            linkedArticle18_4EvidenceAttachmentId: carrierLiabilityArticle18_4EvidenceAttachmentId.trim(),
          } : {}),
          ...(carrierLiabilitySpecialRiskType === 'livestock' ? {
            article18_5NormallyIncumbentStepsTaken: carrierLiabilityArticle18_5NormallyIncumbentSteps,
            article18_5SpecialInstructionsIssued: carrierLiabilityArticle18_5InstructionsIssued,
            article18_5SpecialInstructionsComplied: carrierLiabilityArticle18_5InstructionsComplied,
            article18_5AssessmentDetail: carrierLiabilityArticle18_5AssessmentDetail,
            linkedArticle18_5EvidenceAttachmentId: carrierLiabilityArticle18_5EvidenceAttachmentId.trim(),
          } : {}),
          ...(isOpenUnsheetedVehicleRisk ? {
            abnormalShortageObserved: carrierLiabilityAbnormalShortageObserved,
            anyPackageLost: carrierLiabilityAnyPackageLost,
          } : {}),
        } : {}),
      }),
      ...(carrierLiabilityIncidentDetail ? { incidentDetail: carrierLiabilityIncidentDetail } : {}),
      ...(carrierLiabilityClaimantStatementDetail ? { claimantStatementDetail: carrierLiabilityClaimantStatementDetail } : {}),
      ...(carrierLiabilityCarrierProofDetail ? { carrierProofDetail: carrierLiabilityCarrierProofDetail } : {}),
      ...(article17_3FactorReported ? {
        article17_3AssessmentDetail: carrierLiabilityArticle17_3AssessmentDetail,
      } : {}),
    };

    try {
      await recordCarrierLiabilityIncidentInAPI(documentId, payload);
      setMessage('Carrier liability incident recorded without exposing private details');
      setCarrierLiabilityIncidentType('damage');
      setCarrierLiabilityTakingOverAt('');
      setCarrierLiabilityIncidentAt('');
      setCarrierLiabilityDeliveryAt('');
      setCarrierLiabilityIncidentEvidenceAttachmentId('');
      setCarrierLiabilityCarrierProofEvidenceAttachmentId('');
      setCarrierLiabilityLossObserved(false);
      setCarrierLiabilityDamageObserved(true);
      setCarrierLiabilityDelayObserved(false);
      setCarrierLiabilityClaimantFault(false);
      setCarrierLiabilityClaimantInstruction(false);
      setCarrierLiabilityInherentVice(false);
      setCarrierLiabilityUnavoidableCircumstances(false);
      setCarrierLiabilitySpecialRiskClaimed(false);
      setCarrierLiabilitySpecialRiskType('defective_or_missing_packing');
      setCarrierLiabilitySpecialRiskPresumption(false);
      setCarrierLiabilityArticle18_2CircumstancesEstablished(false);
      setCarrierLiabilityArticle18_2AssessmentDetail('');
      setCarrierLiabilityArticle18_2EvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4aOpenVehicleUsed(false);
      setCarrierLiabilityArticle17_4aAssessmentDetail('');
      setCarrierLiabilityArticle17_4aEvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4bPackingAbsentOrDefective(false);
      setCarrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking(false);
      setCarrierLiabilityArticle17_4bAssessmentDetail('');
      setCarrierLiabilityArticle17_4bEvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4cHandlingPerformed(false);
      setCarrierLiabilityArticle17_4cLoadingPerformed(false);
      setCarrierLiabilityArticle17_4cStowagePerformed(false);
      setCarrierLiabilityArticle17_4cUnloadingPerformed(false);
      setCarrierLiabilityArticle17_4cActorCapacity('consignor');
      setCarrierLiabilityArticle17_4cAssessmentDetail('');
      setCarrierLiabilityArticle17_4cEvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(false);
      setCarrierLiabilityArticle17_4dSusceptibilityBasis('breakage');
      setCarrierLiabilityArticle17_4dAssessmentDetail('');
      setCarrierLiabilityArticle17_4dEvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4ePackageMarksDeficient(false);
      setCarrierLiabilityArticle17_4ePackageNumbersDeficient(false);
      setCarrierLiabilityArticle17_4eDeficiencyBasis('insufficiency');
      setCarrierLiabilityArticle17_4eAssessmentDetail('');
      setCarrierLiabilityArticle17_4eEvidenceAttachmentId('');
      setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(false);
      setCarrierLiabilityArticle17_4fAssessmentDetail('');
      setCarrierLiabilityArticle17_4fEvidenceAttachmentId('');
      setCarrierLiabilityTemperatureControlled(false);
      setCarrierLiabilityEquipmentStepsEvidence(false);
      setCarrierLiabilityLivestockCareEvidence(false);
      setCarrierLiabilityArticle18_4ChoiceSteps(false);
      setCarrierLiabilityArticle18_4MaintenanceSteps(false);
      setCarrierLiabilityArticle18_4UseSteps(false);
      setCarrierLiabilityArticle18_4InstructionsIssued(false);
      setCarrierLiabilityArticle18_4InstructionsComplied(false);
      setCarrierLiabilityArticle18_4AssessmentDetail('');
      setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
      setCarrierLiabilityArticle18_5NormallyIncumbentSteps(false);
      setCarrierLiabilityArticle18_5InstructionsIssued(false);
      setCarrierLiabilityArticle18_5InstructionsComplied(false);
      setCarrierLiabilityArticle18_5AssessmentDetail('');
      setCarrierLiabilityArticle18_5EvidenceAttachmentId('');
      setCarrierLiabilityAbnormalShortageObserved(false);
      setCarrierLiabilityAnyPackageLost(false);
      setCarrierLiabilityVehicleDefectContributed(false);
      setCarrierLiabilityHiredVehicleProviderContribution(false);
      setCarrierLiabilityArticle17_3AssessmentDetail('');
      setCarrierLiabilityArticle17_3EvidenceAttachmentId('');
      setCarrierLiabilityArticle18_1CauseProofEstablished(false);
      setCarrierLiabilityArticle18_1ProofAssessmentDetail('');
      setCarrierLiabilityArticle18_1EvidenceAttachmentId('');
      setCarrierLiabilityArticle17_2CauseContributed(false);
      setCarrierLiabilityArticle17_5CarrierShare('');
      setCarrierLiabilityArticle17_5ReliefShare('');
      setCarrierLiabilityArticle17_5AssessmentDetail('');
      setCarrierLiabilityArticle17_5EvidenceAttachmentId('');
      setCarrierLiabilityDisposalInstructionId('');
      setCarrierLiabilityInstructionNotCarriedOut(false);
      setCarrierLiabilityInstructionWithoutFirstCopy(false);
      setCarrierLiabilityClaimantEntitlement(false);
      setCarrierLiabilityLossOrDamageCaused(false);
      setCarrierLiabilityIncidentDetail('');
      setCarrierLiabilityClaimantStatementDetail('');
      setCarrierLiabilityCarrierProofDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleArticle18_2ClaimantRebuttal = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const incidentId = article18_2RebuttalIncidentId.trim();
    const counterEvidenceDetail = article18_2RebuttalCounterEvidenceDetail.trim();
    const attachmentId = article18_2RebuttalEvidenceAttachmentId.trim();
    if (!incidentId || !counterEvidenceDetail || !attachmentId) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }
    const payload: Article18_2ClaimantRebuttalRequest = {
      lossOrDamageAttributionDisputed: true,
      claimantCounterEvidenceSubmitted: true,
      counterEvidenceDetail,
      linkedCounterEvidenceAttachmentId: attachmentId,
    };
    try {
      await recordArticle18_2ClaimantRebuttalInAPI(documentId, incidentId, payload);
      setMessage('Article 18.2 claimant counter-evidence recorded without exposing private details');
      setArticle18_2RebuttalIncidentId('');
      setArticle18_2RebuttalCounterEvidenceDetail('');
      setArticle18_2RebuttalEvidenceAttachmentId('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDeliveryTimingAssessment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const isArticle19Delay = deliveryTimingAssessmentType === 'article19_delay';
    const isArticle20LossPresumption = deliveryTimingAssessmentType === 'article20_loss_presumption';
    const isArticle20RecoveryNotice = deliveryTimingAssessmentType === 'article20_recovery_notice';
    const diligentCarrierAllowedDurationMinutes = Number(deliveryTimingDiligentCarrierAllowedDurationMinutes);
    const partialLoadAssemblyDurationMinutes = Number(deliveryTimingPartialLoadAssemblyDurationMinutes);
    const validDiligentCarrierDuration = Number.isSafeInteger(diligentCarrierAllowedDurationMinutes)
      && diligentCarrierAllowedDurationMinutes > 0;
    const validPartialLoadDuration = Number.isSafeInteger(partialLoadAssemblyDurationMinutes)
      && partialLoadAssemblyDurationMinutes > 0;

    if (
      (isArticle19Delay && (
        deliveryTimingAgreedLimitPresent
          ? !deliveryTimingAgreedLimitExpired
          : (
              !deliveryTimingTakingOverAt ||
              (!deliveryTimingActualDeliveryAt && !(deliveryTimingGoodsNotDelivered && deliveryTimingAssessmentAt)) ||
              Boolean(deliveryTimingActualDeliveryAt && deliveryTimingGoodsNotDelivered) ||
              !validDiligentCarrierDuration ||
              (deliveryTimingPartialLoadConsidered && !validPartialLoadDuration)
            )
      )) ||
      (isArticle20LossPresumption && (
        !deliveryTimingGoodsNotDelivered ||
        !deliveryTimingAssessmentAt ||
        (deliveryTimingAgreedLimitPresent
          ? !deliveryTimingThirtyDaysExpired
          : (!deliveryTimingSixtyDaysExpired || !deliveryTimingTakingOverAt))
      )) ||
      (isArticle20RecoveryNotice && (
        !deliveryTimingRecoveryRequested ||
        !deliveryTimingWrittenAcknowledgement ||
        (deliveryTimingGoodsRecovered && !deliveryTimingRecoveryNotificationSent) ||
        (deliveryTimingRecoveryNotificationSent && !deliveryTimingGoodsRecovered) ||
        (deliveryTimingRecoveryNotificationSent && !deliveryTimingRecoveryNotificationAt) ||
        (Boolean(deliveryTimingRecoveryNotificationAt) && !deliveryTimingRecoveryNotificationSent) ||
        (deliveryTimingDeliveryRequested && (
          !deliveryTimingGoodsRecovered ||
          !deliveryTimingRecoveryNotificationSent ||
          !deliveryTimingRecoveryNotificationReceivedAt ||
          !deliveryTimingDeliveryRequestAt ||
          !deliveryTimingChargesRefundAcknowledged
        )) ||
        (deliveryTimingChargesRefundAcknowledged && !deliveryTimingDeliveryRequested)
      ))
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: DeliveryTimingAssessmentRequest = {
      assessmentType: deliveryTimingAssessmentType,
    };

    if (isArticle19Delay) {
      Object.assign(payload, {
        ...(deliveryTimingAgreedLimitPresent ? {
          agreedTimeLimitPresent: true,
          agreedTimeLimitExpired: true,
        } : {
          takingOverAt: deliveryTimingTakingOverAt,
          diligentCarrierAllowedDurationMinutes,
          ...(deliveryTimingGoodsNotDelivered ? { goodsNotDelivered: true } : {}),
          ...(deliveryTimingPartialLoadConsidered ? {
            partialLoadAssemblyTimeConsidered: true,
            partialLoadAssemblyDurationMinutes,
          } : {}),
        }),
        ...(deliveryTimingActualDeliveryAt ? { actualDeliveryAt: deliveryTimingActualDeliveryAt } : {}),
        ...(deliveryTimingAssessmentAt ? { assessmentAt: deliveryTimingAssessmentAt } : {}),
        ...(deliveryTimingDetail ? { timingDetail: deliveryTimingDetail } : {}),
        ...(deliveryTimingClaimDetail ? { claimDetail: deliveryTimingClaimDetail } : {}),
      });
    }

    if (isArticle20LossPresumption) {
      Object.assign(payload, {
        goodsNotDelivered: true,
        ...(deliveryTimingAgreedLimitPresent ? {
          agreedTimeLimitPresent: true,
          thirtyDaysAfterAgreedLimitExpired: true,
        } : {
          takingOverAt: deliveryTimingTakingOverAt,
          sixtyDaysAfterTakeoverExpired: true,
        }),
        assessmentAt: deliveryTimingAssessmentAt,
        ...(deliveryTimingDetail ? { timingDetail: deliveryTimingDetail } : {}),
        ...(deliveryTimingClaimDetail ? { claimDetail: deliveryTimingClaimDetail } : {}),
      });
    }

    if (isArticle20RecoveryNotice) {
      Object.assign(payload, {
        recoveryNotificationRequested: true,
        writtenAcknowledgementIssued: true,
        ...(deliveryTimingGoodsRecovered ? { goodsRecovered: true } : {}),
        ...(deliveryTimingRecoveryNotificationSent ? { recoveryNotificationSent: true } : {}),
        ...(deliveryTimingRecoveryNotificationAt ? { recoveryNotificationAt: deliveryTimingRecoveryNotificationAt } : {}),
        ...(deliveryTimingDeliveryRequested ? {
          deliveryRequested: true,
          recoveryNotificationReceivedAt: deliveryTimingRecoveryNotificationReceivedAt,
          deliveryRequestAt: deliveryTimingDeliveryRequestAt,
          chargesAndRefundConditionAcknowledged: true,
        } : {}),
        ...(deliveryTimingRecoveryDetail ? { recoveryDetail: deliveryTimingRecoveryDetail } : {}),
      });
    }

    try {
      await recordDeliveryTimingAssessmentInAPI(documentId, payload);
      setMessage('Delivery timing assessment recorded without exposing private Articles 19-20 dates or details');
      setDeliveryTimingAssessmentType('article20_loss_presumption');
      setDeliveryTimingTakingOverAt('');
      setDeliveryTimingActualDeliveryAt('');
      setDeliveryTimingAssessmentAt('');
      setDeliveryTimingRecoveryNotificationAt('');
      setDeliveryTimingRecoveryNotificationReceivedAt('');
      setDeliveryTimingDeliveryRequestAt('');
      setDeliveryTimingGoodsNotDelivered(true);
      setDeliveryTimingAgreedLimitPresent(false);
      setDeliveryTimingAgreedLimitExpired(false);
      setDeliveryTimingDiligentCarrierAllowedDurationMinutes('');
      setDeliveryTimingPartialLoadConsidered(false);
      setDeliveryTimingPartialLoadAssemblyDurationMinutes('');
      setDeliveryTimingThirtyDaysExpired(false);
      setDeliveryTimingSixtyDaysExpired(true);
      setDeliveryTimingRecoveryRequested(false);
      setDeliveryTimingWrittenAcknowledgement(false);
      setDeliveryTimingGoodsRecovered(false);
      setDeliveryTimingRecoveryNotificationSent(false);
      setDeliveryTimingDeliveryRequested(false);
      setDeliveryTimingChargesRefundAcknowledged(false);
      setDeliveryTimingDetail('');
      setDeliveryTimingClaimDetail('');
      setDeliveryTimingRecoveryDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCompensationPayment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const paymentAmount = Number(compensationPaymentAmount);
    const paymentCurrency = compensationPaymentCurrency.trim().toUpperCase();
    if (
      !compensationPaymentPaidAt ||
      !Number.isFinite(paymentAmount) ||
      paymentAmount <= 0 ||
      !/^[A-Z]{3}$/.test(paymentCurrency) ||
      !compensationPaymentAttachmentId.trim() ||
      !compensationPaymentForMissingGoods ||
      !compensationPaymentSettled ||
      !compensationPaymentRecipientAcknowledged
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: CompensationPaymentRequest = {
      paymentType: 'missing_goods_compensation_payment',
      paymentEvidenceType: compensationPaymentEvidenceType,
      paidAt: compensationPaymentPaidAt,
      paymentAmount: compensationPaymentAmount,
      paymentCurrency,
      compensationForMissingGoods: true,
      paymentSettled: true,
      recipientAcknowledgedReceipt: true,
      linkedPaymentEvidenceAttachmentId: compensationPaymentAttachmentId.trim(),
      ...(compensationPaymentDetail ? { paymentDetail: compensationPaymentDetail } : {}),
    };

    try {
      await recordCompensationPaymentInAPI(documentId, payload);
      setMessage('Compensation payment evidence recorded without exposing private Article 20.2 payment data');
      setCompensationPaymentEvidenceType('bank_transfer_confirmation');
      setCompensationPaymentPaidAt('');
      setCompensationPaymentAmount('');
      setCompensationPaymentCurrency('EUR');
      setCompensationPaymentAttachmentId('');
      setCompensationPaymentForMissingGoods(true);
      setCompensationPaymentSettled(true);
      setCompensationPaymentRecipientAcknowledged(true);
      setCompensationPaymentDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleRecoveryDeliverySettlement = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const chargeLines = recoverySettlementChargeLines.map((line) => ({
      amount: line.amount.trim(),
      currency: line.currency.trim().toUpperCase(),
    }));
    const chargeCurrencies = new Set(chargeLines.map((line) => line.currency));
    const refundAmount = Number(recoverySettlementRefundAmount);
    const includedCharges = Number(recoverySettlementIncludedCharges);
    const refundCurrency = recoverySettlementRefundCurrency.trim().toUpperCase();
    if (
      chargeLines.length === 0 ||
      chargeLines.some((line) => {
        const amount = Number(line.amount);
        return !line.amount || !Number.isFinite(amount) || amount <= 0 || !/^[A-Z]{3}$/.test(line.currency);
      }) ||
      chargeCurrencies.size !== chargeLines.length ||
      !recoverySettlementChargesPaidAt ||
      !recoverySettlementRefundAmount.trim() ||
      !Number.isFinite(refundAmount) ||
      refundAmount < 0 ||
      !/^[A-Z]{3}$/.test(refundCurrency) ||
      !recoverySettlementIncludedCharges.trim() ||
      !Number.isFinite(includedCharges) ||
      includedCharges < 0 ||
      !recoverySettlementRefundedAt ||
      !recoverySettlementChargesAttachmentId ||
      !recoverySettlementRefundAttachmentId ||
      !recoverySettlementChargesSettled ||
      !recoverySettlementRefundSettled ||
      !recoverySettlementDelayRightsPreserved
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: RecoveryDeliverySettlementRequest = {
      settlementType: 'article20_recovery_delivery_settlement',
      chargesPaidAmounts: chargeLines,
      chargesPaidAt: recoverySettlementChargesPaidAt,
      compensationRefundAmount: recoverySettlementRefundAmount,
      compensationRefundCurrency: refundCurrency,
      chargesIncludedInCompensationAmount: recoverySettlementIncludedCharges,
      compensationRefundedAt: recoverySettlementRefundedAt,
      chargesPaymentSettled: true,
      compensationRefundSettled: true,
      delayClaimsRightsPreserved: true,
      linkedChargesPaymentEvidenceAttachmentId: recoverySettlementChargesAttachmentId,
      linkedCompensationRefundEvidenceAttachmentId: recoverySettlementRefundAttachmentId,
      ...(recoverySettlementDetail ? { settlementDetail: recoverySettlementDetail } : {}),
    };

    try {
      await recordRecoveryDeliverySettlementInAPI(documentId, payload);
      setMessage('Recovery delivery settlement recorded without exposing private Article 20.3 financial data');
      setRecoverySettlementChargeLines([{ amount: '', currency: 'EUR' }]);
      setRecoverySettlementChargesPaidAt('');
      setRecoverySettlementRefundAmount('');
      setRecoverySettlementRefundCurrency('EUR');
      setRecoverySettlementIncludedCharges('0');
      setRecoverySettlementRefundedAt('');
      setRecoverySettlementChargesAttachmentId('');
      setRecoverySettlementRefundAttachmentId('');
      setRecoverySettlementChargesSettled(true);
      setRecoverySettlementRefundSettled(true);
      setRecoverySettlementDelayRightsPreserved(true);
      setRecoverySettlementDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleRecoveredGoodsDisposition = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const countryCode = recoveredGoodsLocationCountryCode.trim().toUpperCase();
    const requiresWrittenRequestAbsence = recoveredGoodsDispositionBasis === 'no_written_recovery_notification_request';
    const requiresInstructionPeriod = recoveredGoodsDispositionBasis === 'no_instructions_after_thirty_days';
    if (
      !recoveredGoodsRecoveredAt ||
      !recoveredGoodsDispositionAt ||
      !/^[A-Z]{2}$/.test(countryCode) ||
      !recoveredGoodsLocationDetail.trim() ||
      !recoveredGoodsApplicableLawDetail.trim() ||
      !recoveredGoodsDispositionDetail.trim() ||
      !recoveredGoodsRecoveryAttachmentId ||
      !recoveredGoodsDispositionAttachmentId ||
      !recoveredGoodsRecovered ||
      !recoveredGoodsLocalLawIdentified ||
      !recoveredGoodsDispositionUnderLaw ||
      (requiresWrittenRequestAbsence && !recoveredGoodsWrittenRequestAbsent) ||
      (requiresInstructionPeriod && (
        !recoveredGoodsNotificationReceivedAt ||
        !recoveredGoodsInstructionsAssessedAt ||
        !recoveredGoodsInstructionsAbsent
      ))
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: RecoveredGoodsDispositionRequest = {
      recordType: 'article20_recovered_goods_disposition',
      dispositionBasis: recoveredGoodsDispositionBasis,
      dispositionActionType: recoveredGoodsDispositionAction,
      goodsRecovered: true,
      goodsRecoveredAt: recoveredGoodsRecoveredAt,
      dispositionAt: recoveredGoodsDispositionAt,
      ...(requiresWrittenRequestAbsence ? {
        writtenRecoveryNotificationRequestAbsent: true,
      } : {}),
      ...(requiresInstructionPeriod ? {
        recoveryNotificationReceivedAt: recoveredGoodsNotificationReceivedAt,
        instructionsAbsent: true,
        instructionsAbsenceAssessedAt: recoveredGoodsInstructionsAssessedAt,
      } : {}),
      goodsLocationCountryCode: countryCode,
      goodsLocationDetail: recoveredGoodsLocationDetail.trim(),
      applicableLocalLawIdentified: true,
      applicableLawDetail: recoveredGoodsApplicableLawDetail.trim(),
      dispositionUnderLocalLawRecorded: true,
      dispositionDetail: recoveredGoodsDispositionDetail.trim(),
      linkedRecoveryEvidenceAttachmentId: recoveredGoodsRecoveryAttachmentId,
      linkedDispositionEvidenceAttachmentId: recoveredGoodsDispositionAttachmentId,
    };

    try {
      await recordRecoveredGoodsDispositionInAPI(documentId, payload);
      setMessage('Recovered goods disposition recorded without exposing private Article 20.4 data');
      setRecoveredGoodsDispositionBasis('no_written_recovery_notification_request');
      setRecoveredGoodsDispositionAction('stored');
      setRecoveredGoodsRecoveredAt('');
      setRecoveredGoodsDispositionAt('');
      setRecoveredGoodsNotificationReceivedAt('');
      setRecoveredGoodsInstructionsAssessedAt('');
      setRecoveredGoodsLocationCountryCode('');
      setRecoveredGoodsLocationDetail('');
      setRecoveredGoodsApplicableLawDetail('');
      setRecoveredGoodsDispositionDetail('');
      setRecoveredGoodsRecoveryAttachmentId('');
      setRecoveredGoodsDispositionAttachmentId('');
      setRecoveredGoodsRecovered(true);
      setRecoveredGoodsWrittenRequestAbsent(true);
      setRecoveredGoodsInstructionsAbsent(true);
      setRecoveredGoodsLocalLawIdentified(true);
      setRecoveredGoodsDispositionUnderLaw(true);
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCodDeliveryOmission = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      await recordCodDeliveryOmissionInAPI(documentId, {
        omissionType: codOmissionType,
        deliveryAt: codDeliveryAt,
        goodsDeliveredToConsignee: codGoodsDeliveredToConsignee,
        codChargeUncollected,
        consignorCompensationClaimed: codConsignorCompensationClaimed,
        carrierActionAgainstConsigneeReserved: codCarrierActionReserved,
        ...(codOmissionDetail ? { omissionDetail: codOmissionDetail } : {}),
        ...(codCompensationClaimDetail ? { compensationClaimDetail: codCompensationClaimDetail } : {}),
        ...(codConsigneeActionDetail ? { consigneeActionDetail: codConsigneeActionDetail } : {}),
      });
      setMessage('COD delivery omission recorded without exposing private Article 21 amount or details');
      setCodOmissionType('cod_uncollected_delivery');
      setCodDeliveryAt('');
      setCodGoodsDeliveredToConsignee(true);
      setCodChargeUncollected(true);
      setCodConsignorCompensationClaimed(false);
      setCodCarrierActionReserved(false);
      setCodOmissionDetail('');
      setCodCompensationClaimDetail('');
      setCodConsigneeActionDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDangerousGoodsNotice = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const isCarrierAction = dangerNoticeType === 'carrier_unaware_dangerous_goods_action';
    const noticePayload: DangerousGoodsNoticeRequest = isCarrierAction
      ? {
          noticeType: dangerNoticeType,
          actionAt: dangerActionAt,
          dangerousGoodsHandedToCarrier,
          carrierDidNotKnowDangerousNature: dangerCarrierDidNotKnow,
          carrierActionType: dangerCarrierActionType,
          carrierActionTaken: dangerCarrierActionTaken,
          withoutCompensationBasisRecorded: dangerWithoutCompensationBasisRecorded,
          ...(dangerSenderExpenseLiabilityClaimed ? { senderExpenseLiabilityClaimed: true } : {}),
          ...(dangerCarrierActionDetail ? { carrierActionDetail: dangerCarrierActionDetail } : {}),
          ...(dangerSenderExpenseLiabilityClaimed && dangerExpenseClaimDetail ? { expenseClaimDetail: dangerExpenseClaimDetail } : {}),
        }
      : {
          noticeType: dangerNoticeType,
          noticeAt: dangerNoticeAt,
          dangerousGoodsHandedToCarrier,
          precautionsRequired: dangerPrecautionsRequired,
          carrierKnowledgeProvedByOtherMeans: dangerCarrierKnowledgeProved,
          ...(dangerSenderExpenseLiabilityClaimed ? { senderExpenseLiabilityClaimed: true } : {}),
          ...(dangerCarrierKnowledgeProved && dangerNatureDetail ? { dangerNatureDetail } : {}),
          ...(dangerCarrierKnowledgeProved && dangerPrecautionsRequired && dangerPrecautionsDetail
            ? { precautionsDetail: dangerPrecautionsDetail }
            : {}),
          ...(dangerCarrierKnowledgeProved && dangerCarrierKnowledgeProofDetail
            ? { carrierKnowledgeProofDetail: dangerCarrierKnowledgeProofDetail }
            : {}),
          ...(dangerCarrierKnowledgeProved && dangerCarrierKnowledgeProofAttachmentId.trim()
            ? { linkedCarrierKnowledgeProofAttachmentId: dangerCarrierKnowledgeProofAttachmentId.trim() }
            : {}),
          ...(dangerSenderExpenseLiabilityClaimed && dangerExpenseClaimDetail ? { expenseClaimDetail: dangerExpenseClaimDetail } : {}),
        };
    try {
      await recordDangerousGoodsNoticeInAPI(documentId, noticePayload);
      setMessage('Dangerous goods notice recorded without exposing private Article 22 details');
      setDangerNoticeType('danger_information_notice');
      setDangerNoticeAt('');
      setDangerActionAt('');
      setDangerousGoodsHandedToCarrier(true);
      setDangerPrecautionsRequired(false);
      setDangerCarrierKnowledgeProved(false);
      setDangerCarrierKnowledgeProofAttachmentId('');
      setDangerCarrierDidNotKnow(false);
      setDangerCarrierActionType('unloaded');
      setDangerCarrierActionTaken(false);
      setDangerWithoutCompensationBasisRecorded(false);
      setDangerSenderExpenseLiabilityClaimed(false);
      setDangerNatureDetail('');
      setDangerPrecautionsDetail('');
      setDangerCarrierKnowledgeProofDetail('');
      setDangerCarrierActionDetail('');
      setDangerExpenseClaimDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCompensationAssessment = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const compensationAssessmentPayload: CompensationAssessmentRequest = {
      assessmentType: compensationAssessmentType,
      assessmentAt: compensationAssessmentAt,
      linkedIncidentId: compensationLinkedIncidentId.trim(),
      ...(compensationCalculationDetail ? { calculationDetail: compensationCalculationDetail } : {}),
      ...(compensationClaimDetail ? { claimDetail: compensationClaimDetail } : {}),
    };
    if (
      compensationAssessmentType === 'loss_compensation_limit'
      || compensationAssessmentType === 'damage_depreciation_compensation_limit'
      || compensationAssessmentType === 'delay_compensation_limit'
    ) {
      compensationAssessmentPayload.carrierLiabilityBasisRecorded = compensationCarrierLiabilityBasisRecorded;
    }
    if (
      compensationAssessmentType === 'loss_compensation_limit'
      || compensationAssessmentType === 'damage_depreciation_compensation_limit'
    ) {
      compensationAssessmentPayload.goodsValueBasis = compensationGoodsValueBasis;
      compensationAssessmentPayload.liabilityLimitBasis = compensationLiabilityLimitBasis;
      if (compensationGoodsValueAmount) {
        compensationAssessmentPayload.goodsValueAmount = compensationGoodsValueAmount;
        compensationAssessmentPayload.goodsValueCurrency = compensationGoodsValueCurrency;
      }
      if (compensationValuationDetail) {
        compensationAssessmentPayload.valuationDetail = compensationValuationDetail;
      }
      if (compensationValuationEvidenceAttachmentId.trim()) {
        compensationAssessmentPayload.linkedValuationEvidenceAttachmentId = compensationValuationEvidenceAttachmentId.trim();
      }
    }
    if (
      compensationAssessmentType === 'loss_compensation_limit'
      || compensationAssessmentType === 'damage_depreciation_compensation_limit'
      || compensationAssessmentType === 'delay_compensation_limit'
      || compensationAssessmentType === 'special_interest_additional_compensation'
    ) {
      if (compensationClaimAmount) {
        compensationAssessmentPayload.claimAmount = compensationClaimAmount;
        compensationAssessmentPayload.claimCurrency = compensationClaimCurrency;
      }
    }
    if (compensationAssessmentType === 'loss_compensation_limit') {
      if (compensationGrossWeightShortKg) {
        compensationAssessmentPayload.grossWeightShortKg = compensationGrossWeightShortKg;
      }
      if (compensationCarriageChargesAmount) {
        compensationAssessmentPayload.carriageChargesAmount = compensationCarriageChargesAmount;
        compensationAssessmentPayload.carriageChargesCurrency = compensationCarriageChargesCurrency;
      }
      compensationAssessmentPayload.article23LimitApplied = compensationArticle23LimitApplied;
      compensationAssessmentPayload.carriageChargesRefundConsidered = compensationCarriageChargesRefundConsidered;
    } else if (compensationAssessmentType === 'damage_depreciation_compensation_limit') {
      if (compensationDiminishedValueAmount) {
        compensationAssessmentPayload.diminishedValueAmount = compensationDiminishedValueAmount;
        compensationAssessmentPayload.diminishedValueCurrency = compensationDiminishedValueCurrency;
      }
      compensationAssessmentPayload.damageAffectedWholeConsignment = compensationDamageAffectedWholeConsignment;
      compensationAssessmentPayload.article25LossLimitApplied = compensationArticle25LossLimitApplied;
    } else if (compensationAssessmentType === 'delay_compensation_limit') {
      if (compensationCarriageChargesAmount) {
        compensationAssessmentPayload.carriageChargesAmount = compensationCarriageChargesAmount;
        compensationAssessmentPayload.carriageChargesCurrency = compensationCarriageChargesCurrency;
      }
      compensationAssessmentPayload.delayDamageProved = compensationDelayDamageProved;
      compensationAssessmentPayload.carriageChargesLimitApplied = compensationCarriageChargesLimitApplied;
      compensationAssessmentPayload.agreedTimeLimitExceeded = compensationAgreedTimeLimitExceeded;
    } else if (compensationAssessmentType === 'declared_value_substitution') {
      if (compensationDeclaredValueAmount) {
        compensationAssessmentPayload.declaredValueAmount = compensationDeclaredValueAmount;
        compensationAssessmentPayload.declaredValueCurrency = compensationDeclaredValueCurrency;
      }
      if (compensationValuationDetail) {
        compensationAssessmentPayload.valuationDetail = compensationValuationDetail;
      }
      compensationAssessmentPayload.declaredValueEnteredInConsignmentNote = compensationDeclaredValueEntered;
      compensationAssessmentPayload.declaredValueSurchargeAgreed = compensationDeclaredValueSurchargeAgreed;
      compensationAssessmentPayload.declaredValueExceedsArticle23Limit = compensationDeclaredValueExceedsArticle23Limit;
      compensationAssessmentPayload.declaredValueLimitSubstituted = compensationDeclaredValueLimitSubstituted;
    } else if (compensationAssessmentType === 'special_interest_additional_compensation') {
      if (compensationSpecialInterestAmount) {
        compensationAssessmentPayload.specialInterestAmount = compensationSpecialInterestAmount;
        compensationAssessmentPayload.specialInterestCurrency = compensationSpecialInterestCurrency;
      }
      if (compensationValuationDetail) {
        compensationAssessmentPayload.valuationDetail = compensationValuationDetail;
      }
      compensationAssessmentPayload.specialInterestEnteredInConsignmentNote = compensationSpecialInterestEntered;
      compensationAssessmentPayload.specialInterestSurchargeAgreed = compensationSpecialInterestSurchargeAgreed;
      compensationAssessmentPayload.additionalLossOrDamageProved = compensationAdditionalLossOrDamageProved;
      compensationAssessmentPayload.specialInterestLimitApplied = compensationSpecialInterestLimitApplied;
    }
    try {
      await recordCompensationAssessmentInAPI(documentId, compensationAssessmentPayload);
      setMessage('Compensation assessment recorded without exposing private Articles 23-26 amounts or details');
      setCompensationAssessmentType('loss_compensation_limit');
      setCompensationAssessmentAt('');
      setCompensationLinkedIncidentId('');
      setCompensationGoodsValueBasis('current_market_price');
      setCompensationLiabilityLimitBasis('sdr_8_33_per_kg');
      setCompensationGoodsValueAmount('');
      setCompensationGoodsValueCurrency('EUR');
      setCompensationGrossWeightShortKg('');
      setCompensationClaimAmount('');
      setCompensationClaimCurrency('EUR');
      setCompensationCarriageChargesAmount('');
      setCompensationCarriageChargesCurrency('EUR');
      setCompensationDeclaredValueAmount('');
      setCompensationDeclaredValueCurrency('EUR');
      setCompensationSpecialInterestAmount('');
      setCompensationSpecialInterestCurrency('EUR');
      setCompensationDiminishedValueAmount('');
      setCompensationDiminishedValueCurrency('EUR');
      setCompensationCarrierLiabilityBasisRecorded(true);
      setCompensationArticle23LimitApplied(true);
      setCompensationCarriageChargesRefundConsidered(true);
      setCompensationDelayDamageProved(false);
      setCompensationCarriageChargesLimitApplied(false);
      setCompensationDamageAffectedWholeConsignment(false);
      setCompensationArticle25LossLimitApplied(false);
      setCompensationDeclaredValueEntered(false);
      setCompensationDeclaredValueSurchargeAgreed(false);
      setCompensationDeclaredValueExceedsArticle23Limit(false);
      setCompensationDeclaredValueLimitSubstituted(false);
      setCompensationSpecialInterestEntered(false);
      setCompensationSpecialInterestSurchargeAgreed(false);
      setCompensationAdditionalLossOrDamageProved(false);
      setCompensationSpecialInterestLimitApplied(false);
      setCompensationAgreedTimeLimitExceeded(false);
      setCompensationValuationDetail('');
      setCompensationValuationEvidenceAttachmentId('');
      setCompensationCalculationDetail('');
      setCompensationClaimDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCompensationInterest = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const isCurrencyConversion = compensationInterestRecordType === 'currency_conversion';
    const compensationInterestPayload: CompensationInterestRequest = isCurrencyConversion
      ? {
          recordType: compensationInterestRecordType,
          ...(compensationInterestLinkedAssessmentId ? { linkedCompensationAssessmentId: compensationInterestLinkedAssessmentId } : {}),
          amountsNotExpressedInPaymentClaimCurrency: compensationInterestAmountsNotPaymentCurrency,
          paymentClaimCurrencyCountryBasisRecorded: compensationInterestPaymentClaimCurrencyCountryBasis,
          paymentPlaceBasisRecorded: compensationInterestPaymentPlaceBasis,
          ...(compensationInterestPaymentAt ? { paymentAt: compensationInterestPaymentAt } : {}),
          exchangeRateApplicableAtPaymentPlaceAndDate: compensationInterestExchangeRateApplicable,
          ...(compensationInterestSourceAmount ? {
            sourceAmount: compensationInterestSourceAmount,
            sourceCurrency: compensationInterestSourceCurrency,
          } : {}),
          targetCurrency: compensationInterestTargetCurrency,
          ...(compensationInterestConvertedAmount ? {
            convertedAmount: compensationInterestConvertedAmount,
            convertedCurrency: compensationInterestConvertedCurrency,
          } : {}),
          ...(compensationInterestExchangeRateDetail ? { exchangeRateDetail: compensationInterestExchangeRateDetail } : {}),
          ...(compensationInterestClaimDetail ? { claimDetail: compensationInterestClaimDetail } : {}),
        }
      : {
          recordType: compensationInterestRecordType,
          ...(compensationInterestLinkedAssessmentId ? { linkedCompensationAssessmentId: compensationInterestLinkedAssessmentId } : {}),
          compensationPayableBasisRecorded: compensationInterestPayableBasisRecorded,
          interestStartBasis: compensationInterestStartBasis,
          ...(compensationInterestStartBasis === 'written_claim'
            ? { claimSentInWriting: compensationInterestClaimSentInWriting }
            : { legalProceedingsInstituted: compensationInterestLegalProceedingsInstituted }),
          ...(compensationInterestStartAt ? { interestStartAt: compensationInterestStartAt } : {}),
          ...(compensationInterestCalculatedAt ? { interestCalculatedAt: compensationInterestCalculatedAt } : {}),
          fivePercentPerAnnumApplied: compensationInterestFivePercentApplied,
          ...(compensationInterestCompensationAmount ? {
            compensationAmount: compensationInterestCompensationAmount,
            compensationCurrency: compensationInterestCompensationCurrency,
          } : {}),
          ...(compensationInterestAmount ? {
            interestAmount: compensationInterestAmount,
            interestCurrency: compensationInterestCurrency,
          } : {}),
          ...(compensationInterestCalculationDetail ? { interestCalculationDetail: compensationInterestCalculationDetail } : {}),
          ...(compensationInterestClaimDetail ? { claimDetail: compensationInterestClaimDetail } : {}),
        };
    try {
      await recordCompensationInterestInAPI(documentId, compensationInterestPayload);
      setMessage('Compensation interest/conversion record recorded without exposing private Article 27 amounts or details');
      setCompensationInterestRecordType('interest_on_compensation');
      setCompensationInterestStartBasis('written_claim');
      setCompensationInterestStartAt('');
      setCompensationInterestCalculatedAt('');
      setCompensationInterestPaymentAt('');
      setCompensationInterestLinkedAssessmentId('');
      setCompensationInterestCompensationAmount('');
      setCompensationInterestCompensationCurrency('EUR');
      setCompensationInterestAmount('');
      setCompensationInterestCurrency('EUR');
      setCompensationInterestSourceAmount('');
      setCompensationInterestSourceCurrency('EUR');
      setCompensationInterestTargetCurrency('EUR');
      setCompensationInterestConvertedAmount('');
      setCompensationInterestConvertedCurrency('EUR');
      setCompensationInterestPayableBasisRecorded(true);
      setCompensationInterestClaimSentInWriting(true);
      setCompensationInterestLegalProceedingsInstituted(false);
      setCompensationInterestFivePercentApplied(true);
      setCompensationInterestAmountsNotPaymentCurrency(false);
      setCompensationInterestPaymentClaimCurrencyCountryBasis(false);
      setCompensationInterestPaymentPlaceBasis(false);
      setCompensationInterestExchangeRateApplicable(false);
      setCompensationInterestCalculationDetail('');
      setCompensationInterestExchangeRateDetail('');
      setCompensationInterestClaimDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleLiabilityLimitation = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const isArticle3Person = liabilityLimitationPersonRole !== 'carrier';
    const liabilityPayload: LiabilityLimitationRequest = {
      recordType: liabilityLimitationRecordType,
      liablePersonRole: liabilityLimitationPersonRole,
      linkedIncidentId: liabilityLimitationLinkedIncidentId.trim(),
      claimArisesFromCarriageUnderCmr: liabilityClaimArisesFromCarriage,
      lossDamageOrDelayClaim: liabilityLossDamageOrDelayClaim,
      ...(isArticle3Person ? {
        article3ResponsiblePersonInvolved: liabilityArticle3PersonInvolved,
        article3PersonScopeRecorded: liabilityArticle3ScopeRecorded,
        actingWithinScopeOfEmployment: liabilityActingWithinScope,
        ...(liabilityArticle3PersonDetail ? { article3PersonDetail: liabilityArticle3PersonDetail } : {}),
      } : {}),
      ...(liabilityClaimDetail ? { claimDetail: liabilityClaimDetail } : {}),
    };
    if (liabilityLimitationRecordType === 'extra_contractual_claim_defense') {
      Object.assign(liabilityPayload, {
        extraContractualClaimUnderApplicableLaw: liabilityExtraContractualClaim,
        cmrExclusionOrLimitInvoked: liabilityCmrLimitInvoked,
        ...(liabilityApplicableLawDetail ? { applicableLawDetail: liabilityApplicableLawDetail } : {}),
      });
    } else {
      Object.assign(liabilityPayload, {
        limitationOrBurdenShiftProvisionInvoked: liabilityLimitationProvisionInvoked,
        wilfulMisconductAlleged: liabilityWilfulMisconductAlleged,
        equivalentDefaultAlleged: liabilityEquivalentDefaultAlleged,
        ...(liabilityEquivalentDefaultAlleged ? { courtOrTribunalLawBasisRecorded: liabilityCourtLawBasisRecorded } : {}),
        limitationDisapplicationBasisRecorded: liabilityDisapplicationBasisRecorded,
        ...(liabilityMisconductDetail ? { misconductDetail: liabilityMisconductDetail } : {}),
        ...(liabilityEquivalentDefaultAlleged && liabilityCourtLawDetail ? { courtLawDetail: liabilityCourtLawDetail } : {}),
      });
    }
    try {
      await recordLiabilityLimitationInAPI(documentId, liabilityPayload);
      setMessage('Liability limitation record recorded without exposing private Articles 28-29 claim or misconduct details');
      setLiabilityLimitationRecordType('extra_contractual_claim_defense');
      setLiabilityLimitationPersonRole('carrier');
      setLiabilityLimitationLinkedIncidentId('');
      setLiabilityClaimArisesFromCarriage(true);
      setLiabilityLossDamageOrDelayClaim(true);
      setLiabilityExtraContractualClaim(true);
      setLiabilityCmrLimitInvoked(true);
      setLiabilityArticle3PersonInvolved(false);
      setLiabilityArticle3ScopeRecorded(false);
      setLiabilityActingWithinScope(false);
      setLiabilityLimitationProvisionInvoked(true);
      setLiabilityWilfulMisconductAlleged(true);
      setLiabilityEquivalentDefaultAlleged(false);
      setLiabilityCourtLawBasisRecorded(false);
      setLiabilityDisapplicationBasisRecorded(true);
      setLiabilityClaimDetail('');
      setLiabilityApplicableLawDetail('');
      setLiabilityArticle3PersonDetail('');
      setLiabilityMisconductDetail('');
      setLiabilityCourtLawDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleClaimReservation = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const claimReservationPayload: ClaimReservationRequest = {
      recordType: claimReservationRecordType,
    };
    const excludedPublicHolidayDates = claimReservationExcludedPublicHolidayDates
      .split(/[\s,;]+/)
      .map((dateText) => dateText.trim())
      .filter(Boolean);
    if (claimReservationRecordType === 'apparent_loss_or_damage_reservation') {
      Object.assign(claimReservationPayload, {
        ...(claimReservationLinkedDeliveryId ? { linkedDeliveryId: claimReservationLinkedDeliveryId } : {}),
        ...(claimReservationLinkedClaimId ? { linkedClaimId: claimReservationLinkedClaimId } : {}),
        ...(claimReservationDeliveryAt ? { deliveryAt: claimReservationDeliveryAt } : {}),
        ...(claimReservationSentAt ? { reservationSentAt: claimReservationSentAt } : {}),
        lossOrDamageApparent: claimLossOrDamageApparent,
        reservationSentAtDelivery: claimReservationSentAtDelivery,
        ...(claimReservationDetail ? { reservationDetail: claimReservationDetail } : {}),
      });
    } else if (claimReservationRecordType === 'non_apparent_loss_or_damage_reservation') {
      Object.assign(claimReservationPayload, {
        ...(claimReservationLinkedDeliveryId ? { linkedDeliveryId: claimReservationLinkedDeliveryId } : {}),
        ...(claimReservationLinkedClaimId ? { linkedClaimId: claimReservationLinkedClaimId } : {}),
        ...(claimReservationDeliveryAt ? { deliveryAt: claimReservationDeliveryAt } : {}),
        ...(claimReservationConditionCheckedAt ? { conditionCheckedAt: claimReservationConditionCheckedAt } : {}),
        ...(claimReservationSentAt ? { reservationSentAt: claimReservationSentAt } : {}),
        lossOrDamageNotApparent: claimLossOrDamageNotApparent,
        reservationSentInWriting: claimReservationSentInWriting,
        reservationWithinSevenDaysExcludingHolidaysRecorded: claimReservationWithinSevenDays,
        relevantStartDateExcludedFromLimitCalculation: claimRelevantStartDateExcluded,
        ...(excludedPublicHolidayDates.length ? { excludedPublicHolidayDates } : {}),
        ...(claimConditionCheckedWithCarrier ? { conditionCheckedWithCarrier: true } : {}),
        ...(claimConditionCheckedWithCarrier ? {
          contraryEvidenceReservationSentInWriting: claimContraryEvidenceReservationWritten,
        } : {}),
        ...(claimReservationDetail ? { reservationDetail: claimReservationDetail } : {}),
        ...(claimConditionCheckDetail ? { conditionCheckDetail: claimConditionCheckDetail } : {}),
      });
    } else if (claimReservationRecordType === 'delay_compensation_reservation') {
      Object.assign(claimReservationPayload, {
        ...(claimReservationLinkedDeliveryId ? { linkedDeliveryId: claimReservationLinkedDeliveryId } : {}),
        ...(claimReservationLinkedClaimId ? { linkedClaimId: claimReservationLinkedClaimId } : {}),
        ...(claimReservationGoodsAvailableAt ? { goodsAvailableAt: claimReservationGoodsAvailableAt } : {}),
        ...(claimReservationSentAt ? { reservationSentAt: claimReservationSentAt } : {}),
        delayCompensationClaim: claimDelayCompensationClaim,
        delayReservationSentInWriting: claimDelayReservationWritten,
        reservationWithinTwentyOneDaysRecorded: claimReservationWithinTwentyOneDays,
        relevantStartDateExcludedFromLimitCalculation: claimRelevantStartDateExcluded,
        ...(claimDelayClaimDetail ? { delayClaimDetail: claimDelayClaimDetail } : {}),
      });
    } else {
      Object.assign(claimReservationPayload, {
        ...(claimReservationLinkedDeliveryId ? { linkedDeliveryId: claimReservationLinkedDeliveryId } : {}),
        ...(claimReservationLinkedConditionCheckId ? { linkedConditionCheckId: claimReservationLinkedConditionCheckId } : {}),
        ...(claimReservationConditionCheckedAt ? { conditionCheckedAt: claimReservationConditionCheckedAt } : {}),
        conditionCheckedWithCarrier: claimConditionCheckedWithCarrier,
        reasonableFacilitiesProvided: claimReasonableFacilitiesProvided,
        ...(claimConditionCheckDetail ? { conditionCheckDetail: claimConditionCheckDetail } : {}),
        ...(claimInvestigationFacilityDetail ? { investigationFacilityDetail: claimInvestigationFacilityDetail } : {}),
      });
    }
    try {
      await recordClaimReservationInAPI(documentId, claimReservationPayload);
      setMessage('Claim reservation record recorded without exposing private Article 30 reservation or claim details');
      setClaimReservationRecordType('non_apparent_loss_or_damage_reservation');
      setClaimReservationLinkedDeliveryId('');
      setClaimReservationLinkedClaimId('');
      setClaimReservationLinkedConditionCheckId('');
      setClaimReservationDeliveryAt('');
      setClaimReservationConditionCheckedAt('');
      setClaimReservationGoodsAvailableAt('');
      setClaimReservationSentAt('');
      setClaimReservationExcludedPublicHolidayDates('');
      setClaimLossOrDamageApparent(false);
      setClaimLossOrDamageNotApparent(true);
      setClaimDelayCompensationClaim(false);
      setClaimReservationSentAtDelivery(false);
      setClaimReservationSentInWriting(true);
      setClaimReservationWithinSevenDays(true);
      setClaimConditionCheckedWithCarrier(false);
      setClaimContraryEvidenceReservationWritten(false);
      setClaimDelayReservationWritten(false);
      setClaimReservationWithinTwentyOneDays(false);
      setClaimRelevantStartDateExcluded(true);
      setClaimReasonableFacilitiesProvided(false);
      setClaimReservationDetail('');
      setClaimConditionCheckDetail('');
      setClaimDelayClaimDetail('');
      setClaimInvestigationFacilityDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDisputeProcedure = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    const disputePayload: DisputeProcedureRequest = {
      recordType: disputeProcedureRecordType,
    };
    if (disputeProcedureRecordType === 'jurisdiction_basis_notice') {
      Object.assign(disputePayload, {
        ...(disputeLinkedClaimId ? { linkedClaimId: disputeLinkedClaimId } : {}),
        ...(disputeLinkedProceedingId ? { linkedProceedingId: disputeLinkedProceedingId } : {}),
        partyDesignatedCourtOrTribunal: disputePartyDesignatedCourt,
        defendantResidenceOrBusinessPlaceBasis: disputeDefendantResidenceBasis,
        contractBranchOrAgencyBasis: disputeContractBranchBasis,
        takeoverPlaceBasis: disputeTakeoverPlaceBasis,
        deliveryPlaceBasis: disputeDeliveryPlaceBasis,
        contractingCountryCourtOrTribunal: disputeContractingCountryCourt,
        ...(disputeSecurityForCostsNotRequired ? { securityForCostsNotRequiredRecorded: true } : {}),
        ...(disputeJurisdictionDetail ? { jurisdictionDetail: disputeJurisdictionDetail } : {}),
        ...(disputePartyAgreementDetail ? { partyAgreementDetail: disputePartyAgreementDetail } : {}),
        ...(disputeTakeoverDeliveryPlaceDetail ? { takeoverDeliveryPlaceDetail: disputeTakeoverDeliveryPlaceDetail } : {}),
        ...(disputeCourtOrTribunalDetail ? { courtOrTribunalDetail: disputeCourtOrTribunalDetail } : {}),
      });
    } else if (disputeProcedureRecordType === 'pending_or_judgment_bar_notice') {
      Object.assign(disputePayload, {
        ...(disputeLinkedClaimId ? { linkedClaimId: disputeLinkedClaimId } : {}),
        ...(disputeLinkedProceedingId ? { linkedProceedingId: disputeLinkedProceedingId } : {}),
        ...(disputeLinkedJudgmentId ? { linkedJudgmentId: disputeLinkedJudgmentId } : {}),
        ...(disputeActionFiledAt ? { actionFiledAt: disputeActionFiledAt } : {}),
        ...(disputeJudgmentEnteredAt ? { judgmentEnteredAt: disputeJudgmentEnteredAt } : {}),
        ...(disputeJudgmentEnforceableAt ? { judgmentEnforceableAt: disputeJudgmentEnforceableAt } : {}),
        samePartiesAndGroundsRecorded: disputeSamePartiesAndGrounds,
        pendingCompetentProceedingRecorded: disputePendingCompetentProceeding,
        judgmentEnteredByCompetentCourt: disputeJudgmentEnteredByCompetentCourt,
        ...(disputeJudgmentEnteredByCompetentCourt ? {
          freshProceedingOnlyIfJudgmentNotEnforceableRecorded: disputeFreshProceedingOnlyIfNotEnforceable,
          judgmentEnforceabilityFormalitiesOnlyRecorded: disputeJudgmentFormalitiesOnly,
          meritsReopeningProhibitedRecorded: disputeMeritsReopeningProhibited,
        } : {}),
        ...(disputePendingActionDetail ? { pendingActionDetail: disputePendingActionDetail } : {}),
        ...(disputeJudgmentDetail ? { judgmentDetail: disputeJudgmentDetail } : {}),
      });
    } else if (disputeProcedureRecordType === 'limitation_period_record') {
      Object.assign(disputePayload, {
        claimCategory: disputeClaimCategory,
        limitationPeriodBasis: disputeLimitationPeriodBasis,
        limitationStartBasis: disputeLimitationStartBasis,
        ...(disputeLinkedClaimId ? { linkedClaimId: disputeLinkedClaimId } : {}),
        ...(disputeDeliveryAt ? { deliveryAt: disputeDeliveryAt } : {}),
        ...(disputeTakeoverAt ? { takeoverAt: disputeTakeoverAt } : {}),
        ...(disputeAgreedTimeLimitExpiresAt ? { agreedTimeLimitExpiresAt: disputeAgreedTimeLimitExpiresAt } : {}),
        ...(disputeContractMadeAt ? { contractMadeAt: disputeContractMadeAt } : {}),
        ...(disputeLimitationStartedAt ? { limitationStartedAt: disputeLimitationStartedAt } : {}),
        ...(disputeLimitationExpiresAt ? { limitationExpiresAt: disputeLimitationExpiresAt } : {}),
        ...(disputeLimitationPeriodBasis === 'three_year_wilful_misconduct' ? {
          wilfulMisconductOrEquivalentDefaultAlleged: disputeWilfulMisconductEquivalent,
        } : {}),
        limitationPeriodRecorded: disputeLimitationPeriodRecorded,
        limitationStartDateExcluded: disputeLimitationStartDateExcluded,
        ...(disputeClaimBarredNoCounterclaim ? { claimBarredNotAvailableByCounterclaimOrSetoffRecorded: true } : {}),
        ...(disputeCourtLawExtensionBasis ? { courtLawExtensionOrFreshAccrualBasisRecorded: true } : {}),
        ...(disputeLimitationCalculationDetail ? { limitationCalculationDetail: disputeLimitationCalculationDetail } : {}),
        ...(disputeCourtOrTribunalDetail ? { courtOrTribunalDetail: disputeCourtOrTribunalDetail } : {}),
      });
    } else if (disputeProcedureRecordType === 'written_claim_suspension_record') {
      Object.assign(disputePayload, {
        ...(disputeLinkedClaimId ? { linkedClaimId: disputeLinkedClaimId } : {}),
        ...(disputeLinkedWrittenClaimId ? { linkedWrittenClaimId: disputeLinkedWrittenClaimId } : {}),
        ...(disputeWrittenClaimReceivedAt ? { writtenClaimReceivedAt: disputeWrittenClaimReceivedAt } : {}),
        ...(disputeClaimRejectedAt ? { claimRejectedAt: disputeClaimRejectedAt } : {}),
        ...(disputeDocumentsReturnedAt ? { documentsReturnedAt: disputeDocumentsReturnedAt } : {}),
        writtenClaimReceived: disputeWrittenClaimReceived,
        ...(disputeCarrierRejectedClaimInWriting ? { carrierRejectedClaimInWriting: true } : {}),
        ...(disputeDocumentsReturned ? { documentsReturned: true } : {}),
        ...(disputeClaimPartlyAdmitted ? { claimPartlyAdmitted: true } : {}),
        ...(disputeClaimPartlyAdmitted ? { remainingPartStillInDispute: disputeRemainingPartStillInDispute } : {}),
        receiptReplyReturnProofBurdenRecorded: disputeReceiptReplyReturnProofBurden,
        furtherSameObjectClaimsDoNotSuspendRecorded: disputeFurtherSameObjectNoSuspend,
        ...(disputeWrittenClaimDetail ? { writtenClaimDetail: disputeWrittenClaimDetail } : {}),
        ...(disputeRejectionDetail ? { rejectionDetail: disputeRejectionDetail } : {}),
      });
    } else {
      Object.assign(disputePayload, {
        ...(disputeLinkedArbitrationClauseId ? { linkedArbitrationClauseId: disputeLinkedArbitrationClauseId } : {}),
        arbitrationClausePresent: disputeArbitrationClausePresent,
        arbitrationTribunalCompetenceRecorded: disputeArbitrationTribunalCompetence,
        tribunalAppliesCmrConvention: disputeTribunalAppliesCmr,
        ...(disputeArbitrationClauseDetail ? { arbitrationClauseDetail: disputeArbitrationClauseDetail } : {}),
      });
    }
    try {
      await recordDisputeProcedureInAPI(documentId, disputePayload);
      setMessage('Dispute procedure record recorded without exposing private Articles 31-33 court, limitation, claim, or arbitration details');
      setDisputeProcedureRecordType('limitation_period_record');
      setDisputeClaimCategory('damage');
      setDisputeLimitationPeriodBasis('one_year');
      setDisputeLimitationStartBasis('delivery_date');
      setDisputeLinkedClaimId('');
      setDisputeLinkedProceedingId('');
      setDisputeLinkedJudgmentId('');
      setDisputeLinkedWrittenClaimId('');
      setDisputeLinkedArbitrationClauseId('');
      setDisputeActionFiledAt('');
      setDisputeJudgmentEnteredAt('');
      setDisputeJudgmentEnforceableAt('');
      setDisputeDeliveryAt('');
      setDisputeTakeoverAt('');
      setDisputeAgreedTimeLimitExpiresAt('');
      setDisputeContractMadeAt('');
      setDisputeLimitationStartedAt('');
      setDisputeLimitationExpiresAt('');
      setDisputeWrittenClaimReceivedAt('');
      setDisputeClaimRejectedAt('');
      setDisputeDocumentsReturnedAt('');
      setDisputePartyDesignatedCourt(false);
      setDisputeDefendantResidenceBasis(false);
      setDisputeContractBranchBasis(false);
      setDisputeTakeoverPlaceBasis(false);
      setDisputeDeliveryPlaceBasis(false);
      setDisputeContractingCountryCourt(false);
      setDisputeSecurityForCostsNotRequired(false);
      setDisputeSamePartiesAndGrounds(false);
      setDisputePendingCompetentProceeding(false);
      setDisputeJudgmentEnteredByCompetentCourt(false);
      setDisputeFreshProceedingOnlyIfNotEnforceable(false);
      setDisputeJudgmentFormalitiesOnly(false);
      setDisputeMeritsReopeningProhibited(false);
      setDisputeWilfulMisconductEquivalent(false);
      setDisputeLimitationPeriodRecorded(true);
      setDisputeLimitationStartDateExcluded(true);
      setDisputeClaimBarredNoCounterclaim(false);
      setDisputeCourtLawExtensionBasis(false);
      setDisputeWrittenClaimReceived(false);
      setDisputeCarrierRejectedClaimInWriting(false);
      setDisputeDocumentsReturned(false);
      setDisputeClaimPartlyAdmitted(false);
      setDisputeRemainingPartStillInDispute(false);
      setDisputeReceiptReplyReturnProofBurden(false);
      setDisputeFurtherSameObjectNoSuspend(false);
      setDisputeArbitrationClausePresent(false);
      setDisputeArbitrationTribunalCompetence(false);
      setDisputeTribunalAppliesCmr(false);
      setDisputeJurisdictionDetail('');
      setDisputePartyAgreementDetail('');
      setDisputeTakeoverDeliveryPlaceDetail('');
      setDisputePendingActionDetail('');
      setDisputeJudgmentDetail('');
      setDisputeLimitationCalculationDetail('');
      setDisputeWrittenClaimDetail('');
      setDisputeRejectionDetail('');
      setDisputeArbitrationClauseDetail('');
      setDisputeCourtOrTribunalDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleConsignmentNoteCopyAccess = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const isSenderFirstCopy = copyRecordType === 'sender_first_copy_access_available';
    const isGoodsAccompanyingSecondCopy = copyRecordType === 'goods_accompanying_second_copy_access_available';
    const isCarrierRetainedThirdCopy = copyRecordType === 'carrier_retained_third_copy_access_available';

    if (
      (!copyLinkedAccessRecordId && !copyAccessDetail) ||
      (isSenderFirstCopy && !copySenderFirstCopyAvailable) ||
      (isGoodsAccompanyingSecondCopy && !copyGoodsAccompanyingSecondCopyAvailable) ||
      (isCarrierRetainedThirdCopy && !copyCarrierRetainedThirdCopyAvailable) ||
      (isGoodsAccompanyingSecondCopy && !copyLinkedHandoverRecordId && !copyAccessDetail)
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: ConsignmentNoteCopyRequest = {
      recordType: copyRecordType,
      electronicCopyAccessAvailable: copyElectronicAccessAvailable,
      entitledPartyAccessAvailable: copyEntitledPartyAccessAvailable,
      ...(isSenderFirstCopy ? { senderFirstCopyAvailable: true } : {}),
      ...(isGoodsAccompanyingSecondCopy ? { goodsAccompanyingSecondCopyAvailable: true } : {}),
      ...(isCarrierRetainedThirdCopy ? { carrierRetainedThirdCopyAvailable: true } : {}),
      ...(copyLinkedAccessRecordId ? { linkedCopyAccessRecordId: copyLinkedAccessRecordId } : {}),
      ...(isGoodsAccompanyingSecondCopy && copyLinkedHandoverRecordId ? { linkedHandoverRecordId: copyLinkedHandoverRecordId } : {}),
      ...(copyAccessDetail ? { copyAccessDetail } : {}),
    };

    try {
      await recordConsignmentNoteCopyInAPI(documentId, payload);
      setMessage('Consignment note copy access recorded without exposing private Article 5 copy or handover details');
      setCopyRecordType('sender_first_copy_access_available');
      setCopyLinkedAccessRecordId('');
      setCopyLinkedHandoverRecordId('');
      setCopyElectronicAccessAvailable(true);
      setCopyEntitledPartyAccessAvailable(true);
      setCopySenderFirstCopyAvailable(true);
      setCopyGoodsAccompanyingSecondCopyAvailable(false);
      setCopyCarrierRetainedThirdCopyAvailable(false);
      setCopyAccessDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleEcmrReceiptAccessRequest = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const payload: EcmrReceiptAccessSenderRequest = {
      ...(ecmrSenderRequestDetail ? { requestDetail: ecmrSenderRequestDetail } : {}),
    };

    try {
      await requestEcmrReceiptAccessInAPI(documentId, payload);
      setMessage('eCMR Article 6.1 sender request recorded without exposing private request details');
      setEcmrSenderRequestDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleEcmrReceiptAccess = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (
      !ecmrReceiptHandoverAt ||
      !ecmrSenderRequestReceived ||
      !ecmrReceiptForGoodsProvided ||
      !ecmrShipmentIdentificationInformationProvided ||
      !ecmrElectronicConsignmentNoteAccessInformationProvided ||
      (!ecmrLinkedGoodsReceiptId && !ecmrReceiptDetail) ||
      (!ecmrLinkedShipmentIdentificationRecordId && !ecmrShipmentIdentificationDetail) ||
      (!ecmrLinkedAccessInformationRecordId && !ecmrAccessInformationDetail)
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: EcmrReceiptAccessRequest = {
      recordType: 'sender_receipt_access_information_handover',
      handoverAt: ecmrReceiptHandoverAt,
      senderRequestReceived: ecmrSenderRequestReceived,
      receiptForGoodsProvided: ecmrReceiptForGoodsProvided,
      shipmentIdentificationInformationProvided: ecmrShipmentIdentificationInformationProvided,
      electronicConsignmentNoteAccessInformationProvided: ecmrElectronicConsignmentNoteAccessInformationProvided,
      ...(ecmrLinkedGoodsReceiptId ? { linkedGoodsReceiptId: ecmrLinkedGoodsReceiptId } : {}),
      ...(ecmrLinkedShipmentIdentificationRecordId ? { linkedShipmentIdentificationRecordId: ecmrLinkedShipmentIdentificationRecordId } : {}),
      ...(ecmrLinkedAccessInformationRecordId ? { linkedAccessInformationRecordId: ecmrLinkedAccessInformationRecordId } : {}),
      ...(ecmrReceiptDetail ? { receiptDetail: ecmrReceiptDetail } : {}),
      ...(ecmrShipmentIdentificationDetail ? { shipmentIdentificationDetail: ecmrShipmentIdentificationDetail } : {}),
      ...(ecmrAccessInformationDetail ? { accessInformationDetail: ecmrAccessInformationDetail } : {}),
    };

    try {
      await recordEcmrReceiptAccessInAPI(documentId, payload);
      setMessage('eCMR Article 6.1 receipt/access evidence recorded without exposing private receipt, shipment, or access details');
      setEcmrReceiptHandoverAt('');
      setEcmrLinkedGoodsReceiptId('');
      setEcmrLinkedShipmentIdentificationRecordId('');
      setEcmrLinkedAccessInformationRecordId('');
      setEcmrSenderRequestReceived(true);
      setEcmrReceiptForGoodsProvided(true);
      setEcmrShipmentIdentificationInformationProvided(true);
      setEcmrElectronicConsignmentNoteAccessInformationProvided(true);
      setEcmrReceiptDetail('');
      setEcmrShipmentIdentificationDetail('');
      setEcmrAccessInformationDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleMultimodalCarriage = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const isLoadedVehicleRecord = multimodalRecordType === 'loaded_vehicle_other_mode_carriage';
    const isArticle14Record = multimodalRecordType === 'article14_unloading_impediment_basis';
    const missingOtherModeDocumentReference = !multimodalLinkedOtherModeDocumentId;

    if (
      !multimodalVehicleCarriedByOtherMode ||
      !multimodalOtherModeEvidencePresent ||
      missingOtherModeDocumentReference ||
      (isLoadedVehicleRecord && (!multimodalGoodsRemainedLoaded || multimodalGoodsUnloaded)) ||
      (isArticle14Record && (
        !multimodalGoodsUnloaded ||
        !multimodalArticle14BasisRecorded ||
        !multimodalLinkedArticle14ImpedimentId
      ))
    ) {
      if (missingOtherModeDocumentReference) {
        setMultimodalOtherModeDetail('');
      }
      if (isArticle14Record && !multimodalLinkedArticle14ImpedimentId) {
        setMultimodalArticle14ImpedimentDetail('');
      }
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: MultimodalCarriageRequest = {
      recordType: multimodalRecordType,
      otherModeType: multimodalOtherModeType,
      vehicleCarriedByOtherMode: multimodalVehicleCarriedByOtherMode,
      otherModeCarriageEvidencePresent: multimodalOtherModeEvidencePresent,
      ...(isLoadedVehicleRecord ? { goodsRemainedLoadedOnVehicle: true } : {}),
      ...(isArticle14Record ? {
        goodsUnloadedFromVehicle: true,
        article14ImpedimentBasisRecorded: true,
      } : {}),
      ...(multimodalLinkedOtherModeDocumentId ? { linkedOtherModeCarriageDocumentId: multimodalLinkedOtherModeDocumentId } : {}),
      ...(multimodalLinkedDeclarationId ? { linkedMultimodalDeclarationId: multimodalLinkedDeclarationId } : {}),
      ...(multimodalOtherModeDetail ? { otherModeCarriageDetail: multimodalOtherModeDetail } : {}),
      ...(isArticle14Record && multimodalLinkedArticle14ImpedimentId ? { linkedArticle14ImpedimentRecordId: multimodalLinkedArticle14ImpedimentId } : {}),
      ...(isArticle14Record && multimodalArticle14ImpedimentDetail ? { article14ImpedimentDetail: multimodalArticle14ImpedimentDetail } : {}),
    };

    try {
      await recordMultimodalCarriageInAPI(documentId, payload);
      setMessage('Article 2 multimodal carriage recorded without exposing private other-mode carriage or Article 14 details');
      setMultimodalRecordType('loaded_vehicle_other_mode_carriage');
      setMultimodalOtherModeType('rail');
      setMultimodalLinkedOtherModeDocumentId('');
      setMultimodalLinkedArticle14ImpedimentId('');
      setMultimodalLinkedDeclarationId('');
      setMultimodalVehicleCarriedByOtherMode(true);
      setMultimodalGoodsRemainedLoaded(true);
      setMultimodalGoodsUnloaded(false);
      setMultimodalArticle14BasisRecorded(false);
      setMultimodalOtherModeEvidencePresent(true);
      setMultimodalOtherModeDetail('');
      setMultimodalArticle14ImpedimentDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleSenderParticularsResponsibility = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const isParticularInaccuracyRecord = [
      'sender_article6_particular_inaccuracy',
      'sender_optional_particular_inaccuracy',
    ].includes(senderParticularsRecordType);
    const isInstructionInaccuracyRecord = senderParticularsRecordType === 'sender_instruction_inaccuracy';
    const isArticle71Record = isParticularInaccuracyRecord || isInstructionInaccuracyRecord;
    const isCarrierEntryRecord = senderParticularsRecordType === 'carrier_entered_particulars_on_sender_behalf';
    const isCmrStatementOmissionRecord = senderParticularsRecordType === 'carrier_cmr_statement_omission';
    const hasParticularEvidence = Boolean(senderParticularsLinkedParticularRecordId || senderParticularsParticularDetail);
    const hasInstructionEvidence = Boolean(senderParticularsLinkedInstructionId || senderParticularsInstructionDetail);
    const hasClaimOrExpenseEvidence = Boolean(
      senderParticularsLinkedClaimId ||
      senderParticularsLinkedExpenseRecordId ||
      senderParticularsExpenseLossDamageDetail
    );

    if (
      (isArticle71Record && (
        !senderParticularsInaccuracyRecorded ||
        !senderParticularsExpenseLossDamageClaimed ||
        (isParticularInaccuracyRecord && !hasParticularEvidence) ||
        (isInstructionInaccuracyRecord && !hasInstructionEvidence) ||
        !hasClaimOrExpenseEvidence
      )) ||
      (isCarrierEntryRecord && (
        !senderParticularsCarrierEnteredAtRequest ||
        !senderParticularsSenderBehalfPresumption ||
        (!senderParticularsLinkedCarrierEntryRecordId && !senderParticularsCarrierEntryDetail)
      )) ||
      (isCmrStatementOmissionRecord && (
        !senderParticularsCmrStatementOmissionRecorded ||
        !senderParticularsCarrierLiabilityBasisRecorded ||
        !senderParticularsExpenseLossDamageClaimed ||
        (!senderParticularsLinkedReviewId && !senderParticularsCmrStatementOmissionDetail)
      ))
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const affectedParticularCategory: SenderParticularsResponsibilityCategory =
      senderParticularsRecordType === 'sender_optional_particular_inaccuracy'
        ? 'article6_2_optional_particular'
        : senderParticularsRecordType === 'sender_instruction_inaccuracy'
          ? 'sender_other_particular_or_instruction'
          : senderParticularsRecordType === 'carrier_cmr_statement_omission'
            ? 'article6_1_k_cmr_statement'
            : senderParticularsCategory;

    const payload: SenderParticularsResponsibilityRequest = {
      recordType: senderParticularsRecordType,
      affectedParticularCategory,
      ...(senderParticularsReviewedAt ? { reviewedAt: senderParticularsReviewedAt } : {}),
    };

    if (isArticle71Record) {
      Object.assign(payload, {
        ...(isParticularInaccuracyRecord && senderParticularsLinkedParticularRecordId ? { linkedParticularRecordId: senderParticularsLinkedParticularRecordId } : {}),
        ...(isInstructionInaccuracyRecord && senderParticularsLinkedInstructionId ? { linkedInstructionId: senderParticularsLinkedInstructionId } : {}),
        ...(senderParticularsLinkedClaimId ? { linkedClaimId: senderParticularsLinkedClaimId } : {}),
        ...(senderParticularsLinkedExpenseRecordId ? { linkedExpenseRecordId: senderParticularsLinkedExpenseRecordId } : {}),
        inaccuracyOrInadequacyRecorded: true,
        expenseLossDamageClaimed: true,
        ...(isParticularInaccuracyRecord && senderParticularsParticularDetail ? { particularDetail: senderParticularsParticularDetail } : {}),
        ...(isInstructionInaccuracyRecord && senderParticularsInstructionDetail ? { instructionDetail: senderParticularsInstructionDetail } : {}),
        ...(senderParticularsExpenseLossDamageDetail ? { expenseLossDamageDetail: senderParticularsExpenseLossDamageDetail } : {}),
      });
    }

    if (isCarrierEntryRecord) {
      Object.assign(payload, {
        ...(senderParticularsLinkedCarrierEntryRecordId ? { linkedCarrierEntryRecordId: senderParticularsLinkedCarrierEntryRecordId } : {}),
        carrierEnteredAtSenderRequest: true,
        senderBehalfPresumptionRecorded: true,
        ...(senderParticularsCarrierEntryDetail ? { carrierEntryDetail: senderParticularsCarrierEntryDetail } : {}),
      });
    }

    if (isCmrStatementOmissionRecord) {
      Object.assign(payload, {
        ...(senderParticularsLinkedReviewId ? { linkedConsignmentNoteReviewId: senderParticularsLinkedReviewId } : {}),
        cmrStatementOmissionRecorded: true,
        carrierLiabilityBasisRecorded: true,
        expenseLossDamageClaimed: true,
        ...(senderParticularsCmrStatementOmissionDetail ? { cmrStatementOmissionDetail: senderParticularsCmrStatementOmissionDetail } : {}),
      });
    }

    try {
      await recordSenderParticularsResponsibilityInAPI(documentId, payload);
      setMessage('Article 7 sender particulars responsibility recorded without exposing private particulars, instructions, claims, or review details');
      setSenderParticularsRecordType('sender_article6_particular_inaccuracy');
      setSenderParticularsCategory('article6_1_f_goods_packing');
      setSenderParticularsLinkedParticularRecordId('');
      setSenderParticularsLinkedInstructionId('');
      setSenderParticularsLinkedClaimId('');
      setSenderParticularsLinkedExpenseRecordId('');
      setSenderParticularsLinkedCarrierEntryRecordId('');
      setSenderParticularsLinkedReviewId('');
      setSenderParticularsReviewedAt('');
      setSenderParticularsInaccuracyRecorded(true);
      setSenderParticularsExpenseLossDamageClaimed(true);
      setSenderParticularsCarrierEnteredAtRequest(false);
      setSenderParticularsSenderBehalfPresumption(false);
      setSenderParticularsCmrStatementOmissionRecorded(false);
      setSenderParticularsCarrierLiabilityBasisRecorded(false);
      setSenderParticularsParticularDetail('');
      setSenderParticularsInstructionDetail('');
      setSenderParticularsExpenseLossDamageDetail('');
      setSenderParticularsCarrierEntryDetail('');
      setSenderParticularsCmrStatementOmissionDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleSuccessiveCarrier = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (
      (successiveAcceptanceRecordSelected && (!successiveCarrierAcceptedAt || !successiveReceiptIssuedAt || !successiveLinkedConsignmentNoteCopyId)) ||
      (successiveCarrierRecordType === 'successive_carrier_recovery_allocation' && (!successiveCompensationPaidAt || !successiveLinkedCompensationAssessmentId)) ||
      (
        successiveCarrierRecordType === 'successive_carrier_recovery_proceeding' &&
        (!successiveRecoveryClaimFiledAt ||
          !successiveLinkedCompensationAssessmentId ||
          (successiveRecoveryStartBasis === 'actual_payment' && !successiveCompensationPaidAt) ||
          (successiveRecoveryStartBasis === 'final_judicial_decision' && !successiveFinalJudicialDecisionAt))
      ) ||
      (successiveCarrierRecordType === 'successive_carrier_agreement_variation' && !successiveAgreementRecordedAt) ||
      (
        successiveCarrierRecordType === 'successive_carrier_action_scope' &&
        !successiveCounterclaimOrSetoff &&
        !successiveActionAgainstFirstCarrier &&
        !successiveActionAgainstLastCarrier &&
        !successiveActionAgainstEventCarrier
      )
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: SuccessiveCarrierRequest = {
      recordType: successiveCarrierRecordType,
    };

    if (successiveCarrierRecordType === 'successive_carrier_acceptance') {
      Object.assign(payload, {
        ...(successiveLinkedSuccessiveCarrierId ? { linkedSuccessiveCarrierId: successiveLinkedSuccessiveCarrierId } : {}),
        ...(successiveLinkedPreviousCarrierId ? { linkedPreviousCarrierId: successiveLinkedPreviousCarrierId } : {}),
        ...(successiveLinkedNextCarrierId ? { linkedNextCarrierId: successiveLinkedNextCarrierId } : {}),
        ...(successiveLinkedConsignmentNoteCopyId ? { linkedConsignmentNoteCopyId: successiveLinkedConsignmentNoteCopyId } : {}),
        ...(successiveLinkedReservationId ? { linkedReservationId: successiveLinkedReservationId } : {}),
        ...(successiveCarrierAcceptedAt ? { carrierAcceptedAt: successiveCarrierAcceptedAt } : {}),
        ...(successiveReceiptIssuedAt ? { receiptIssuedAt: successiveReceiptIssuedAt } : {}),
        ...(successiveReceiptDetail ? { receiptDetail: successiveReceiptDetail } : {}),
        ...(successiveCarrierIdentityEntryDetail ? { carrierIdentityEntryDetail: successiveCarrierIdentityEntryDetail } : {}),
        ...(successiveReservationDetail ? { reservationDetail: successiveReservationDetail } : {}),
        singleContractCarriage: true,
        successiveRoadCarrier: true,
        goodsAccepted: true,
        consignmentNoteAccepted: true,
        becomesPartyByAcceptance: true,
        datedSignedReceiptIssued: true,
        carrierNameAddressEnteredOnSecondCopy: true,
        article8ReservationsEnteredWhereApplicable: successiveArticle8ReservationsEntered,
        article9AppliesBetweenSuccessiveCarriers: true,
        liabilityForWholeOperationRecorded: true,
      });
    }

    if (successiveCarrierRecordType === 'successive_carrier_action_scope') {
      Object.assign(payload, {
        ...(successiveLinkedSuccessiveCarrierId ? { linkedSuccessiveCarrierId: successiveLinkedSuccessiveCarrierId } : {}),
        ...(successiveLinkedClaimId ? { linkedClaimId: successiveLinkedClaimId } : {}),
        ...(successiveLinkedProceedingId ? { linkedProceedingId: successiveLinkedProceedingId } : {}),
        ...(successiveActionScopeDetail ? { actionScopeDetail: successiveActionScopeDetail } : {}),
        lossDamageOrDelayProceeding: true,
        actionAgainstFirstCarrier: successiveActionAgainstFirstCarrier,
        actionAgainstLastCarrier: successiveActionAgainstLastCarrier,
        actionAgainstEventCarrier: successiveActionAgainstEventCarrier,
        counterclaimOrSetoffSameContract: successiveCounterclaimOrSetoff,
        multiplePermittedCarriersJoined: successiveMultipleCarriersJoined,
      });
    }

    if (successiveCarrierRecordType === 'successive_carrier_recovery_allocation') {
      Object.assign(payload, {
        ...(successiveLinkedSuccessiveCarrierId ? { linkedSuccessiveCarrierId: successiveLinkedSuccessiveCarrierId } : {}),
        ...(successiveLinkedClaimId ? { linkedClaimId: successiveLinkedClaimId } : {}),
        ...(successiveLinkedCompensationPaymentId ? { linkedCompensationPaymentId: successiveLinkedCompensationPaymentId } : {}),
        linkedCompensationAssessmentId: successiveLinkedCompensationAssessmentId,
        ...(successiveCompensationPaidAt ? { compensationPaidAt: successiveCompensationPaidAt } : {}),
        ...(successiveAllocationDetail ? { allocationDetail: successiveAllocationDetail } : {}),
        ...(successiveAllocationBasis === 'insolvent_carrier_share_reallocated' && successiveInsolvencyDetail ? { insolvencyDetail: successiveInsolvencyDetail } : {}),
        allocationBasis: successiveAllocationBasis,
        compensationPaidUnderCmr: true,
        interestAndCostsIncluded: true,
        responsibleCarrierSoleLiabilityRecorded: successiveAllocationBasis === 'responsible_carrier',
        multipleCarrierLiabilityApportioned: successiveAllocationBasis === 'multiple_carriers_liability_share',
        liabilityCannotBeAttributed: successiveAllocationBasis === 'unknown_attribution_payment_share',
        paymentShareApportionmentRecorded: successiveAllocationBasis === 'multiple_carriers_payment_share' || successiveAllocationBasis === 'unknown_attribution_payment_share',
        insolventCarrierShareUnpaid: successiveAllocationBasis === 'insolvent_carrier_share_reallocated',
        insolventShareReallocatedByPaymentShare: successiveAllocationBasis === 'insolvent_carrier_share_reallocated',
      });
    }

    if (successiveCarrierRecordType === 'successive_carrier_recovery_proceeding') {
      Object.assign(payload, {
        ...(successiveLinkedSuccessiveCarrierId ? { linkedSuccessiveCarrierId: successiveLinkedSuccessiveCarrierId } : {}),
        ...(successiveLinkedCompensationPaymentId ? { linkedCompensationPaymentId: successiveLinkedCompensationPaymentId } : {}),
        linkedCompensationAssessmentId: successiveLinkedCompensationAssessmentId,
        ...(successiveLinkedProceedingId ? { linkedProceedingId: successiveLinkedProceedingId } : {}),
        ...(successiveRecoveryStartBasis === 'actual_payment' && successiveCompensationPaidAt ? { compensationPaidAt: successiveCompensationPaidAt } : {}),
        ...(successiveRecoveryStartBasis === 'final_judicial_decision' && successiveFinalJudicialDecisionAt ? { finalJudicialDecisionAt: successiveFinalJudicialDecisionAt } : {}),
        ...(successiveRecoveryClaimFiledAt ? { recoveryClaimFiledAt: successiveRecoveryClaimFiledAt } : {}),
        ...(successiveProceedingDetail ? { proceedingDetail: successiveProceedingDetail } : {}),
        ...(successivePaymentValidityDisputeBar && successiveNoticeOpportunityDetail ? { noticeOpportunityDetail: successiveNoticeOpportunityDetail } : {}),
        recoveryLimitationStartBasis: successiveRecoveryStartBasis,
        compensationPaidUnderCmr: true,
        competentRecoveryForumRecorded: true,
        allConcernedCarriersMayBeDefendants: successiveAllConcernedCarriersMayBeDefendants,
        article31JudgmentRulesApplied: true,
        article32LimitationApplied: true,
        recoveryLimitationStartRecorded: true,
        paymentValidityDisputeBarRecorded: successivePaymentValidityDisputeBar,
        judicialAuthorityDeterminedCompensation: successivePaymentValidityDisputeBar,
        carrierGivenDueNoticeOfProceedings: successivePaymentValidityDisputeBar,
        carrierHadOpportunityToAppear: successivePaymentValidityDisputeBar,
      });
    }

    if (successiveCarrierRecordType === 'successive_carrier_agreement_variation') {
      Object.assign(payload, {
        ...(successiveLinkedCarrierAgreementId ? { linkedCarrierAgreementId: successiveLinkedCarrierAgreementId } : {}),
        ...(successiveAgreementRecordedAt ? { agreementRecordedAt: successiveAgreementRecordedAt } : {}),
        ...(successiveAgreementDetail ? { agreementDetail: successiveAgreementDetail } : {}),
        carriersAgreedAlternativeAllocation: true,
        agreementLimitedToArticles37And38: true,
        article40AgreementRecorded: true,
      });
    }

    try {
      await recordSuccessiveCarrierInAPI(documentId, payload);
      setMessage('Successive carrier record stored without exposing private Articles 34-40 carrier, receipt, allocation, proceeding, or agreement details');
      setSuccessiveCarrierRecordType('successive_carrier_acceptance');
      setSuccessiveAllocationBasis('unknown_attribution_payment_share');
      setSuccessiveRecoveryStartBasis('actual_payment');
      setSuccessiveLinkedSuccessiveCarrierId('');
      setSuccessiveLinkedPreviousCarrierId('');
      setSuccessiveLinkedNextCarrierId('');
      setSuccessiveLinkedConsignmentNoteCopyId('');
      setSuccessiveLinkedReservationId('');
      setSuccessiveLinkedClaimId('');
      setSuccessiveLinkedCompensationPaymentId('');
      setSuccessiveLinkedCompensationAssessmentId('');
      setSuccessiveLinkedProceedingId('');
      setSuccessiveLinkedCarrierAgreementId('');
      setSuccessiveCarrierAcceptedAt('');
      setSuccessiveReceiptIssuedAt('');
      setSuccessiveCompensationPaidAt('');
      setSuccessiveFinalJudicialDecisionAt('');
      setSuccessiveRecoveryClaimFiledAt('');
      setSuccessiveAgreementRecordedAt('');
      setSuccessiveArticle8ReservationsEntered(true);
      setSuccessiveActionAgainstFirstCarrier(true);
      setSuccessiveActionAgainstLastCarrier(false);
      setSuccessiveActionAgainstEventCarrier(false);
      setSuccessiveCounterclaimOrSetoff(false);
      setSuccessiveMultipleCarriersJoined(false);
      setSuccessivePaymentValidityDisputeBar(false);
      setSuccessiveAllConcernedCarriersMayBeDefendants(true);
      setSuccessiveReceiptDetail('');
      setSuccessiveCarrierIdentityEntryDetail('');
      setSuccessiveReservationDetail('');
      setSuccessiveActionScopeDetail('');
      setSuccessiveAllocationDetail('');
      setSuccessiveInsolvencyDetail('');
      setSuccessiveProceedingDetail('');
      setSuccessiveAgreementDetail('');
      setSuccessiveNoticeOpportunityDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleProhibitedClause = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (!prohibitedClauseReviewedAt || (!prohibitedLinkedContractTermId && !prohibitedLinkedClauseReviewId && !prohibitedClauseDetail)) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: ProhibitedClauseRequest = {
      recordType: prohibitedClauseRecordType,
      clauseReviewedAt: prohibitedClauseReviewedAt,
      contractStipulationReviewed: true,
      clauseDetected: true,
      nullitySeverabilityRecorded: prohibitedNullitySeverabilityRecorded,
      reviewOutcomeRecorded: prohibitedReviewOutcomeRecorded,
      ...(prohibitedLinkedContractTermId ? { linkedContractTermId: prohibitedLinkedContractTermId } : {}),
      ...(prohibitedLinkedClauseReviewId ? { linkedClauseReviewId: prohibitedLinkedClauseReviewId } : {}),
      ...(prohibitedClauseDetail ? { clauseDetail: prohibitedClauseDetail } : {}),
      ...(prohibitedReviewDetail ? { reviewDetail: prohibitedReviewDetail } : {}),
    };

    if (prohibitedClauseRecordType === 'convention_derogation_clause') {
      Object.assign(payload, {
        ...(prohibitedLinkedArticle40AgreementId ? { linkedArticle40AgreementId: prohibitedLinkedArticle40AgreementId } : {}),
        directOrIndirectDerogationDetected: true,
        article40ExceptionAssessed: prohibitedArticle40ExceptionAssessed,
        article40ExceptionNotApplicable: prohibitedArticle40ExceptionNotApplicable,
        ...(prohibitedArticle40ExceptionDetail ? { article40ExceptionDetail: prohibitedArticle40ExceptionDetail } : {}),
      });
    }

    if (prohibitedClauseRecordType === 'insurance_benefit_to_carrier_clause') {
      Object.assign(payload, {
        ...(prohibitedLinkedInsuranceRecordId ? { linkedInsuranceRecordId: prohibitedLinkedInsuranceRecordId } : {}),
        benefitOfInsuranceForCarrierDetected: true,
        ...(prohibitedInsuranceBenefitDetail ? { insuranceBenefitDetail: prohibitedInsuranceBenefitDetail } : {}),
      });
    }

    if (prohibitedClauseRecordType === 'burden_of_proof_shift_clause') {
      Object.assign(payload, {
        ...(prohibitedLinkedClaimId ? { linkedClaimId: prohibitedLinkedClaimId } : {}),
        burdenOfProofShiftDetected: true,
        ...(prohibitedBurdenShiftDetail ? { burdenShiftDetail: prohibitedBurdenShiftDetail } : {}),
      });
    }

    try {
      await recordProhibitedClauseInAPI(documentId, payload);
      setMessage('Article 41 prohibited clause record stored without exposing private clause, review, insurance, burden, or Article 40 details');
      setProhibitedClauseRecordType('convention_derogation_clause');
      setProhibitedLinkedContractTermId('');
      setProhibitedLinkedClauseReviewId('');
      setProhibitedLinkedInsuranceRecordId('');
      setProhibitedLinkedClaimId('');
      setProhibitedLinkedArticle40AgreementId('');
      setProhibitedClauseReviewedAt('');
      setProhibitedArticle40ExceptionAssessed(true);
      setProhibitedArticle40ExceptionNotApplicable(true);
      setProhibitedNullitySeverabilityRecorded(true);
      setProhibitedReviewOutcomeRecorded(true);
      setProhibitedClauseDetail('');
      setProhibitedReviewDetail('');
      setProhibitedInsuranceBenefitDetail('');
      setProhibitedBurdenShiftDetail('');
      setProhibitedArticle40ExceptionDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleReplacementByOtherMeans = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      await recordReplacementByOtherMeansInAPI(documentId, {
        replacementMedium,
        reason: replacementReason,
        ...(replacementReplacedAt ? { replacedAt: replacementReplacedAt } : {}),
        ...(replacementExternalReference ? { externalReference: replacementExternalReference } : {}),
      });
      setMessage('Replacement evidence recorded without exposing private replacement details');
      setReplacementMedium('paper');
      setReplacementReason('');
      setReplacementReplacedAt('');
      setReplacementExternalReference('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleCarrierReservationAcknowledgement = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      await acknowledgeCarrierReservationInAPI(documentId, carrierAcknowledgementReservationId, {
        decision: carrierAcknowledgementDecision,
        ...(carrierAcknowledgementComment ? { comment: carrierAcknowledgementComment } : {}),
      });
      setMessage('Carrier reservation acknowledgement recorded without exposing private reservation details');
      setCarrierAcknowledgementReservationId('');
      setCarrierAcknowledgementDecision('acknowledged');
      setCarrierAcknowledgementComment('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDisposalInstruction = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const conditionsSatisfied =
      disposalRightNotCeased &&
      disposalFirstCopyProduced &&
      disposalCarrierIndemnityAccepted &&
      disposalInstructionFeasible &&
      disposalDoesNotPrejudiceOtherConsignments &&
      disposalDoesNotDivideConsignment;

    if (
      !conditionsSatisfied ||
      !disposalLinkedConsignmentNoteCopyRecordId.trim() ||
      (disposalInstructionType === 'change_delivery_place' && !disposalNewDeliveryPlace.trim()) ||
      (disposalInstructionType === 'change_consignee' && !disposalNewConsignee.trim())
    ) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: DisposalInstructionRequest = {
      instructionType: disposalInstructionType,
      rightOfDisposalNotCeased: true,
      firstCopyInstructionProduced: true,
      carrierIndemnityAccepted: true,
      instructionFeasible: true,
      doesNotPrejudiceOtherConsignments: true,
      doesNotDivideConsignment: true,
      linkedConsignmentNoteCopyRecordId: disposalLinkedConsignmentNoteCopyRecordId.trim(),
      ...(disposalRequestedExecutionAt ? { requestedExecutionAt: disposalRequestedExecutionAt } : {}),
      ...(disposalReason ? { reason: disposalReason } : {}),
      ...(disposalInstructionType === 'change_delivery_place' && disposalNewDeliveryPlace ? { newDeliveryPlace: disposalNewDeliveryPlace } : {}),
      ...(disposalInstructionType === 'change_consignee' && disposalNewConsignee ? { newConsignee: disposalNewConsignee } : {}),
      ...(disposalInstructionType === 'change_consignee' && disposalSuccessiveConsigneeInstructionFinal ? { successiveConsigneeInstructionFinal: true } : {}),
    };

    try {
      await recordDisposalInstructionInAPI(documentId, payload);
      setMessage('Disposal instruction recorded without exposing private Article 12 details');
      setDisposalInstructionType('stop_in_transit');
      setDisposalRightNotCeased(true);
      setDisposalFirstCopyProduced(true);
      setDisposalCarrierIndemnityAccepted(true);
      setDisposalInstructionFeasible(true);
      setDisposalDoesNotPrejudiceOtherConsignments(true);
      setDisposalDoesNotDivideConsignment(true);
      setDisposalSuccessiveConsigneeInstructionFinal(false);
      setDisposalLinkedConsignmentNoteCopyRecordId('');
      setDisposalRequestedExecutionAt('');
      setDisposalReason('');
      setDisposalNewDeliveryPlace('');
      setDisposalNewConsignee('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDisposalExecutionNotice = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    if (!disposalNoticeInstructionId.trim() || !disposalNoticeSentAt.trim()) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    try {
      await recordDisposalExecutionNoticeInAPI(documentId, disposalNoticeInstructionId, {
        noticeType: 'unable_to_execute',
        unableToExecute: true,
        notificationSent: true,
        notificationSentAt: disposalNoticeSentAt,
        notificationTargetRole: disposalNoticeTargetRole,
        ...(disposalNoticeReason ? { reason: disposalNoticeReason } : {}),
        ...(disposalNoticeDetail ? { notificationDetail: disposalNoticeDetail } : {}),
      });
      setMessage('Disposal execution notice recorded without exposing private Article 12.6 details');
      setDisposalNoticeInstructionId('');
      setDisposalNoticeSentAt('');
      setDisposalNoticeTargetRole('consignor');
      setDisposalNoticeReason('');
      setDisposalNoticeDetail('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleConsigneeDeliveryRequest = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();

    const isDeliveryAfterArrival = consigneeDeliveryRequestType === 'delivery_after_arrival';
    const isDeliveryReceipt = consigneeDeliveryRequestType === 'delivery_receipt_and_second_copy_handover';
    const normalizedConsigneeChargeLines = consigneeChargesPaidLines.map((line) => ({
      amount: line.amount.trim(),
      currency: line.currency.trim().toUpperCase(),
    }));
    const consigneeChargeCurrencies = new Set(normalizedConsigneeChargeLines.map((line) => line.currency));
    const invalidConsigneeChargeLine = normalizedConsigneeChargeLines.some((line) => {
      const amount = Number(line.amount);
      return !line.amount || !Number.isFinite(amount) || amount <= 0 || !/^[A-Z]{3}$/.test(line.currency);
    });
    if (isDeliveryAfterArrival) {
      if (
        !consigneeArrivalAtDeliveryPlace ||
        !consigneeDeliveryRequested ||
        !consigneeSecondCopyRequested ||
        !consigneeReceiptCommitment ||
        (consigneeChargesPaymentAcknowledged && consigneeChargesDisputed) ||
        (!consigneeChargesPaymentAcknowledged && !(consigneeChargesDisputed && consigneeSecurityFurnished)) ||
        (consigneeChargesPaymentAcknowledged && (
          invalidConsigneeChargeLine ||
          consigneeChargeCurrencies.size !== normalizedConsigneeChargeLines.length ||
          !consigneeChargesPaidAt ||
          !consigneeChargesPaymentEvidenceAttachmentId.trim()
        )) ||
        (consigneeChargesDisputed && (
          !consigneeSecurityFurnishedAt ||
          !consigneeChargesSecurityEvidenceAttachmentId.trim()
        ))
      ) {
        setError(sanitizedOperationalErrorMessage);
        return;
      }
    } else if (isDeliveryReceipt) {
      if (
        !consigneeLinkedDeliveryId.trim() ||
        !consigneeLinkedSecondCopyRecordId.trim() ||
        !consigneeGoodsReceived ||
        !consigneeSecondCopyReceived ||
        !consigneeReceiptIssued
      ) {
        setError(sanitizedOperationalErrorMessage);
        return;
      }
    } else if (!consigneeRightsReservedNotice || (!consigneeLossEstablished && !consigneeArticle19DelayPeriodExpired)) {
      setError(sanitizedOperationalErrorMessage);
      return;
    }

    const payload: ConsigneeDeliveryRequestRequest = isDeliveryAfterArrival
      ? {
          requestType: consigneeDeliveryRequestType,
          arrivalAtDeliveryPlace: consigneeArrivalAtDeliveryPlace,
          deliveryRequested: consigneeDeliveryRequested,
          secondCopyRequested: consigneeSecondCopyRequested,
          receiptCommitment: consigneeReceiptCommitment,
          chargesPaymentAcknowledged: consigneeChargesPaymentAcknowledged,
          chargesDisputed: consigneeChargesDisputed,
          securityFurnished: consigneeSecurityFurnished,
          ...(consigneeChargesPaymentAcknowledged ? {
            chargesPaidAmounts: normalizedConsigneeChargeLines,
            chargesPaidAt: consigneeChargesPaidAt,
            linkedChargesPaymentEvidenceAttachmentId: consigneeChargesPaymentEvidenceAttachmentId.trim(),
          } : {
            securityFurnishedAt: consigneeSecurityFurnishedAt,
            linkedChargesSecurityEvidenceAttachmentId: consigneeChargesSecurityEvidenceAttachmentId.trim(),
          }),
          ...(consigneeReceiptDetail ? { receiptDetail: consigneeReceiptDetail } : {}),
          ...(consigneeChargesDisputed && consigneeSecurityFurnished && consigneeChargesSecurityDetail
            ? { chargesSecurityDetail: consigneeChargesSecurityDetail }
            : {}),
          ...(consigneeReason ? { reason: consigneeReason } : {}),
        }
      : isDeliveryReceipt
      ? {
          requestType: consigneeDeliveryRequestType,
          linkedDeliveryId: consigneeLinkedDeliveryId.trim(),
          linkedConsignmentNoteCopyRecordId: consigneeLinkedSecondCopyRecordId.trim(),
          goodsReceived: true,
          secondCopyReceived: true,
          receiptIssued: true,
          ...(consigneeReceiptDetail ? { receiptDetail: consigneeReceiptDetail } : {}),
          ...(consigneeReason ? { reason: consigneeReason } : {}),
        }
      : {
          requestType: consigneeDeliveryRequestType,
          rightsReservedNotice: consigneeRightsReservedNotice,
          lossEstablished: consigneeLossEstablished,
          article19DelayPeriodExpired: consigneeArticle19DelayPeriodExpired,
          ...(consigneeEnforcementBasisDetail ? { enforcementBasisDetail: consigneeEnforcementBasisDetail } : {}),
          ...(consigneeReason ? { reason: consigneeReason } : {}),
        };

    try {
      await recordConsigneeDeliveryRequestInAPI(documentId, payload);
      setMessage('Consignee delivery request recorded without exposing private Article 13 details');
      setConsigneeDeliveryRequestType('delivery_after_arrival');
      setConsigneeArrivalAtDeliveryPlace(true);
      setConsigneeDeliveryRequested(true);
      setConsigneeSecondCopyRequested(true);
      setConsigneeReceiptCommitment(true);
      setConsigneeChargesPaymentAcknowledged(true);
      setConsigneeChargesDisputed(false);
      setConsigneeSecurityFurnished(false);
      setConsigneeChargesPaidLines([{ amount: '', currency: 'EUR' }]);
      setConsigneeChargesPaidAt('');
      setConsigneeChargesPaymentEvidenceAttachmentId('');
      setConsigneeSecurityFurnishedAt('');
      setConsigneeChargesSecurityEvidenceAttachmentId('');
      setConsigneeRightsReservedNotice(false);
      setConsigneeLossEstablished(false);
      setConsigneeArticle19DelayPeriodExpired(false);
      setConsigneeLinkedDeliveryId('');
      setConsigneeLinkedSecondCopyRecordId('');
      setConsigneeGoodsReceived(false);
      setConsigneeSecondCopyReceived(false);
      setConsigneeReceiptIssued(false);
      setConsigneeReceiptDetail('');
      setConsigneeChargesSecurityDetail('');
      setConsigneeEnforcementBasisDetail('');
      setConsigneeReason('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDeliveryConfirmation = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      const deliveryReservations = deliveryReservationGrounds
        ? [{
            category: deliveryReservationCategory,
            grounds: deliveryReservationGrounds,
            ...(deliveryPackageReference ? { packageReference: deliveryPackageReference } : {}),
          }]
        : [];
      await confirmDeliveryInAPI(documentId, {
        deliveredAt: deliveryDeliveredAt,
        recipientName: deliveryRecipientName,
        deliveryLocation: {
          name: deliveryLocationName,
          city: deliveryCity,
          country: deliveryCountry,
        },
        signatureMethod: 'simple-electronic-attestation',
        ...(deliveryReservations.length > 0 ? { deliveryReservations } : {}),
      });
      setMessage('Delivery confirmation recorded without exposing private proof details');
      setDeliveryDeliveredAt('');
      setDeliveryRecipientName('');
      setDeliveryLocationName('');
      setDeliveryCity('');
      setDeliveryCountry('');
      setDeliveryReservationGrounds('');
      setDeliveryPackageReference('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDeliveryReservationAcknowledgement = async (event: FormEvent) => {
    event.preventDefault();
    resetFeedback();
    try {
      await acknowledgeDeliveryReservationInAPI(
        documentId,
        deliveryAcknowledgementDeliveryId,
        deliveryAcknowledgementReservationId,
        {
          decision: deliveryAcknowledgementDecision,
          ...(deliveryAcknowledgementComment ? { comment: deliveryAcknowledgementComment } : {}),
        }
      );
      setMessage('Delivery reservation acknowledgement recorded without exposing private delivery details');
      setDeliveryAcknowledgementDeliveryId('');
      setDeliveryAcknowledgementReservationId('');
      setDeliveryAcknowledgementDecision('acknowledged');
      setDeliveryAcknowledgementComment('');
    } catch (submitError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleSupplementaryUpload = async (event: FormEvent) => {
    event.preventDefault();
    if (!attachmentFile) {
      setError('Select an attachment file before upload');
      return;
    }

    resetFeedback();
    try {
      await uploadSupplementaryDocumentInAPI(documentId, {
        fileName: attachmentFile.name,
        mimeType: attachmentFile.type || 'application/octet-stream',
        contentBase64: await readFileAsBase64(attachmentFile),
        ...(attachmentType ? { documentType: attachmentType } : {}),
        ...(attachmentDescription ? { description: attachmentDescription } : {}),
      });
      setMessage('Supplementary document uploaded without exposing private attachment identifiers');
      setAttachmentFile(null);
      setAttachmentType('');
      setAttachmentDescription('');
      await loadAttachments();
    } catch (uploadError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  const handleDownload = async (attachment: SupplementaryDocumentEvidence) => {
    resetFeedback();
    try {
      const result = await getSupplementaryDocumentDownloadUrlInAPI(documentId, attachment.attachmentId);
      window.open(result.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (downloadError) {
      setError(sanitizedOperationalErrorMessage);
    }
  };

  if (!documentId) {
    return (
      <div className="container mx-auto p-4">
        <p className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4">Missing API document id.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">Operational Evidence</h1>
          <p className="text-sm text-gray-600">Technical workflow baseline for API document {documentId}.</p>
        </div>
        <Link to="/" className="text-blue-600 hover:text-blue-900 underline">Back to documents</Link>
      </div>

      {message && <p className="mb-4 rounded border-l-4 border-green-500 bg-green-100 p-4 text-green-700">{message}</p>}
      {error && <p className="mb-4 rounded border-l-4 border-red-500 bg-red-100 p-4 text-red-700">{error}</p>}

      <div className="grid gap-4 lg:grid-cols-2">
        <form onSubmit={handleCarrierReservation} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carrier Reservations</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierReservationCategory">
            Reservation category
            <select
              id="carrierReservationCategory"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierCategory}
              onChange={(event) => setCarrierCategory(event.target.value as CarrierReservationCategory)}
            >
              {carrierCategories.map((category) => (
                <option key={category.value} value={category.value}>{category.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierReservationGrounds">
            Reservation grounds
            <textarea
              id="carrierReservationGrounds"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierGrounds}
              onChange={(event) => setCarrierGrounds(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierPackageReference">
            Package reference
            <input
              id="carrierPackageReference"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierPackageReference}
              onChange={(event) => setCarrierPackageReference(event.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="carrierObservedValue">
              Observed value
              <input
                id="carrierObservedValue"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carrierObservedValue}
                onChange={(event) => setCarrierObservedValue(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="carrierExpectedValue">
              Expected value
              <input
                id="carrierExpectedValue"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carrierExpectedValue}
                onChange={(event) => setCarrierExpectedValue(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carrier reservation
          </button>
        </form>

        <form onSubmit={handleCarrierReservationAcknowledgement} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carrier Reservation Acknowledgement</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierAcknowledgementReservationId">
            Reservation reference
            <input
              id="carrierAcknowledgementReservationId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierAcknowledgementReservationId}
              onChange={(event) => setCarrierAcknowledgementReservationId(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierAcknowledgementDecision">
            Acknowledgement decision
            <select
              id="carrierAcknowledgementDecision"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierAcknowledgementDecision}
              onChange={(event) => setCarrierAcknowledgementDecision(event.target.value as CarrierReservationAcknowledgementDecision)}
            >
              <option value="acknowledged">Acknowledge</option>
              <option value="rejected">Reject</option>
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierAcknowledgementComment">
            Acknowledgement comment
            <textarea
              id="carrierAcknowledgementComment"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierAcknowledgementComment}
              onChange={(event) => setCarrierAcknowledgementComment(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carrier reservation acknowledgement
          </button>
        </form>

        <form onSubmit={handleCarrierCheckRequest} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Sender Requested Carrier Checks</h2>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Requested checks</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {requestedCarrierChecks.map((checkType) => (
                <label key={checkType.value} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={senderRequestedChecks.includes(checkType.value)}
                    onChange={() => toggleSenderRequestedCheck(checkType.value)}
                  />
                  {checkType.label}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="senderCheckReason">
            Request reason
            <textarea
              id="senderCheckReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={senderCheckReason}
              onChange={(event) => setSenderCheckReason(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Request carrier checks
          </button>
        </form>

        <form onSubmit={handleCarrierTakeoverCheck} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carrier Takeover Check</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="takeoverResult">
            Takeover result
            <select
              id="takeoverResult"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={takeoverResult}
              onChange={(event) => setTakeoverResult(event.target.value as CarrierTakeoverCheckResult)}
            >
              {carrierTakeoverResults.map((result) => (
                <option key={result.value} value={result.value}>{result.label}</option>
              ))}
            </select>
          </label>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Baseline checks</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={packageCountChecked}
                  onChange={(event) => setPackageCountChecked(event.target.checked)}
                />
                Package count
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={marksAndNumbersChecked}
                  onChange={(event) => setMarksAndNumbersChecked(event.target.checked)}
                />
                Marks and numbers
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={apparentConditionChecked}
                  onChange={(event) => setApparentConditionChecked(event.target.checked)}
                />
                Apparent condition
              </label>
            </div>
          </fieldset>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Requested check results</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={grossWeightChecked}
                  onChange={(event) => setGrossWeightChecked(event.target.checked)}
                />
                Gross weight
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={quantityChecked}
                  onChange={(event) => setQuantityChecked(event.target.checked)}
                />
                Quantity
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={contentsChecked}
                  onChange={(event) => setContentsChecked(event.target.checked)}
                />
                Contents
              </label>
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="takeoverReservationId">
            Related reservation reference
            <input
              id="takeoverReservationId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={takeoverReservationId}
              onChange={(event) => setTakeoverReservationId(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="takeoverComment">
            Takeover comment
            <textarea
              id="takeoverComment"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={takeoverComment}
              onChange={(event) => setTakeoverComment(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carrier takeover check
          </button>
        </form>

        <form onSubmit={handleCarriageImpediment} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carriage Impediment</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageImpedimentType">
            Impediment type
            <select
              id="carriageImpedimentType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageImpedimentType}
              onChange={(event) => handleCarriageImpedimentTypeChange(event.target.value as CarriageImpedimentType)}
            >
              {carriageImpedimentTypes.map((impedimentType) => (
                <option key={impedimentType.value} value={impedimentType.value}>{impedimentType.label}</option>
              ))}
            </select>
          </label>
          {carriageInstructionRequestTypes.has(carriageImpedimentType) && <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Instruction request</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  required
                  checked={carriageInstructionsRequested}
                  onChange={(event) => setCarriageInstructionsRequested(event.target.checked)}
                />
                Instructions requested
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageInstructionTargetRole">
                Instruction target
                <select
                  id="carriageInstructionTargetRole"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageInstructionTargetRole}
                  onChange={(event) => setCarriageInstructionTargetRole(event.target.value as CarriageImpedimentInstructionTargetRole)}
                >
                  {carriageInstructionTargets.map((target) => (
                    <option key={target.value} value={target.value}>{target.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carriageInstructionRequestSentAt">
                Instruction request sent at
                <input
                  id="carriageInstructionRequestSentAt"
                  type="datetime-local"
                  required
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageInstructionRequestSentAt}
                  onChange={(event) => setCarriageInstructionRequestSentAt(event.target.value)}
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageInstructionRequestReceivedAt">
                Instruction request received at
                <input
                  id="carriageInstructionRequestReceivedAt"
                  type="datetime-local"
                  required
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageInstructionRequestReceivedAt}
                  onChange={(event) => setCarriageInstructionRequestReceivedAt(event.target.value)}
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageInstructionRequestEvidenceAttachmentId">
                Instruction request evidence attachment ID
                <input
                  id="carriageInstructionRequestEvidenceAttachmentId"
                  required
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageInstructionRequestEvidenceAttachmentId}
                  onChange={(event) => setCarriageInstructionRequestEvidenceAttachmentId(event.target.value)}
                />
              </label>
            </div>
          </fieldset>}
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Impediment and disposition flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageInstructionsUnavailableInReasonableTime}
                  onChange={(event) => setCarriageInstructionsUnavailableInReasonableTime(event.target.checked)}
                />
                Instructions unavailable in reasonable time
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageBestInterestsActionTaken}
                  onChange={(event) => setCarriageBestInterestsActionTaken(event.target.checked)}
                />
                Best interests action taken
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageArrivalAtDeliveryPlace}
                  onChange={(event) => setCarriageArrivalAtDeliveryPlace(event.target.checked)}
                />
                Arrival at delivery place
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageConsigneeRefusedGoods}
                  onChange={(event) => setCarriageConsigneeRefusedGoods(event.target.checked)}
                />
                Consignee refused goods
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageArticle14Or15ImpedimentBasis}
                  onChange={(event) => setCarriageArticle14Or15ImpedimentBasis(event.target.checked)}
                />
                Article 14 or 15 basis
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageGoodsUnloadedForAccount}
                  onChange={(event) => setCarriageGoodsUnloadedForAccount(event.target.checked)}
                />
                Goods unloaded for account
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageGoodsHeldForEntitledPerson}
                  onChange={(event) => setCarriageGoodsHeldForEntitledPerson(event.target.checked)}
                />
                Goods held for entitled person
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageThirdPartyCustodianUsed}
                  onChange={(event) => setCarriageThirdPartyCustodianUsed(event.target.checked)}
                />
                Third-party custodian used
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={carriageThirdPartyCustodianSelectedWithReasonableCare}
                  onChange={(event) => setCarriageThirdPartyCustodianSelectedWithReasonableCare(event.target.checked)}
                />
                Third-party custodian selected with reasonable care
              </label>
            </div>
          </fieldset>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Sale and expense flags</legend>
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageSaleReason">
              Sale reason
              <select
                id="carriageSaleReason"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carriageSaleReason}
                onChange={(event) => setCarriageSaleReason(event.target.value as CarriageImpedimentSaleReason)}
              >
                {carriageSaleReasons.map((saleReason) => (
                  <option key={saleReason.value} value={saleReason.value}>{saleReason.label}</option>
                ))}
              </select>
            </label>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageProceedsPlacedAtDisposal}
                  onChange={(event) => setCarriageProceedsPlacedAtDisposal(event.target.checked)}
                />
                Proceeds placed at disposal
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageSaleProcedureLawOrCustomAcknowledged}
                  onChange={(event) => setCarriageSaleProcedureLawOrCustomAcknowledged(event.target.checked)}
                />
                Sale law or custom acknowledged
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carriageExpensesRecoverableClaimed}
                  onChange={(event) => setCarriageExpensesRecoverableClaimed(event.target.checked)}
                />
                Expenses recoverable claimed
              </label>
            </div>
          </fieldset>
          {carriageImpedimentType === 'sale_of_goods' && <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Sale procedure basis</legend>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageGoodsLocationCountryCode">
                Goods location country code
                <input
                  id="carriageGoodsLocationCountryCode"
                  className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                  value={carriageGoodsLocationCountryCode}
                  onChange={(event) => setCarriageGoodsLocationCountryCode(event.target.value)}
                  maxLength={2}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageSaleProcedureBasis">
                Sale procedure basis
                <select
                  id="carriageSaleProcedureBasis"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageSaleProcedureBasis}
                  onChange={(event) => setCarriageSaleProcedureBasis(event.target.value as CarriageImpedimentSaleProcedureBasis)}
                  required
                >
                  {carriageSaleProcedureBases.map((basis) => (
                    <option key={basis.value} value={basis.value}>{basis.label}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carriageGoodsLocationDetail">
                Goods location detail
                <textarea
                  id="carriageGoodsLocationDetail"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageGoodsLocationDetail}
                  onChange={(event) => setCarriageGoodsLocationDetail(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carriageSaleProcedureReference">
                Sale procedure reference
                <textarea
                  id="carriageSaleProcedureReference"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageSaleProcedureReference}
                  onChange={(event) => setCarriageSaleProcedureReference(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageSaleProcedureAssessedAt">
                Sale procedure assessed at
                <input
                  id="carriageSaleProcedureAssessedAt"
                  type="datetime-local"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageSaleProcedureAssessedAt}
                  onChange={(event) => setCarriageSaleProcedureAssessedAt(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageSaleProcedureEvidenceAttachmentId">
                Sale procedure evidence attachment ID
                <input
                  id="carriageSaleProcedureEvidenceAttachmentId"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageSaleProcedureEvidenceAttachmentId}
                  onChange={(event) => setCarriageSaleProcedureEvidenceAttachmentId(event.target.value)}
                  required
                />
              </label>
            </div>
          </fieldset>}
          {carriageInstructionRequestTypes.has(carriageImpedimentType) && <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageInstructionRequestDetail">
            Instruction request detail
            <textarea
              id="carriageInstructionRequestDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageInstructionRequestDetail}
              onChange={(event) => setCarriageInstructionRequestDetail(event.target.value)}
            />
          </label>}
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageAlternativeCarriageConditions">
            Alternative carriage conditions
            <textarea
              id="carriageAlternativeCarriageConditions"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAlternativeCarriageConditions}
              onChange={(event) => setCarriageAlternativeCarriageConditions(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageStorageOrSaleDetail">
            Storage or sale detail
            <textarea
              id="carriageStorageOrSaleDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageStorageOrSaleDetail}
              onChange={(event) => setCarriageStorageOrSaleDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="carriageReason">
            Impediment reason
            <textarea
              id="carriageReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageReason}
              onChange={(event) => setCarriageReason(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carriage impediment
          </button>
        </form>

        <form onSubmit={handleCarriageAccounting} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carriage Accounting</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageAccountingImpedimentId">
            Related impediment reference
            <input
              id="carriageAccountingImpedimentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAccountingImpedimentId}
              onChange={(event) => setCarriageAccountingImpedimentId(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageAccountingType">
            Accounting type
            <select
              id="carriageAccountingType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAccountingType}
              onChange={(event) => {
                setCarriageAccountingType(event.target.value as CarriageAccountingType);
                setCarriageAccountingCarrierNotAtFault(false);
                setCarriageAccountingChargeableAgainstGoods(false);
                setCarriageAccountingProceedsPlaced(false);
                setCarriageAccountingChargesExceedProceeds(false);
                setCarriageAccountingAmount('');
                setCarriageAccountingGrossSaleProceeds('');
                setCarriageAccountingDeductedSaleExpenses('');
                setCarriageAccountingSaleProceedsReference('');
                setCarriageAccountingTotalChargesDue('');
              }}
            >
              {carriageAccountingTypes.map((accountingType) => (
                <option key={accountingType.value} value={accountingType.value}>{accountingType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingAmount">
              Amount
              <input
                id="carriageAccountingAmount"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carriageAccountingAmount}
                onChange={(event) => setCarriageAccountingAmount(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingCurrency">
              Currency
              <input
                id="carriageAccountingCurrency"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carriageAccountingCurrency}
                onChange={(event) => setCarriageAccountingCurrency(event.target.value)}
                maxLength={3}
              />
            </label>
          </div>
          {carriageAccountingType === 'sale_proceeds' && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingGrossSaleProceeds">
                Gross sale proceeds
                <input
                  id="carriageAccountingGrossSaleProceeds"
                  inputMode="decimal"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageAccountingGrossSaleProceeds}
                  onChange={(event) => setCarriageAccountingGrossSaleProceeds(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingDeductedSaleExpenses">
                Deducted sale expenses
                <input
                  id="carriageAccountingDeductedSaleExpenses"
                  inputMode="decimal"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageAccountingDeductedSaleExpenses}
                  onChange={(event) => setCarriageAccountingDeductedSaleExpenses(event.target.value)}
                  required
                />
              </label>
            </div>
          )}
          {carriageAccountingType === 'charge_shortfall' && (
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingSaleProceedsReference">
                Sale proceeds accounting reference
                <input
                  id="carriageAccountingSaleProceedsReference"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageAccountingSaleProceedsReference}
                  onChange={(event) => setCarriageAccountingSaleProceedsReference(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingTotalChargesDue">
                Total charges due
                <input
                  id="carriageAccountingTotalChargesDue"
                  inputMode="decimal"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carriageAccountingTotalChargesDue}
                  onChange={(event) => setCarriageAccountingTotalChargesDue(event.target.value)}
                  required
                />
              </label>
            </div>
          )}
          <label className="mb-3 mt-3 block text-sm font-bold text-gray-700" htmlFor="carriageAccountingAt">
            Accounting at
            <input
              id="carriageAccountingAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAccountingAt}
              onChange={(event) => setCarriageAccountingAt(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carriageAccountingEvidenceAttachmentId">
            Accounting evidence attachment ID
            <input
              id="carriageAccountingEvidenceAttachmentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAccountingEvidenceAttachmentId}
              onChange={(event) => setCarriageAccountingEvidenceAttachmentId(event.target.value)}
              required
            />
          </label>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 16 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {(carriageAccountingType === 'instruction_expense'
                || carriageAccountingType === 'goods_disposition_expense'
                || carriageAccountingType === 'charge_shortfall') && (
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carriageAccountingCarrierNotAtFault}
                    onChange={(event) => setCarriageAccountingCarrierNotAtFault(event.target.checked)}
                  />
                  Carrier fault not asserted
                </label>
              )}
              {carriageAccountingType === 'goods_disposition_expense' && (
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carriageAccountingChargeableAgainstGoods}
                    onChange={(event) => setCarriageAccountingChargeableAgainstGoods(event.target.checked)}
                  />
                  Chargeable against goods
                </label>
              )}
              {carriageAccountingType === 'sale_proceeds' && (
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carriageAccountingProceedsPlaced}
                    onChange={(event) => setCarriageAccountingProceedsPlaced(event.target.checked)}
                  />
                  Proceeds placed at disposal
                </label>
              )}
              {carriageAccountingType === 'charge_shortfall' && (
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carriageAccountingChargesExceedProceeds}
                    onChange={(event) => setCarriageAccountingChargesExceedProceeds(event.target.checked)}
                  />
                  Charges exceed proceeds
                </label>
              )}
            </div>
          </fieldset>
          <label className="block text-sm font-bold text-gray-700" htmlFor="carriageAccountingDetail">
            Accounting detail
            <textarea
              id="carriageAccountingDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carriageAccountingDetail}
              onChange={(event) => setCarriageAccountingDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carriage accounting
          </button>
        </form>

        <form onSubmit={handleCarrierLiabilityIncident} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Carrier Liability Incident</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityIncidentType">
            Incident type
            <select
              id="carrierLiabilityIncidentType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityIncidentType}
              onChange={(event) => {
                const nextType = event.target.value as CarrierLiabilityIncidentType;
                setCarrierLiabilityIncidentType(nextType);
                if (nextType === 'article12_disposal_instruction_noncompliance') {
                  setCarrierLiabilityTakingOverAt('');
                  setCarrierLiabilityDeliveryAt('');
                  setCarrierLiabilityIncidentEvidenceAttachmentId('');
                  setCarrierLiabilityCarrierProofEvidenceAttachmentId('');
                  setCarrierLiabilityClaimantFault(false);
                  setCarrierLiabilityClaimantInstruction(false);
                  setCarrierLiabilityInherentVice(false);
                  setCarrierLiabilityUnavoidableCircumstances(false);
                  setCarrierLiabilitySpecialRiskClaimed(false);
                  setCarrierLiabilityArticle17_4aOpenVehicleUsed(false);
                  setCarrierLiabilityArticle17_4aAssessmentDetail('');
                  setCarrierLiabilityArticle17_4aEvidenceAttachmentId('');
                  setCarrierLiabilityArticle17_4cHandlingPerformed(false);
                  setCarrierLiabilityArticle17_4cLoadingPerformed(false);
                  setCarrierLiabilityArticle17_4cStowagePerformed(false);
                  setCarrierLiabilityArticle17_4cUnloadingPerformed(false);
                  setCarrierLiabilityArticle17_4cActorCapacity('consignor');
                  setCarrierLiabilityArticle17_4cAssessmentDetail('');
                  setCarrierLiabilityArticle17_4cEvidenceAttachmentId('');
                  setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(false);
                  setCarrierLiabilityArticle17_4dSusceptibilityBasis('breakage');
                  setCarrierLiabilityArticle17_4dAssessmentDetail('');
                  setCarrierLiabilityArticle17_4dEvidenceAttachmentId('');
                  setCarrierLiabilityArticle17_4ePackageMarksDeficient(false);
                  setCarrierLiabilityArticle17_4ePackageNumbersDeficient(false);
                  setCarrierLiabilityArticle17_4eDeficiencyBasis('insufficiency');
                  setCarrierLiabilityArticle17_4eAssessmentDetail('');
                  setCarrierLiabilityArticle17_4eEvidenceAttachmentId('');
                  setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(false);
                  setCarrierLiabilityArticle17_4fAssessmentDetail('');
                  setCarrierLiabilityArticle17_4fEvidenceAttachmentId('');
                  setCarrierLiabilitySpecialRiskPresumption(false);
                  setCarrierLiabilityArticle18_2CircumstancesEstablished(false);
                  setCarrierLiabilityTemperatureControlled(false);
                  setCarrierLiabilityEquipmentStepsEvidence(false);
                  setCarrierLiabilityLivestockCareEvidence(false);
                  setCarrierLiabilityArticle18_4ChoiceSteps(false);
                  setCarrierLiabilityArticle18_4MaintenanceSteps(false);
                  setCarrierLiabilityArticle18_4UseSteps(false);
                  setCarrierLiabilityArticle18_4InstructionsIssued(false);
                  setCarrierLiabilityArticle18_4InstructionsComplied(false);
                  setCarrierLiabilityArticle18_4AssessmentDetail('');
                  setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
                  setCarrierLiabilityArticle18_5NormallyIncumbentSteps(false);
                  setCarrierLiabilityArticle18_5InstructionsIssued(false);
                  setCarrierLiabilityArticle18_5InstructionsComplied(false);
                  setCarrierLiabilityArticle18_5AssessmentDetail('');
                  setCarrierLiabilityArticle18_5EvidenceAttachmentId('');
                  setCarrierLiabilityAbnormalShortageObserved(false);
                  setCarrierLiabilityAnyPackageLost(false);
                  setCarrierLiabilityCarrierProofDetail('');
                } else {
                  setCarrierLiabilityDisposalInstructionId('');
                  setCarrierLiabilityInstructionNotCarriedOut(false);
                  setCarrierLiabilityInstructionWithoutFirstCopy(false);
                  setCarrierLiabilityClaimantEntitlement(false);
                  setCarrierLiabilityLossOrDamageCaused(false);
                  if (nextType === 'delay') {
                    setCarrierLiabilitySpecialRiskClaimed(false);
                    setCarrierLiabilityArticle17_4aOpenVehicleUsed(false);
                    setCarrierLiabilityArticle17_4aAssessmentDetail('');
                    setCarrierLiabilityArticle17_4aEvidenceAttachmentId('');
                    setCarrierLiabilityArticle17_4cHandlingPerformed(false);
                    setCarrierLiabilityArticle17_4cLoadingPerformed(false);
                    setCarrierLiabilityArticle17_4cStowagePerformed(false);
                    setCarrierLiabilityArticle17_4cUnloadingPerformed(false);
                    setCarrierLiabilityArticle17_4cActorCapacity('consignor');
                    setCarrierLiabilityArticle17_4cAssessmentDetail('');
                    setCarrierLiabilityArticle17_4cEvidenceAttachmentId('');
                    setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(false);
                    setCarrierLiabilityArticle17_4dSusceptibilityBasis('breakage');
                    setCarrierLiabilityArticle17_4dAssessmentDetail('');
                    setCarrierLiabilityArticle17_4dEvidenceAttachmentId('');
                    setCarrierLiabilityArticle17_4ePackageMarksDeficient(false);
                    setCarrierLiabilityArticle17_4ePackageNumbersDeficient(false);
                    setCarrierLiabilityArticle17_4eDeficiencyBasis('insufficiency');
                    setCarrierLiabilityArticle17_4eAssessmentDetail('');
                    setCarrierLiabilityArticle17_4eEvidenceAttachmentId('');
                    setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(false);
                    setCarrierLiabilityArticle17_4fAssessmentDetail('');
                    setCarrierLiabilityArticle17_4fEvidenceAttachmentId('');
                    setCarrierLiabilitySpecialRiskPresumption(false);
                    setCarrierLiabilityArticle18_2CircumstancesEstablished(false);
                    setCarrierLiabilityTemperatureControlled(false);
                    setCarrierLiabilityEquipmentStepsEvidence(false);
                    setCarrierLiabilityLivestockCareEvidence(false);
                    setCarrierLiabilityArticle18_4ChoiceSteps(false);
                    setCarrierLiabilityArticle18_4MaintenanceSteps(false);
                    setCarrierLiabilityArticle18_4UseSteps(false);
                    setCarrierLiabilityArticle18_4InstructionsIssued(false);
                    setCarrierLiabilityArticle18_4InstructionsComplied(false);
                    setCarrierLiabilityArticle18_4AssessmentDetail('');
                    setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
                    setCarrierLiabilityArticle18_5NormallyIncumbentSteps(false);
                    setCarrierLiabilityArticle18_5InstructionsIssued(false);
                    setCarrierLiabilityArticle18_5InstructionsComplied(false);
                    setCarrierLiabilityArticle18_5AssessmentDetail('');
                    setCarrierLiabilityArticle18_5EvidenceAttachmentId('');
                    setCarrierLiabilityAbnormalShortageObserved(false);
                    setCarrierLiabilityAnyPackageLost(false);
                  }
                }
              }}
            >
              {carrierLiabilityIncidentTypes.map((incidentType) => (
                <option key={incidentType.value} value={incidentType.value}>{incidentType.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityIncidentAt">
            Incident at
            <input
              id="carrierLiabilityIncidentAt"
              type="datetime-local"
              required={carrierLiabilityIncidentType !== 'article12_disposal_instruction_noncompliance'}
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityIncidentAt}
              onChange={(event) => setCarrierLiabilityIncidentAt(event.target.value)}
            />
          </label>
          {carrierLiabilityIncidentType === 'article12_disposal_instruction_noncompliance' ? (
            <>
              <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityDisposalInstructionId">
                Linked disposal instruction ID
                <input
                  id="carrierLiabilityDisposalInstructionId"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={carrierLiabilityDisposalInstructionId}
                  onChange={(event) => setCarrierLiabilityDisposalInstructionId(event.target.value)}
                />
              </label>
              <fieldset className="mb-3">
                <legend className="mb-2 block text-sm font-bold text-gray-700">Article 12.7 evidence</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityInstructionNotCarriedOut}
                      onChange={(event) => setCarrierLiabilityInstructionNotCarriedOut(event.target.checked)}
                    />
                    Instruction not carried out
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityInstructionWithoutFirstCopy}
                      onChange={(event) => setCarrierLiabilityInstructionWithoutFirstCopy(event.target.checked)}
                    />
                    Carried out without first copy
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityClaimantEntitlement}
                      onChange={(event) => setCarrierLiabilityClaimantEntitlement(event.target.checked)}
                    />
                    Claimant entitlement asserted
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityLossOrDamageCaused}
                      onChange={(event) => setCarrierLiabilityLossOrDamageCaused(event.target.checked)}
                    />
                    Loss or damage caused
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityLossObserved}
                      onChange={(event) => setCarrierLiabilityLossObserved(event.target.checked)}
                    />
                    Loss observed
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={carrierLiabilityDamageObserved}
                      onChange={(event) => setCarrierLiabilityDamageObserved(event.target.checked)}
                    />
                    Damage observed
                  </label>
                </div>
              </fieldset>
            </>
          ) : (
            <>
          <div className="mb-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityTakingOverAt">
              Taking over at
              <input
                id="carrierLiabilityTakingOverAt"
                type="datetime-local"
                required
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carrierLiabilityTakingOverAt}
                onChange={(event) => setCarrierLiabilityTakingOverAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityDeliveryAt">
              Delivery at
              <input
                id="carrierLiabilityDeliveryAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carrierLiabilityDeliveryAt}
                onChange={(event) => setCarrierLiabilityDeliveryAt(event.target.value)}
              />
            </label>
          </div>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityIncidentEvidenceAttachmentId">
            Incident evidence attachment ID
            <input
              id="carrierLiabilityIncidentEvidenceAttachmentId"
              required
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityIncidentEvidenceAttachmentId}
              onChange={(event) => setCarrierLiabilityIncidentEvidenceAttachmentId(event.target.value)}
            />
          </label>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17 incident flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityLossObserved}
                  onChange={(event) => setCarrierLiabilityLossObserved(event.target.checked)}
                />
                Loss observed
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityDamageObserved}
                  onChange={(event) => setCarrierLiabilityDamageObserved(event.target.checked)}
                />
                Damage observed
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityDelayObserved}
                  onChange={(event) => setCarrierLiabilityDelayObserved(event.target.checked)}
                />
                Delay observed
              </label>
            </div>
          </fieldset>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.2 allegations</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityClaimantFault}
                  onChange={(event) => setCarrierLiabilityClaimantFault(event.target.checked)}
                />
                Claimant fault alleged
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityClaimantInstruction}
                  onChange={(event) => setCarrierLiabilityClaimantInstruction(event.target.checked)}
                />
                Claimant instruction alleged
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityInherentVice}
                  onChange={(event) => setCarrierLiabilityInherentVice(event.target.checked)}
                />
                Inherent vice alleged
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityUnavoidableCircumstances}
                  onChange={(event) => setCarrierLiabilityUnavoidableCircumstances(event.target.checked)}
                />
                Unavoidable circumstances alleged
              </label>
            </div>
          </fieldset>
          {(carrierLiabilityClaimantFault
            || carrierLiabilityClaimantInstruction
            || carrierLiabilityInherentVice
            || carrierLiabilityUnavoidableCircumstances) && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 18.1 burden assessment</legend>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityArticle18_1CauseProofEstablished}
                  onChange={(event) => setCarrierLiabilityArticle18_1CauseProofEstablished(event.target.checked)}
                />
                Article 18.1 cause proof established
              </label>
              {carrierLiabilityArticle18_1CauseProofEstablished && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_1EvidenceAttachmentId">
                    Article 18.1 evidence attachment ID
                    <input
                      id="carrierLiabilityArticle18_1EvidenceAttachmentId"
                      required
                      className="mt-1 block w-full rounded border border-gray-300 p-2"
                      value={carrierLiabilityArticle18_1EvidenceAttachmentId}
                      onChange={(event) => setCarrierLiabilityArticle18_1EvidenceAttachmentId(event.target.value)}
                    />
                  </label>
                  <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_1ProofAssessmentDetail">
                    Article 18.1 proof assessment detail
                    <textarea
                      id="carrierLiabilityArticle18_1ProofAssessmentDetail"
                      required
                      className="mt-1 block w-full rounded border border-gray-300 p-2"
                      value={carrierLiabilityArticle18_1ProofAssessmentDetail}
                      onChange={(event) => setCarrierLiabilityArticle18_1ProofAssessmentDetail(event.target.value)}
                    />
                  </label>
                </div>
              )}
            </fieldset>
          )}
          {(carrierLiabilityClaimantFault
            || carrierLiabilityClaimantInstruction
            || carrierLiabilityInherentVice
            || carrierLiabilityUnavoidableCircumstances) && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.3 factor assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityVehicleDefectContributed}
                    onChange={(event) => {
                      setCarrierLiabilityVehicleDefectContributed(event.target.checked);
                      if (!event.target.checked && !carrierLiabilityHiredVehicleProviderContribution) {
                        setCarrierLiabilityArticle17_3AssessmentDetail('');
                        setCarrierLiabilityArticle17_3EvidenceAttachmentId('');
                      }
                    }}
                  />
                  Vehicle defect contributed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityHiredVehicleProviderContribution}
                    onChange={(event) => {
                      setCarrierLiabilityHiredVehicleProviderContribution(event.target.checked);
                      if (!event.target.checked && !carrierLiabilityVehicleDefectContributed) {
                        setCarrierLiabilityArticle17_3AssessmentDetail('');
                        setCarrierLiabilityArticle17_3EvidenceAttachmentId('');
                      }
                    }}
                  />
                  Hired vehicle provider act or neglect contributed
                </label>
              </div>
              {(carrierLiabilityVehicleDefectContributed || carrierLiabilityHiredVehicleProviderContribution) && (
                <>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_3EvidenceAttachmentId">
                      Article 17.3 evidence attachment ID
                      <input
                        id="carrierLiabilityArticle17_3EvidenceAttachmentId"
                        required
                        className="mt-1 block w-full rounded border border-gray-300 p-2"
                        value={carrierLiabilityArticle17_3EvidenceAttachmentId}
                        onChange={(event) => setCarrierLiabilityArticle17_3EvidenceAttachmentId(event.target.value)}
                      />
                    </label>
                    <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_3AssessmentDetail">
                      Article 17.3 assessment detail
                      <textarea
                        id="carrierLiabilityArticle17_3AssessmentDetail"
                        required
                        className="mt-1 block w-full rounded border border-gray-300 p-2"
                        value={carrierLiabilityArticle17_3AssessmentDetail}
                        onChange={(event) => setCarrierLiabilityArticle17_3AssessmentDetail(event.target.value)}
                      />
                    </label>
                  </div>
                  <div className="mt-3">
                    <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                      <input
                        type="checkbox"
                        checked={carrierLiabilityArticle17_2CauseContributed}
                        disabled={!carrierLiabilityArticle18_1CauseProofEstablished}
                        onChange={(event) => setCarrierLiabilityArticle17_2CauseContributed(event.target.checked)}
                      />
                      Article 17.2 cause also contributed
                    </label>
                  </div>
                  {carrierLiabilityArticle17_2CauseContributed && (
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_5CarrierShare">
                        Article 17.5 carrier-liable factor share (%)
                        <input
                          id="carrierLiabilityArticle17_5CarrierShare"
                          type="number"
                          min="0.01"
                          max="100"
                          step="any"
                          required
                          className="mt-1 block w-full rounded border border-gray-300 p-2"
                          value={carrierLiabilityArticle17_5CarrierShare}
                          onChange={(event) => setCarrierLiabilityArticle17_5CarrierShare(event.target.value)}
                        />
                      </label>
                      <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_5ReliefShare">
                        Article 17.5 relief factor share (%)
                        <input
                          id="carrierLiabilityArticle17_5ReliefShare"
                          type="number"
                          min="0.01"
                          max="100"
                          step="any"
                          required
                          className="mt-1 block w-full rounded border border-gray-300 p-2"
                          value={carrierLiabilityArticle17_5ReliefShare}
                          onChange={(event) => setCarrierLiabilityArticle17_5ReliefShare(event.target.value)}
                        />
                      </label>
                      <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_5EvidenceAttachmentId">
                        Article 17.5 evidence attachment ID
                        <input
                          id="carrierLiabilityArticle17_5EvidenceAttachmentId"
                          required
                          className="mt-1 block w-full rounded border border-gray-300 p-2"
                          value={carrierLiabilityArticle17_5EvidenceAttachmentId}
                          onChange={(event) => setCarrierLiabilityArticle17_5EvidenceAttachmentId(event.target.value)}
                        />
                      </label>
                      <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_5AssessmentDetail">
                        Article 17.5 assessment detail
                        <textarea
                          id="carrierLiabilityArticle17_5AssessmentDetail"
                          required
                          className="mt-1 block w-full rounded border border-gray-300 p-2"
                          value={carrierLiabilityArticle17_5AssessmentDetail}
                          onChange={(event) => setCarrierLiabilityArticle17_5AssessmentDetail(event.target.value)}
                        />
                      </label>
                    </div>
                  )}
                </>
              )}
            </fieldset>
          )}
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4 / 18 special-risk flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilitySpecialRiskClaimed}
                  disabled={carrierLiabilityIncidentType === 'delay'}
                  onChange={(event) => {
                    setCarrierLiabilitySpecialRiskClaimed(event.target.checked);
                    if (!event.target.checked) {
                      setCarrierLiabilitySpecialRiskPresumption(false);
                      setCarrierLiabilityArticle18_2CircumstancesEstablished(false);
                      setCarrierLiabilityArticle17_4aOpenVehicleUsed(false);
                      setCarrierLiabilityArticle17_4aAssessmentDetail('');
                      setCarrierLiabilityArticle17_4aEvidenceAttachmentId('');
                      setCarrierLiabilityArticle17_4bPackingAbsentOrDefective(false);
                      setCarrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking(false);
                      setCarrierLiabilityArticle17_4bAssessmentDetail('');
                      setCarrierLiabilityArticle17_4bEvidenceAttachmentId('');
                      setCarrierLiabilityArticle17_4cHandlingPerformed(false);
                      setCarrierLiabilityArticle17_4cLoadingPerformed(false);
                      setCarrierLiabilityArticle17_4cStowagePerformed(false);
                      setCarrierLiabilityArticle17_4cUnloadingPerformed(false);
                      setCarrierLiabilityArticle17_4cActorCapacity('consignor');
                      setCarrierLiabilityArticle17_4cAssessmentDetail('');
                      setCarrierLiabilityArticle17_4cEvidenceAttachmentId('');
                      setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(false);
                      setCarrierLiabilityArticle17_4dSusceptibilityBasis('breakage');
                      setCarrierLiabilityArticle17_4dAssessmentDetail('');
                      setCarrierLiabilityArticle17_4dEvidenceAttachmentId('');
                      setCarrierLiabilityArticle17_4ePackageMarksDeficient(false);
                      setCarrierLiabilityArticle17_4ePackageNumbersDeficient(false);
                      setCarrierLiabilityArticle17_4eDeficiencyBasis('insufficiency');
                      setCarrierLiabilityArticle17_4eAssessmentDetail('');
                      setCarrierLiabilityArticle17_4eEvidenceAttachmentId('');
                      setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(false);
                      setCarrierLiabilityArticle17_4fAssessmentDetail('');
                      setCarrierLiabilityArticle17_4fEvidenceAttachmentId('');
                      setCarrierLiabilityTemperatureControlled(false);
                      setCarrierLiabilityEquipmentStepsEvidence(false);
                      setCarrierLiabilityLivestockCareEvidence(false);
                      setCarrierLiabilityArticle18_4ChoiceSteps(false);
                      setCarrierLiabilityArticle18_4MaintenanceSteps(false);
                      setCarrierLiabilityArticle18_4UseSteps(false);
                      setCarrierLiabilityArticle18_4InstructionsIssued(false);
                      setCarrierLiabilityArticle18_4InstructionsComplied(false);
                      setCarrierLiabilityArticle18_4AssessmentDetail('');
                      setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
                      setCarrierLiabilityArticle18_5NormallyIncumbentSteps(false);
                      setCarrierLiabilityArticle18_5InstructionsIssued(false);
                      setCarrierLiabilityArticle18_5InstructionsComplied(false);
                      setCarrierLiabilityArticle18_5AssessmentDetail('');
                      setCarrierLiabilityArticle18_5EvidenceAttachmentId('');
                      setCarrierLiabilityAbnormalShortageObserved(false);
                      setCarrierLiabilityAnyPackageLost(false);
                    }
                  }}
                />
                Special risk claimed
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilitySpecialRiskPresumption}
                  disabled={
                    !carrierLiabilitySpecialRiskClaimed
                    || !carrierLiabilityArticle18_2CircumstancesEstablished
                    || carrierLiabilityAbnormalShortageObserved
                    || carrierLiabilityAnyPackageLost
                  }
                  onChange={(event) => setCarrierLiabilitySpecialRiskPresumption(event.target.checked)}
                />
                Special-risk presumption claimed
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityTemperatureControlled}
                  disabled={!carrierLiabilitySpecialRiskClaimed || carrierLiabilitySpecialRiskType !== 'goods_nature'}
                  onChange={(event) => {
                    setCarrierLiabilityTemperatureControlled(event.target.checked);
                    if (!event.target.checked) {
                      setCarrierLiabilityEquipmentStepsEvidence(false);
                      setCarrierLiabilityArticle18_4ChoiceSteps(false);
                      setCarrierLiabilityArticle18_4MaintenanceSteps(false);
                      setCarrierLiabilityArticle18_4UseSteps(false);
                      setCarrierLiabilityArticle18_4InstructionsIssued(false);
                      setCarrierLiabilityArticle18_4InstructionsComplied(false);
                      setCarrierLiabilityArticle18_4AssessmentDetail('');
                      setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
                    }
                  }}
                />
                Temperature-controlled vehicle
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityEquipmentStepsEvidence}
                  required={carrierLiabilityTemperatureControlled}
                  disabled={
                    !carrierLiabilitySpecialRiskClaimed
                    || carrierLiabilitySpecialRiskType !== 'goods_nature'
                    || !carrierLiabilityTemperatureControlled
                  }
                  onChange={(event) => setCarrierLiabilityEquipmentStepsEvidence(event.target.checked)}
                />
                Equipment steps evidence
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityLivestockCareEvidence}
                  required={carrierLiabilitySpecialRiskClaimed && carrierLiabilitySpecialRiskType === 'livestock'}
                  disabled={!carrierLiabilitySpecialRiskClaimed || carrierLiabilitySpecialRiskType !== 'livestock'}
                  onChange={(event) => setCarrierLiabilityLivestockCareEvidence(event.target.checked)}
                />
                Livestock care evidence
              </label>
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilitySpecialRiskType">
            Special risk type
            <select
              id="carrierLiabilitySpecialRiskType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilitySpecialRiskType}
              disabled={!carrierLiabilitySpecialRiskClaimed}
              onChange={(event) => {
                const nextRisk = event.target.value as CarrierLiabilityIncidentSpecialRisk;
                setCarrierLiabilitySpecialRiskType(nextRisk);
                if (nextRisk !== 'defective_or_missing_packing') {
                  setCarrierLiabilityArticle17_4bPackingAbsentOrDefective(false);
                  setCarrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking(false);
                  setCarrierLiabilityArticle17_4bAssessmentDetail('');
                  setCarrierLiabilityArticle17_4bEvidenceAttachmentId('');
                }
                if (nextRisk !== 'sender_loading_handling_stowage_unloading') {
                  setCarrierLiabilityArticle17_4cHandlingPerformed(false);
                  setCarrierLiabilityArticle17_4cLoadingPerformed(false);
                  setCarrierLiabilityArticle17_4cStowagePerformed(false);
                  setCarrierLiabilityArticle17_4cUnloadingPerformed(false);
                  setCarrierLiabilityArticle17_4cActorCapacity('consignor');
                  setCarrierLiabilityArticle17_4cAssessmentDetail('');
                  setCarrierLiabilityArticle17_4cEvidenceAttachmentId('');
                }
                if (nextRisk !== 'goods_nature') {
                  setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(false);
                  setCarrierLiabilityArticle17_4dSusceptibilityBasis('breakage');
                  setCarrierLiabilityArticle17_4dAssessmentDetail('');
                  setCarrierLiabilityArticle17_4dEvidenceAttachmentId('');
                  setCarrierLiabilityTemperatureControlled(false);
                  setCarrierLiabilityEquipmentStepsEvidence(false);
                  setCarrierLiabilityArticle18_4ChoiceSteps(false);
                  setCarrierLiabilityArticle18_4MaintenanceSteps(false);
                  setCarrierLiabilityArticle18_4UseSteps(false);
                  setCarrierLiabilityArticle18_4InstructionsIssued(false);
                  setCarrierLiabilityArticle18_4InstructionsComplied(false);
                  setCarrierLiabilityArticle18_4AssessmentDetail('');
                  setCarrierLiabilityArticle18_4EvidenceAttachmentId('');
                }
                if (nextRisk !== 'insufficient_marks_numbers') {
                  setCarrierLiabilityArticle17_4ePackageMarksDeficient(false);
                  setCarrierLiabilityArticle17_4ePackageNumbersDeficient(false);
                  setCarrierLiabilityArticle17_4eDeficiencyBasis('insufficiency');
                  setCarrierLiabilityArticle17_4eAssessmentDetail('');
                  setCarrierLiabilityArticle17_4eEvidenceAttachmentId('');
                }
                if (nextRisk !== 'livestock') {
                  setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(false);
                  setCarrierLiabilityArticle17_4fAssessmentDetail('');
                  setCarrierLiabilityArticle17_4fEvidenceAttachmentId('');
                  setCarrierLiabilityLivestockCareEvidence(false);
                  setCarrierLiabilityArticle18_5NormallyIncumbentSteps(false);
                  setCarrierLiabilityArticle18_5InstructionsIssued(false);
                  setCarrierLiabilityArticle18_5InstructionsComplied(false);
                  setCarrierLiabilityArticle18_5AssessmentDetail('');
                  setCarrierLiabilityArticle18_5EvidenceAttachmentId('');
                }
                if (nextRisk !== 'open_unsheeted_vehicle') {
                  setCarrierLiabilityArticle17_4aOpenVehicleUsed(false);
                  setCarrierLiabilityArticle17_4aAssessmentDetail('');
                  setCarrierLiabilityArticle17_4aEvidenceAttachmentId('');
                  setCarrierLiabilityAbnormalShortageObserved(false);
                  setCarrierLiabilityAnyPackageLost(false);
                }
              }}
            >
              {carrierLiabilitySpecialRisks.map((risk) => (
                <option key={risk.value} value={risk.value}>{risk.label}</option>
              ))}
            </select>
          </label>
          {carrierLiabilitySpecialRiskClaimed && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 18.2 circumstances assessment</legend>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={carrierLiabilityArticle18_2CircumstancesEstablished}
                  onChange={(event) => setCarrierLiabilityArticle18_2CircumstancesEstablished(event.target.checked)}
                />
                Special-risk circumstances established
              </label>
              {carrierLiabilityArticle18_2CircumstancesEstablished && (
                <div className="mt-3 grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_2EvidenceAttachmentId">
                    Article 18.2 evidence attachment ID
                    <input
                      id="carrierLiabilityArticle18_2EvidenceAttachmentId"
                      required
                      className="mt-1 block w-full rounded border border-gray-300 p-2"
                      value={carrierLiabilityArticle18_2EvidenceAttachmentId}
                      onChange={(event) => setCarrierLiabilityArticle18_2EvidenceAttachmentId(event.target.value)}
                    />
                  </label>
                  <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_2AssessmentDetail">
                    Article 18.2 assessment detail
                    <textarea
                      id="carrierLiabilityArticle18_2AssessmentDetail"
                      required
                      className="mt-1 block w-full rounded border border-gray-300 p-2"
                      value={carrierLiabilityArticle18_2AssessmentDetail}
                      onChange={(event) => setCarrierLiabilityArticle18_2AssessmentDetail(event.target.value)}
                    />
                  </label>
                </div>
              )}
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'open_unsheeted_vehicle' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(a) vehicle-use assessment</legend>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  required
                  checked={carrierLiabilityArticle17_4aOpenVehicleUsed}
                  onChange={(event) => setCarrierLiabilityArticle17_4aOpenVehicleUsed(event.target.checked)}
                />
                Affected carriage reported as using an open unsheeted vehicle
              </label>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4aEvidenceAttachmentId">
                  Article 17.4(a) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4aEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4aEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4aEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4aAssessmentDetail">
                  Article 17.4(a) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4aAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4aAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4aAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'defective_or_missing_packing' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(b) packing assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle17_4bPackingAbsentOrDefective}
                    onChange={(event) => setCarrierLiabilityArticle17_4bPackingAbsentOrDefective(event.target.checked)}
                  />
                  Packing absent or defective observed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking}
                    onChange={(event) => setCarrierLiabilityArticle17_4bGoodsLiableWithoutProperPacking(event.target.checked)}
                  />
                  Goods liable to wastage or damage without proper packing
                </label>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4bEvidenceAttachmentId">
                  Article 17.4(b) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4bEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4bEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4bEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4bAssessmentDetail">
                  Article 17.4(b) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4bAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4bAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4bAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'sender_loading_handling_stowage_unloading' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(c) operation assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4cHandlingPerformed}
                    onChange={(event) => setCarrierLiabilityArticle17_4cHandlingPerformed(event.target.checked)}
                  />
                  Handling performed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4cLoadingPerformed}
                    onChange={(event) => setCarrierLiabilityArticle17_4cLoadingPerformed(event.target.checked)}
                  />
                  Loading performed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4cStowagePerformed}
                    onChange={(event) => setCarrierLiabilityArticle17_4cStowagePerformed(event.target.checked)}
                  />
                  Stowage performed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4cUnloadingPerformed}
                    onChange={(event) => setCarrierLiabilityArticle17_4cUnloadingPerformed(event.target.checked)}
                  />
                  Unloading performed
                </label>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4cActorCapacity">
                  Article 17.4(c) actor capacity
                  <select
                    id="carrierLiabilityArticle17_4cActorCapacity"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4cActorCapacity}
                    onChange={(event) => setCarrierLiabilityArticle17_4cActorCapacity(event.target.value as CarrierLiabilityArticle17_4cActorCapacity)}
                  >
                    <option value="consignor">Consignor</option>
                    <option value="consignee">Consignee</option>
                    <option value="acting_on_behalf_of_consignor">Acting on behalf of consignor</option>
                    <option value="acting_on_behalf_of_consignee">Acting on behalf of consignee</option>
                  </select>
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4cEvidenceAttachmentId">
                  Article 17.4(c) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4cEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4cEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4cEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carrierLiabilityArticle17_4cAssessmentDetail">
                  Article 17.4(c) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4cAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4cAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4cAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'goods_nature' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(d) goods-nature assessment</legend>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  required
                  checked={carrierLiabilityArticle17_4dGoodsParticularlyExposed}
                  onChange={(event) => setCarrierLiabilityArticle17_4dGoodsParticularlyExposed(event.target.checked)}
                />
                Goods particularly exposed by their nature
              </label>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4dSusceptibilityBasis">
                  Article 17.4(d) susceptibility basis
                  <select
                    id="carrierLiabilityArticle17_4dSusceptibilityBasis"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4dSusceptibilityBasis}
                    onChange={(event) => setCarrierLiabilityArticle17_4dSusceptibilityBasis(event.target.value as CarrierLiabilityArticle17_4dSusceptibilityBasis)}
                  >
                    <option value="breakage">Breakage</option>
                    <option value="rust">Rust</option>
                    <option value="decay">Decay</option>
                    <option value="desiccation">Desiccation</option>
                    <option value="leakage">Leakage</option>
                    <option value="normal_wastage">Normal wastage</option>
                    <option value="moth_or_vermin">Moth or vermin</option>
                    <option value="other_intrinsic_nature">Other intrinsic nature</option>
                  </select>
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4dEvidenceAttachmentId">
                  Article 17.4(d) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4dEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4dEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4dEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carrierLiabilityArticle17_4dAssessmentDetail">
                  Article 17.4(d) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4dAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4dAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4dAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'insufficient_marks_numbers' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(e) package marking assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4ePackageMarksDeficient}
                    onChange={(event) => setCarrierLiabilityArticle17_4ePackageMarksDeficient(event.target.checked)}
                  />
                  Package marks insufficient or inadequate
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle17_4ePackageNumbersDeficient}
                    onChange={(event) => setCarrierLiabilityArticle17_4ePackageNumbersDeficient(event.target.checked)}
                  />
                  Package numbers insufficient or inadequate
                </label>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4eDeficiencyBasis">
                  Article 17.4(e) deficiency basis
                  <select
                    id="carrierLiabilityArticle17_4eDeficiencyBasis"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4eDeficiencyBasis}
                    onChange={(event) => setCarrierLiabilityArticle17_4eDeficiencyBasis(event.target.value as CarrierLiabilityArticle17_4eDeficiencyBasis)}
                  >
                    <option value="insufficiency">Insufficiency</option>
                    <option value="inadequacy">Inadequacy</option>
                    <option value="insufficiency_and_inadequacy">Insufficiency and inadequacy</option>
                  </select>
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4eEvidenceAttachmentId">
                  Article 17.4(e) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4eEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4eEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4eEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="carrierLiabilityArticle17_4eAssessmentDetail">
                  Article 17.4(e) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4eAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4eAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4eAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed
            && carrierLiabilitySpecialRiskType === 'goods_nature'
            && carrierLiabilityTemperatureControlled && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 18.4 equipment assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle18_4ChoiceSteps}
                    onChange={(event) => setCarrierLiabilityArticle18_4ChoiceSteps(event.target.checked)}
                  />
                  Equipment choice steps taken
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle18_4MaintenanceSteps}
                    onChange={(event) => setCarrierLiabilityArticle18_4MaintenanceSteps(event.target.checked)}
                  />
                  Equipment maintenance steps taken
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle18_4UseSteps}
                    onChange={(event) => setCarrierLiabilityArticle18_4UseSteps(event.target.checked)}
                  />
                  Equipment use steps taken
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle18_4InstructionsIssued}
                    onChange={(event) => {
                      setCarrierLiabilityArticle18_4InstructionsIssued(event.target.checked);
                      if (!event.target.checked) {
                        setCarrierLiabilityArticle18_4InstructionsComplied(false);
                      }
                    }}
                  />
                  Special instructions issued
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required={carrierLiabilityArticle18_4InstructionsIssued}
                    disabled={!carrierLiabilityArticle18_4InstructionsIssued}
                    checked={carrierLiabilityArticle18_4InstructionsComplied}
                    onChange={(event) => setCarrierLiabilityArticle18_4InstructionsComplied(event.target.checked)}
                  />
                  Special instructions complied with
                </label>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_4EvidenceAttachmentId">
                  Article 18.4 evidence attachment ID
                  <input
                    id="carrierLiabilityArticle18_4EvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle18_4EvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle18_4EvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_4AssessmentDetail">
                  Article 18.4 assessment detail
                  <textarea
                    id="carrierLiabilityArticle18_4AssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle18_4AssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle18_4AssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed && carrierLiabilitySpecialRiskType === 'livestock' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 17.4(f) livestock carriage assessment</legend>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  required
                  checked={carrierLiabilityArticle17_4fLivestockCarriageConfirmed}
                  onChange={(event) => setCarrierLiabilityArticle17_4fLivestockCarriageConfirmed(event.target.checked)}
                />
                Affected carriage reported as livestock carriage
              </label>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4fEvidenceAttachmentId">
                  Article 17.4(f) evidence attachment ID
                  <input
                    id="carrierLiabilityArticle17_4fEvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4fEvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle17_4fEvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle17_4fAssessmentDetail">
                  Article 17.4(f) assessment detail
                  <textarea
                    id="carrierLiabilityArticle17_4fAssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle17_4fAssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle17_4fAssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed && carrierLiabilitySpecialRiskType === 'livestock' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 18.5 livestock assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required
                    checked={carrierLiabilityArticle18_5NormallyIncumbentSteps}
                    onChange={(event) => setCarrierLiabilityArticle18_5NormallyIncumbentSteps(event.target.checked)}
                  />
                  Normally incumbent livestock steps taken
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityArticle18_5InstructionsIssued}
                    onChange={(event) => {
                      setCarrierLiabilityArticle18_5InstructionsIssued(event.target.checked);
                      if (!event.target.checked) {
                        setCarrierLiabilityArticle18_5InstructionsComplied(false);
                      }
                    }}
                  />
                  Special instructions issued
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    required={carrierLiabilityArticle18_5InstructionsIssued}
                    disabled={!carrierLiabilityArticle18_5InstructionsIssued}
                    checked={carrierLiabilityArticle18_5InstructionsComplied}
                    onChange={(event) => setCarrierLiabilityArticle18_5InstructionsComplied(event.target.checked)}
                  />
                  Special instructions complied with
                </label>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_5EvidenceAttachmentId">
                  Article 18.5 evidence attachment ID
                  <input
                    id="carrierLiabilityArticle18_5EvidenceAttachmentId"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle18_5EvidenceAttachmentId}
                    onChange={(event) => setCarrierLiabilityArticle18_5EvidenceAttachmentId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityArticle18_5AssessmentDetail">
                  Article 18.5 assessment detail
                  <textarea
                    id="carrierLiabilityArticle18_5AssessmentDetail"
                    required
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={carrierLiabilityArticle18_5AssessmentDetail}
                    onChange={(event) => setCarrierLiabilityArticle18_5AssessmentDetail(event.target.value)}
                  />
                </label>
              </div>
            </fieldset>
          )}
          {carrierLiabilitySpecialRiskClaimed && carrierLiabilitySpecialRiskType === 'open_unsheeted_vehicle' && (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Article 18.3 exception assessment</legend>
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityAbnormalShortageObserved}
                    onChange={(event) => {
                      setCarrierLiabilityAbnormalShortageObserved(event.target.checked);
                      if (event.target.checked) {
                        setCarrierLiabilitySpecialRiskPresumption(false);
                      }
                    }}
                  />
                  Abnormal shortage observed
                </label>
                <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={carrierLiabilityAnyPackageLost}
                    onChange={(event) => {
                      setCarrierLiabilityAnyPackageLost(event.target.checked);
                      if (event.target.checked) {
                        setCarrierLiabilitySpecialRiskPresumption(false);
                      }
                    }}
                  />
                  Package loss observed
                </label>
              </div>
            </fieldset>
          )}
          {(carrierLiabilityClaimantFault
            || carrierLiabilityClaimantInstruction
            || carrierLiabilityInherentVice
            || carrierLiabilityUnavoidableCircumstances
            || carrierLiabilitySpecialRiskClaimed
            || carrierLiabilitySpecialRiskPresumption) && (
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityCarrierProofEvidenceAttachmentId">
              Carrier proof evidence attachment ID
              <input
                id="carrierLiabilityCarrierProofEvidenceAttachmentId"
                required
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={carrierLiabilityCarrierProofEvidenceAttachmentId}
                onChange={(event) => setCarrierLiabilityCarrierProofEvidenceAttachmentId(event.target.value)}
              />
            </label>
          )}
            </>
          )}
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityIncidentDetail">
            Incident detail
            <textarea
              id="carrierLiabilityIncidentDetail"
              required={carrierLiabilityIncidentType !== 'article12_disposal_instruction_noncompliance'}
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityIncidentDetail}
              onChange={(event) => setCarrierLiabilityIncidentDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityClaimantStatementDetail">
            Claimant statement detail
            <textarea
              id="carrierLiabilityClaimantStatementDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityClaimantStatementDetail}
              onChange={(event) => setCarrierLiabilityClaimantStatementDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="carrierLiabilityCarrierProofDetail">
            Carrier proof detail
            <textarea
              id="carrierLiabilityCarrierProofDetail"
              required={carrierLiabilityIncidentType !== 'article12_disposal_instruction_noncompliance' && (
                carrierLiabilityClaimantFault
                || carrierLiabilityClaimantInstruction
                || carrierLiabilityInherentVice
                || carrierLiabilityUnavoidableCircumstances
                || carrierLiabilitySpecialRiskClaimed
                || carrierLiabilitySpecialRiskPresumption
              )}
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={carrierLiabilityCarrierProofDetail}
              onChange={(event) => setCarrierLiabilityCarrierProofDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record carrier liability incident
          </button>
        </form>

        <form onSubmit={handleArticle18_2ClaimantRebuttal} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Article 18.2 Claimant Rebuttal</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="article18_2RebuttalIncidentId">
            Carrier presumption incident ID
            <input
              id="article18_2RebuttalIncidentId"
              required
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={article18_2RebuttalIncidentId}
              onChange={(event) => setArticle18_2RebuttalIncidentId(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="article18_2RebuttalEvidenceAttachmentId">
            Counter-evidence attachment ID
            <input
              id="article18_2RebuttalEvidenceAttachmentId"
              required
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={article18_2RebuttalEvidenceAttachmentId}
              onChange={(event) => setArticle18_2RebuttalEvidenceAttachmentId(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="article18_2RebuttalCounterEvidenceDetail">
            Counter-evidence detail
            <textarea
              id="article18_2RebuttalCounterEvidenceDetail"
              required
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={article18_2RebuttalCounterEvidenceDetail}
              onChange={(event) => setArticle18_2RebuttalCounterEvidenceDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record Article 18.2 claimant rebuttal
          </button>
        </form>

        <form onSubmit={handleDeliveryTimingAssessment} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Delivery Timing Assessment</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryTimingAssessmentType">
            Assessment type
            <select
              id="deliveryTimingAssessmentType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryTimingAssessmentType}
              onChange={(event) => setDeliveryTimingAssessmentType(event.target.value as DeliveryTimingAssessmentType)}
            >
              {deliveryTimingAssessmentTypes.map((assessmentType) => (
                <option key={assessmentType.value} value={assessmentType.value}>{assessmentType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingTakingOverAt">
              Taking over at
              <input
                id="deliveryTimingTakingOverAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryTimingTakingOverAt}
                onChange={(event) => setDeliveryTimingTakingOverAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingActualDeliveryAt">
              Actual delivery at
              <input
                id="deliveryTimingActualDeliveryAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryTimingActualDeliveryAt}
                onChange={(event) => setDeliveryTimingActualDeliveryAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingAssessmentAt">
              Assessment at
              <input
                id="deliveryTimingAssessmentAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryTimingAssessmentAt}
                onChange={(event) => setDeliveryTimingAssessmentAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="deliveryTimingRecoveryNotificationAt">
              Recovery notification sent at
              <input
                id="deliveryTimingRecoveryNotificationAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryTimingRecoveryNotificationAt}
                onChange={(event) => setDeliveryTimingRecoveryNotificationAt(event.target.value)}
              />
            </label>
            {deliveryTimingAssessmentType === 'article20_recovery_notice' && deliveryTimingDeliveryRequested && (
              <>
                <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingRecoveryNotificationReceivedAt">
                  Recovery notification received at
                  <input
                    id="deliveryTimingRecoveryNotificationReceivedAt"
                    type="datetime-local"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={deliveryTimingRecoveryNotificationReceivedAt}
                    onChange={(event) => setDeliveryTimingRecoveryNotificationReceivedAt(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingDeliveryRequestAt">
                  Delivery requested at
                  <input
                    id="deliveryTimingDeliveryRequestAt"
                    type="datetime-local"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={deliveryTimingDeliveryRequestAt}
                    onChange={(event) => setDeliveryTimingDeliveryRequestAt(event.target.value)}
                  />
                </label>
              </>
            )}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 19 / 20 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Goods not delivered', deliveryTimingGoodsNotDelivered, setDeliveryTimingGoodsNotDelivered],
                ['Agreed time limit present', deliveryTimingAgreedLimitPresent, setDeliveryTimingAgreedLimitPresent],
                ['Agreed time limit expired', deliveryTimingAgreedLimitExpired, setDeliveryTimingAgreedLimitExpired],
                ['Partial-load assembly time considered', deliveryTimingPartialLoadConsidered, setDeliveryTimingPartialLoadConsidered],
                ['Thirty days after agreed limit expired', deliveryTimingThirtyDaysExpired, setDeliveryTimingThirtyDaysExpired],
                ['Sixty days after takeover expired', deliveryTimingSixtyDaysExpired, setDeliveryTimingSixtyDaysExpired],
                ['Recovery notification requested', deliveryTimingRecoveryRequested, setDeliveryTimingRecoveryRequested],
                ['Written acknowledgement issued', deliveryTimingWrittenAcknowledgement, setDeliveryTimingWrittenAcknowledgement],
                ['Goods recovered', deliveryTimingGoodsRecovered, setDeliveryTimingGoodsRecovered],
                ['Recovery notification sent', deliveryTimingRecoveryNotificationSent, setDeliveryTimingRecoveryNotificationSent],
                ['Delivery requested', deliveryTimingDeliveryRequested, setDeliveryTimingDeliveryRequested],
                ['Charges and refund condition acknowledged', deliveryTimingChargesRefundAcknowledged, setDeliveryTimingChargesRefundAcknowledged],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          {deliveryTimingAssessmentType === 'article19_delay' && !deliveryTimingAgreedLimitPresent && (
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingDiligentCarrierAllowedDurationMinutes">
                Diligent carrier allowance (minutes)
                <input
                  id="deliveryTimingDiligentCarrierAllowedDurationMinutes"
                  type="number"
                  min="1"
                  max="5270400"
                  step="1"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={deliveryTimingDiligentCarrierAllowedDurationMinutes}
                  onChange={(event) => setDeliveryTimingDiligentCarrierAllowedDurationMinutes(event.target.value)}
                />
              </label>
              {deliveryTimingPartialLoadConsidered && (
                <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingPartialLoadAssemblyDurationMinutes">
                  Partial-load assembly (minutes)
                  <input
                    id="deliveryTimingPartialLoadAssemblyDurationMinutes"
                    type="number"
                    min="1"
                    max="5270400"
                    step="1"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={deliveryTimingPartialLoadAssemblyDurationMinutes}
                    onChange={(event) => setDeliveryTimingPartialLoadAssemblyDurationMinutes(event.target.value)}
                  />
                </label>
              )}
            </div>
          )}
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryTimingDetail">
            Timing detail
            <textarea
              id="deliveryTimingDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryTimingDetail}
              onChange={(event) => setDeliveryTimingDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryTimingClaimDetail">
            Claim detail
            <textarea
              id="deliveryTimingClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryTimingClaimDetail}
              onChange={(event) => setDeliveryTimingClaimDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryTimingRecoveryDetail">
            Recovery detail
            <textarea
              id="deliveryTimingRecoveryDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryTimingRecoveryDetail}
              onChange={(event) => setDeliveryTimingRecoveryDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record delivery timing assessment
          </button>
        </form>

        <form onSubmit={handleCompensationPayment} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Article 20.2 Compensation Payment</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationPaymentEvidenceType">
            Payment evidence type
            <select
              id="compensationPaymentEvidenceType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationPaymentEvidenceType}
              onChange={(event) => setCompensationPaymentEvidenceType(event.target.value as CompensationPaymentEvidenceType)}
            >
              {compensationPaymentEvidenceTypes.map((evidenceType) => (
                <option key={evidenceType.value} value={evidenceType.value}>{evidenceType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationPaymentPaidAt">
              Paid at
              <input
                id="compensationPaymentPaidAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationPaymentPaidAt}
                onChange={(event) => setCompensationPaymentPaidAt(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationPaymentAmount">
              Payment amount
              <input
                id="compensationPaymentAmount"
                type="number"
                min="0.01"
                step="0.01"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationPaymentAmount}
                onChange={(event) => setCompensationPaymentAmount(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationPaymentCurrency">
              Currency
              <input
                id="compensationPaymentCurrency"
                className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                value={compensationPaymentCurrency}
                onChange={(event) => setCompensationPaymentCurrency(event.target.value.toUpperCase())}
                maxLength={3}
                required
              />
            </label>
          </div>
          <label className="mb-3 mt-3 block text-sm font-bold text-gray-700" htmlFor="compensationPaymentAttachmentId">
            Payment evidence attachment ID
            <input
              id="compensationPaymentAttachmentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationPaymentAttachmentId}
              onChange={(event) => setCompensationPaymentAttachmentId(event.target.value)}
              required
            />
          </label>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Payment conditions</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Compensation is for missing goods', compensationPaymentForMissingGoods, setCompensationPaymentForMissingGoods],
                ['Payment is settled', compensationPaymentSettled, setCompensationPaymentSettled],
                ['Recipient acknowledged receipt', compensationPaymentRecipientAcknowledged, setCompensationPaymentRecipientAcknowledged],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block text-sm font-bold text-gray-700" htmlFor="compensationPaymentDetail">
            Payment detail
            <textarea
              id="compensationPaymentDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationPaymentDetail}
              onChange={(event) => setCompensationPaymentDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record compensation payment
          </button>
        </form>

        <form onSubmit={handleRecoveryDeliverySettlement} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Article 20.3 Recovery Delivery Settlement</h2>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Consignment-note charges paid</legend>
            <div className="space-y-2">
              {recoverySettlementChargeLines.map((line, index) => (
                <div key={`recovery-settlement-charge-${index}`} className="grid grid-cols-[minmax(0,1fr)_7rem_2.5rem] gap-2">
                  <label className="block text-sm font-bold text-gray-700" htmlFor={`recoverySettlementChargeAmount-${index}`}>
                    Amount
                    <input
                      id={`recoverySettlementChargeAmount-${index}`}
                      type="number"
                      min="0.01"
                      step="0.01"
                      className="mt-1 block w-full rounded border border-gray-300 p-2"
                      value={line.amount}
                      onChange={(event) => setRecoverySettlementChargeLines((current) => current.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, amount: event.target.value } : item
                      )))}
                      required
                    />
                  </label>
                  <label className="block text-sm font-bold text-gray-700" htmlFor={`recoverySettlementChargeCurrency-${index}`}>
                    Currency
                    <input
                      id={`recoverySettlementChargeCurrency-${index}`}
                      className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                      value={line.currency}
                      onChange={(event) => setRecoverySettlementChargeLines((current) => current.map((item, itemIndex) => (
                        itemIndex === index ? { ...item, currency: event.target.value.toUpperCase() } : item
                      )))}
                      maxLength={3}
                      required
                    />
                  </label>
                  <button
                    type="button"
                    className="mt-6 h-10 w-10 rounded border border-gray-300 text-xl text-gray-700 disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() => setRecoverySettlementChargeLines((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                    disabled={recoverySettlementChargeLines.length === 1}
                    aria-label={`Remove charge line ${index + 1}`}
                    title="Remove charge line"
                  >
                    −
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-2 h-9 w-9 rounded border border-gray-300 text-xl font-bold text-gray-700"
              onClick={() => setRecoverySettlementChargeLines((current) => [...current, { amount: '', currency: 'EUR' }])}
              aria-label="Add charge line"
              title="Add charge line"
            >
              +
            </button>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementChargesPaidAt">
              Charges paid at
              <input
                id="recoverySettlementChargesPaidAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementChargesPaidAt}
                onChange={(event) => setRecoverySettlementChargesPaidAt(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementChargesAttachmentId">
              Charges payment evidence
              <select
                id="recoverySettlementChargesAttachmentId"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementChargesAttachmentId}
                onChange={(event) => setRecoverySettlementChargesAttachmentId(event.target.value)}
                required
              >
                <option value="">Select attachment</option>
                {attachments.map((attachment, index) => (
                  <option key={attachment.attachmentId} value={attachment.attachmentId}>
                    {attachment.documentType || `Attachment ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementRefundAmount">
              Compensation refund
              <input
                id="recoverySettlementRefundAmount"
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementRefundAmount}
                onChange={(event) => setRecoverySettlementRefundAmount(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementRefundCurrency">
              Refund currency
              <input
                id="recoverySettlementRefundCurrency"
                className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                value={recoverySettlementRefundCurrency}
                onChange={(event) => setRecoverySettlementRefundCurrency(event.target.value.toUpperCase())}
                maxLength={3}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementIncludedCharges">
              Charges included in compensation
              <input
                id="recoverySettlementIncludedCharges"
                type="number"
                min="0"
                step="0.01"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementIncludedCharges}
                onChange={(event) => setRecoverySettlementIncludedCharges(event.target.value)}
                required
              />
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementRefundedAt">
              Compensation refunded at
              <input
                id="recoverySettlementRefundedAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementRefundedAt}
                onChange={(event) => setRecoverySettlementRefundedAt(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementRefundAttachmentId">
              Compensation refund evidence
              <select
                id="recoverySettlementRefundAttachmentId"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoverySettlementRefundAttachmentId}
                onChange={(event) => setRecoverySettlementRefundAttachmentId(event.target.value)}
                required
              >
                <option value="">Select attachment</option>
                {attachments.map((attachment, index) => (
                  <option key={attachment.attachmentId} value={attachment.attachmentId}>
                    {attachment.documentType || `Attachment ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="my-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Settlement conditions</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Consignment-note charges are settled', recoverySettlementChargesSettled, setRecoverySettlementChargesSettled],
                ['Compensation refund is settled', recoverySettlementRefundSettled, setRecoverySettlementRefundSettled],
                ['Delay compensation rights are preserved', recoverySettlementDelayRightsPreserved, setRecoverySettlementDelayRightsPreserved],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block text-sm font-bold text-gray-700" htmlFor="recoverySettlementDetail">
            Settlement detail
            <textarea
              id="recoverySettlementDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={recoverySettlementDetail}
              onChange={(event) => setRecoverySettlementDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record recovery delivery settlement
          </button>
        </form>

        <form onSubmit={handleRecoveredGoodsDisposition} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Article 20.4 Recovered Goods Disposition</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsDispositionBasis">
              Disposition basis
              <select
                id="recoveredGoodsDispositionBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsDispositionBasis}
                onChange={(event) => setRecoveredGoodsDispositionBasis(event.target.value as RecoveredGoodsDispositionBasis)}
              >
                {recoveredGoodsDispositionBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsDispositionAction">
              Disposition action
              <select
                id="recoveredGoodsDispositionAction"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsDispositionAction}
                onChange={(event) => setRecoveredGoodsDispositionAction(event.target.value as RecoveredGoodsDispositionActionType)}
              >
                {recoveredGoodsDispositionActions.map((action) => (
                  <option key={action.value} value={action.value}>{action.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsRecoveredAt">
              Goods recovered at
              <input
                id="recoveredGoodsRecoveredAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsRecoveredAt}
                onChange={(event) => setRecoveredGoodsRecoveredAt(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsDispositionAt">
              Disposition at
              <input
                id="recoveredGoodsDispositionAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsDispositionAt}
                onChange={(event) => setRecoveredGoodsDispositionAt(event.target.value)}
                required
              />
            </label>
          </div>
          {recoveredGoodsDispositionBasis === 'no_written_recovery_notification_request' && (
            <label className="mt-3 flex items-center gap-2 text-sm font-bold text-gray-700">
              <input
                type="checkbox"
                checked={recoveredGoodsWrittenRequestAbsent}
                onChange={(event) => setRecoveredGoodsWrittenRequestAbsent(event.target.checked)}
              />
              Written recovery-notification request absent
            </label>
          )}
          {recoveredGoodsDispositionBasis === 'no_instructions_after_thirty_days' && (
            <fieldset className="mt-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Instruction period</legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsNotificationReceivedAt">
                  Recovery notification received at
                  <input
                    id="recoveredGoodsNotificationReceivedAt"
                    type="datetime-local"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={recoveredGoodsNotificationReceivedAt}
                    onChange={(event) => setRecoveredGoodsNotificationReceivedAt(event.target.value)}
                    required
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsInstructionsAssessedAt">
                  Instruction absence assessed at
                  <input
                    id="recoveredGoodsInstructionsAssessedAt"
                    type="datetime-local"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={recoveredGoodsInstructionsAssessedAt}
                    onChange={(event) => setRecoveredGoodsInstructionsAssessedAt(event.target.value)}
                    required
                  />
                </label>
              </div>
              <label className="mt-2 flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={recoveredGoodsInstructionsAbsent}
                  onChange={(event) => setRecoveredGoodsInstructionsAbsent(event.target.checked)}
                />
                Instructions absent
              </label>
            </fieldset>
          )}
          <div className="mt-3 grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsLocationCountryCode">
              Country code
              <input
                id="recoveredGoodsLocationCountryCode"
                className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                value={recoveredGoodsLocationCountryCode}
                onChange={(event) => setRecoveredGoodsLocationCountryCode(event.target.value.toUpperCase())}
                maxLength={2}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsLocationDetail">
              Goods location detail
              <input
                id="recoveredGoodsLocationDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsLocationDetail}
                onChange={(event) => setRecoveredGoodsLocationDetail(event.target.value)}
                required
              />
            </label>
          </div>
          <label className="mt-3 block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsApplicableLawDetail">
            Applicable local law detail
            <textarea
              id="recoveredGoodsApplicableLawDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={recoveredGoodsApplicableLawDetail}
              onChange={(event) => setRecoveredGoodsApplicableLawDetail(event.target.value)}
              required
            />
          </label>
          <label className="mt-3 block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsDispositionDetail">
            Disposition detail
            <textarea
              id="recoveredGoodsDispositionDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={recoveredGoodsDispositionDetail}
              onChange={(event) => setRecoveredGoodsDispositionDetail(event.target.value)}
              required
            />
          </label>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsRecoveryAttachmentId">
              Recovery evidence
              <select
                id="recoveredGoodsRecoveryAttachmentId"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsRecoveryAttachmentId}
                onChange={(event) => setRecoveredGoodsRecoveryAttachmentId(event.target.value)}
                required
              >
                <option value="">Select attachment</option>
                {attachments.map((attachment, index) => (
                  <option key={attachment.attachmentId} value={attachment.attachmentId}>
                    {attachment.documentType || `Attachment ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="recoveredGoodsDispositionAttachmentId">
              Disposition evidence
              <select
                id="recoveredGoodsDispositionAttachmentId"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={recoveredGoodsDispositionAttachmentId}
                onChange={(event) => setRecoveredGoodsDispositionAttachmentId(event.target.value)}
                required
              >
                <option value="">Select attachment</option>
                {attachments.map((attachment, index) => (
                  <option key={attachment.attachmentId} value={attachment.attachmentId}>
                    {attachment.documentType || `Attachment ${index + 1}`}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="my-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Disposition conditions</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Goods recovered', recoveredGoodsRecovered, setRecoveredGoodsRecovered],
                ['Applicable local law identified', recoveredGoodsLocalLawIdentified, setRecoveredGoodsLocalLawIdentified],
                ['Disposition under local law recorded', recoveredGoodsDispositionUnderLaw, setRecoveredGoodsDispositionUnderLaw],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record recovered goods disposition
          </button>
        </form>

        <form onSubmit={handleCodDeliveryOmission} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">COD Delivery Omission</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="codOmissionType">
            Omission type
            <select
              id="codOmissionType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={codOmissionType}
              onChange={(event) => setCodOmissionType(event.target.value as CodDeliveryOmissionType)}
            >
              {codDeliveryOmissionTypes.map((omissionType) => (
                <option key={omissionType.value} value={omissionType.value}>{omissionType.label}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="codDeliveryAt">
            Delivery at
            <input
              id="codDeliveryAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={codDeliveryAt}
              onChange={(event) => setCodDeliveryAt(event.target.value)}
              required
            />
          </label>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 21 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Goods delivered to consignee', codGoodsDeliveredToConsignee, setCodGoodsDeliveredToConsignee],
                ['COD charge uncollected', codChargeUncollected, setCodChargeUncollected],
                ['Consignor compensation claimed', codConsignorCompensationClaimed, setCodConsignorCompensationClaimed],
                ['Carrier action against consignee reserved', codCarrierActionReserved, setCodCarrierActionReserved],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="codOmissionDetail">
            Omission detail
            <textarea
              id="codOmissionDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={codOmissionDetail}
              onChange={(event) => setCodOmissionDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="codCompensationClaimDetail">
            Compensation claim detail
            <textarea
              id="codCompensationClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={codCompensationClaimDetail}
              onChange={(event) => setCodCompensationClaimDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="codConsigneeActionDetail">
            Consignee action detail
            <textarea
              id="codConsigneeActionDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={codConsigneeActionDetail}
              onChange={(event) => setCodConsigneeActionDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record COD delivery omission
          </button>
        </form>

        <form onSubmit={handleDangerousGoodsNotice} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Dangerous Goods Notice</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerNoticeType">
            Notice type
            <select
              id="dangerNoticeType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerNoticeType}
              onChange={(event) => {
                const nextType = event.target.value as DangerousGoodsNoticeType;
                setDangerNoticeType(nextType);
                if (nextType === 'carrier_unaware_dangerous_goods_action') {
                  setDangerPrecautionsRequired(false);
                  setDangerCarrierKnowledgeProved(false);
                  setDangerNatureDetail('');
                  setDangerPrecautionsDetail('');
                  setDangerCarrierKnowledgeProofDetail('');
                  setDangerCarrierKnowledgeProofAttachmentId('');
                  setDangerCarrierDidNotKnow(true);
                  setDangerCarrierActionTaken(true);
                  setDangerWithoutCompensationBasisRecorded(true);
                } else {
                  setDangerPrecautionsRequired(false);
                  setDangerCarrierDidNotKnow(false);
                  setDangerCarrierActionTaken(false);
                  setDangerWithoutCompensationBasisRecorded(false);
                }
              }}
            >
              {dangerousGoodsNoticeTypes.map((noticeType) => (
                <option key={noticeType.value} value={noticeType.value}>{noticeType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="dangerNoticeAt">
              Notice at
              <input
                id="dangerNoticeAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={dangerNoticeAt}
                onChange={(event) => setDangerNoticeAt(event.target.value)}
                required={dangerNoticeType === 'danger_information_notice'}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="dangerActionAt">
              Action at
              <input
                id="dangerActionAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={dangerActionAt}
                onChange={(event) => setDangerActionAt(event.target.value)}
                required={dangerNoticeType === 'carrier_unaware_dangerous_goods_action'}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="dangerCarrierActionType">
              Carrier action type
              <select
                id="dangerCarrierActionType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={dangerCarrierActionType}
                onChange={(event) => setDangerCarrierActionType(event.target.value as DangerousGoodsCarrierActionType)}
              >
                {dangerousGoodsCarrierActionTypes.map((actionType) => (
                  <option key={actionType.value} value={actionType.value}>{actionType.label}</option>
                ))}
              </select>
            </label>
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 22 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Dangerous goods handed to carrier', dangerousGoodsHandedToCarrier, setDangerousGoodsHandedToCarrier],
                ['Carrier did not know dangerous nature', dangerCarrierDidNotKnow, setDangerCarrierDidNotKnow],
                ['Carrier action taken', dangerCarrierActionTaken, setDangerCarrierActionTaken],
                ['Without compensation basis recorded', dangerWithoutCompensationBasisRecorded, setDangerWithoutCompensationBasisRecorded],
                ['Sender expense liability claimed', dangerSenderExpenseLiabilityClaimed, setDangerSenderExpenseLiabilityClaimed],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={dangerPrecautionsRequired}
                  disabled={dangerNoticeType !== 'danger_information_notice'}
                  onChange={(event) => {
                    setDangerPrecautionsRequired(event.target.checked);
                    if (!event.target.checked) setDangerPrecautionsDetail('');
                  }}
                />
                Precautions required
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={dangerCarrierKnowledgeProved}
                  disabled={dangerNoticeType !== 'danger_information_notice'}
                  onChange={(event) => {
                    setDangerCarrierKnowledgeProved(event.target.checked);
                    if (!event.target.checked) {
                      setDangerNatureDetail('');
                      setDangerPrecautionsDetail('');
                      setDangerCarrierKnowledgeProofDetail('');
                      setDangerCarrierKnowledgeProofAttachmentId('');
                    }
                  }}
                />
                Carrier knowledge proved by other means
              </label>
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerNatureDetail">
            Danger nature detail
            <textarea
              id="dangerNatureDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerNatureDetail}
              onChange={(event) => setDangerNatureDetail(event.target.value)}
              required={dangerNoticeType === 'danger_information_notice' && dangerCarrierKnowledgeProved}
              disabled={dangerNoticeType !== 'danger_information_notice' || !dangerCarrierKnowledgeProved}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerPrecautionsDetail">
            Precautions detail
            <textarea
              id="dangerPrecautionsDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerPrecautionsDetail}
              onChange={(event) => setDangerPrecautionsDetail(event.target.value)}
              required={
                dangerNoticeType === 'danger_information_notice'
                && dangerCarrierKnowledgeProved
                && dangerPrecautionsRequired
              }
              disabled={
                dangerNoticeType !== 'danger_information_notice'
                || !dangerCarrierKnowledgeProved
                || !dangerPrecautionsRequired
              }
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerCarrierKnowledgeProofDetail">
            Carrier knowledge proof detail
            <textarea
              id="dangerCarrierKnowledgeProofDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerCarrierKnowledgeProofDetail}
              onChange={(event) => setDangerCarrierKnowledgeProofDetail(event.target.value)}
              required={dangerNoticeType === 'danger_information_notice' && dangerCarrierKnowledgeProved}
              disabled={dangerNoticeType !== 'danger_information_notice' || !dangerCarrierKnowledgeProved}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerCarrierKnowledgeProofAttachmentId">
            Carrier knowledge proof attachment reference
            <input
              id="dangerCarrierKnowledgeProofAttachmentId"
              type="text"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerCarrierKnowledgeProofAttachmentId}
              onChange={(event) => setDangerCarrierKnowledgeProofAttachmentId(event.target.value)}
              required={dangerNoticeType === 'danger_information_notice' && dangerCarrierKnowledgeProved}
              disabled={dangerNoticeType !== 'danger_information_notice' || !dangerCarrierKnowledgeProved}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="dangerCarrierActionDetail">
            Carrier action detail
            <textarea
              id="dangerCarrierActionDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerCarrierActionDetail}
              onChange={(event) => setDangerCarrierActionDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="dangerExpenseClaimDetail">
            Expense claim detail
            <textarea
              id="dangerExpenseClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={dangerExpenseClaimDetail}
              onChange={(event) => setDangerExpenseClaimDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record dangerous goods notice
          </button>
        </form>

        <form onSubmit={handleCompensationAssessment} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Compensation Assessment</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationAssessmentType">
              Assessment type
              <select
                id="compensationAssessmentType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationAssessmentType}
                onChange={(event) => {
                  const nextType = event.target.value as CompensationAssessmentType;
                  setCompensationAssessmentType(nextType);
                  setCompensationCarrierLiabilityBasisRecorded(
                    nextType === 'loss_compensation_limit'
                    || nextType === 'damage_depreciation_compensation_limit'
                    || nextType === 'delay_compensation_limit',
                  );
                  setCompensationArticle23LimitApplied(nextType === 'loss_compensation_limit');
                  setCompensationCarriageChargesRefundConsidered(nextType === 'loss_compensation_limit');
                  setCompensationDelayDamageProved(nextType === 'delay_compensation_limit');
                  setCompensationCarriageChargesLimitApplied(nextType === 'delay_compensation_limit');
                  setCompensationArticle25LossLimitApplied(nextType === 'damage_depreciation_compensation_limit');
                  setCompensationDeclaredValueEntered(nextType === 'declared_value_substitution');
                  setCompensationDeclaredValueSurchargeAgreed(nextType === 'declared_value_substitution');
                  setCompensationDeclaredValueExceedsArticle23Limit(nextType === 'declared_value_substitution');
                  setCompensationDeclaredValueLimitSubstituted(nextType === 'declared_value_substitution');
                  setCompensationSpecialInterestEntered(nextType === 'special_interest_additional_compensation');
                  setCompensationSpecialInterestSurchargeAgreed(nextType === 'special_interest_additional_compensation');
                  setCompensationAdditionalLossOrDamageProved(nextType === 'special_interest_additional_compensation');
                  setCompensationSpecialInterestLimitApplied(nextType === 'special_interest_additional_compensation');
                  if (
                    nextType !== 'loss_compensation_limit'
                    && nextType !== 'damage_depreciation_compensation_limit'
                  ) {
                    setCompensationValuationEvidenceAttachmentId('');
                  }
                }}
              >
                {compensationAssessmentTypes.map((assessmentType) => (
                  <option key={assessmentType.value} value={assessmentType.value}>{assessmentType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationAssessmentAt">
              Assessment at
              <input
                id="compensationAssessmentAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationAssessmentAt}
                onChange={(event) => setCompensationAssessmentAt(event.target.value)}
                required
              />
            </label>
          </div>
          <label className="mb-3 mt-3 block text-sm font-bold text-gray-700" htmlFor="compensationLinkedIncidentId">
            Linked incident reference
            <input
              id="compensationLinkedIncidentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationLinkedIncidentId}
              onChange={(event) => setCompensationLinkedIncidentId(event.target.value)}
              required
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationGoodsValueBasis">
              Goods value basis
              <select
                id="compensationGoodsValueBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationGoodsValueBasis}
                onChange={(event) => setCompensationGoodsValueBasis(event.target.value as CompensationGoodsValueBasis)}
              >
                {compensationGoodsValueBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationLiabilityLimitBasis">
              Liability limit basis
              <select
                id="compensationLiabilityLimitBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationLiabilityLimitBasis}
                onChange={(event) => setCompensationLiabilityLimitBasis(event.target.value as CompensationLiabilityLimitBasis)}
              >
                {compensationLiabilityLimitBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Goods value amount', compensationGoodsValueAmount, setCompensationGoodsValueAmount],
              ['Gross weight short kg', compensationGrossWeightShortKg, setCompensationGrossWeightShortKg],
              ['Claim amount', compensationClaimAmount, setCompensationClaimAmount],
              ['Carriage charges amount', compensationCarriageChargesAmount, setCompensationCarriageChargesAmount],
              ['Declared value amount', compensationDeclaredValueAmount, setCompensationDeclaredValueAmount],
              ['Special interest amount', compensationSpecialInterestAmount, setCompensationSpecialInterestAmount],
              ['Diminished value amount', compensationDiminishedValueAmount, setCompensationDiminishedValueAmount],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
            {[
              ['Goods value currency', compensationGoodsValueCurrency, setCompensationGoodsValueCurrency],
              ['Claim currency', compensationClaimCurrency, setCompensationClaimCurrency],
              ['Carriage charges currency', compensationCarriageChargesCurrency, setCompensationCarriageChargesCurrency],
              ['Declared value currency', compensationDeclaredValueCurrency, setCompensationDeclaredValueCurrency],
              ['Special interest currency', compensationSpecialInterestCurrency, setCompensationSpecialInterestCurrency],
              ['Diminished value currency', compensationDiminishedValueCurrency, setCompensationDiminishedValueCurrency],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                  value={value as string}
                  maxLength={3}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value.toUpperCase())}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Articles 23-26 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Carrier liability basis recorded', compensationCarrierLiabilityBasisRecorded, setCompensationCarrierLiabilityBasisRecorded],
                ['Article 23 limit applied', compensationArticle23LimitApplied, setCompensationArticle23LimitApplied],
                ['Carriage charges refund considered', compensationCarriageChargesRefundConsidered, setCompensationCarriageChargesRefundConsidered],
                ['Delay damage proved', compensationDelayDamageProved, setCompensationDelayDamageProved],
                ['Carriage charges limit applied', compensationCarriageChargesLimitApplied, setCompensationCarriageChargesLimitApplied],
                ['Damage affected whole consignment', compensationDamageAffectedWholeConsignment, setCompensationDamageAffectedWholeConsignment],
                ['Article 25 loss limit applied', compensationArticle25LossLimitApplied, setCompensationArticle25LossLimitApplied],
                ['Declared value entered in consignment note', compensationDeclaredValueEntered, setCompensationDeclaredValueEntered],
                ['Declared value surcharge agreed', compensationDeclaredValueSurchargeAgreed, setCompensationDeclaredValueSurchargeAgreed],
                ['Declared value exceeds Article 23 limit', compensationDeclaredValueExceedsArticle23Limit, setCompensationDeclaredValueExceedsArticle23Limit],
                ['Declared value limit substituted', compensationDeclaredValueLimitSubstituted, setCompensationDeclaredValueLimitSubstituted],
                ['Special interest entered in consignment note', compensationSpecialInterestEntered, setCompensationSpecialInterestEntered],
                ['Special interest surcharge agreed', compensationSpecialInterestSurchargeAgreed, setCompensationSpecialInterestSurchargeAgreed],
                ['Additional loss or damage proved', compensationAdditionalLossOrDamageProved, setCompensationAdditionalLossOrDamageProved],
                ['Special interest limit applied', compensationSpecialInterestLimitApplied, setCompensationSpecialInterestLimitApplied],
                ['Agreed time limit exceeded', compensationAgreedTimeLimitExceeded, setCompensationAgreedTimeLimitExceeded],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationValuationDetail">
            Valuation detail
            <textarea
              id="compensationValuationDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationValuationDetail}
              onChange={(event) => setCompensationValuationDetail(event.target.value)}
              required={
                compensationAssessmentType === 'loss_compensation_limit'
                || compensationAssessmentType === 'damage_depreciation_compensation_limit'
              }
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationValuationEvidenceAttachmentId">
            Valuation evidence attachment reference
            <input
              id="compensationValuationEvidenceAttachmentId"
              type="text"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationValuationEvidenceAttachmentId}
              onChange={(event) => setCompensationValuationEvidenceAttachmentId(event.target.value)}
              required={
                compensationAssessmentType === 'loss_compensation_limit'
                || compensationAssessmentType === 'damage_depreciation_compensation_limit'
              }
              disabled={
                compensationAssessmentType !== 'loss_compensation_limit'
                && compensationAssessmentType !== 'damage_depreciation_compensation_limit'
              }
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationCalculationDetail">
            Private calculation detail
            <textarea
              id="compensationCalculationDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationCalculationDetail}
              onChange={(event) => setCompensationCalculationDetail(event.target.value)}
              required={compensationAssessmentType === 'loss_compensation_limit'}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="compensationClaimDetail">
            Compensation claim detail
            <textarea
              id="compensationClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationClaimDetail}
              onChange={(event) => setCompensationClaimDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record compensation assessment
          </button>
        </form>

        <form onSubmit={handleCompensationInterest} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Compensation Interest / Conversion</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestRecordType">
              Record type
              <select
                id="compensationInterestRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationInterestRecordType}
                onChange={(event) => {
                  const nextType = event.target.value as CompensationInterestRecordType;
                  setCompensationInterestRecordType(nextType);
                  if (nextType === 'currency_conversion') {
                    setCompensationInterestPayableBasisRecorded(false);
                    setCompensationInterestClaimSentInWriting(false);
                    setCompensationInterestLegalProceedingsInstituted(false);
                    setCompensationInterestFivePercentApplied(false);
                    setCompensationInterestAmountsNotPaymentCurrency(true);
                    setCompensationInterestPaymentClaimCurrencyCountryBasis(true);
                    setCompensationInterestPaymentPlaceBasis(true);
                    setCompensationInterestExchangeRateApplicable(true);
                  } else {
                    setCompensationInterestStartBasis('written_claim');
                    setCompensationInterestPayableBasisRecorded(true);
                    setCompensationInterestClaimSentInWriting(true);
                    setCompensationInterestLegalProceedingsInstituted(false);
                    setCompensationInterestFivePercentApplied(true);
                    setCompensationInterestAmountsNotPaymentCurrency(false);
                    setCompensationInterestPaymentClaimCurrencyCountryBasis(false);
                    setCompensationInterestPaymentPlaceBasis(false);
                    setCompensationInterestExchangeRateApplicable(false);
                  }
                }}
              >
                {compensationInterestRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestStartBasis">
              Interest start basis
              <select
                id="compensationInterestStartBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationInterestStartBasis}
                onChange={(event) => {
                  const nextBasis = event.target.value as CompensationInterestStartBasis;
                  setCompensationInterestStartBasis(nextBasis);
                  setCompensationInterestClaimSentInWriting(nextBasis === 'written_claim');
                  setCompensationInterestLegalProceedingsInstituted(nextBasis === 'legal_proceedings');
                }}
              >
                {compensationInterestStartBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="mb-3 mt-3 block text-sm font-bold text-gray-700" htmlFor="compensationInterestLinkedAssessmentId">
            Linked compensation assessment reference
            <input
              id="compensationInterestLinkedAssessmentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationInterestLinkedAssessmentId}
              onChange={(event) => setCompensationInterestLinkedAssessmentId(event.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestStartAt">
              Interest start at
              <input
                id="compensationInterestStartAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationInterestStartAt}
                onChange={(event) => setCompensationInterestStartAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestCalculatedAt">
              Interest calculated at
              <input
                id="compensationInterestCalculatedAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationInterestCalculatedAt}
                onChange={(event) => setCompensationInterestCalculatedAt(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestPaymentAt">
              Payment at
              <input
                id="compensationInterestPaymentAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={compensationInterestPaymentAt}
                onChange={(event) => setCompensationInterestPaymentAt(event.target.value)}
              />
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Compensation payable amount', compensationInterestCompensationAmount, setCompensationInterestCompensationAmount],
              ['Interest amount', compensationInterestAmount, setCompensationInterestAmount],
              ['Source amount', compensationInterestSourceAmount, setCompensationInterestSourceAmount],
              ['Converted amount', compensationInterestConvertedAmount, setCompensationInterestConvertedAmount],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
            {[
              ['Compensation currency', compensationInterestCompensationCurrency, setCompensationInterestCompensationCurrency],
              ['Interest currency', compensationInterestCurrency, setCompensationInterestCurrency],
              ['Source currency', compensationInterestSourceCurrency, setCompensationInterestSourceCurrency],
              ['Target currency', compensationInterestTargetCurrency, setCompensationInterestTargetCurrency],
              ['Converted currency', compensationInterestConvertedCurrency, setCompensationInterestConvertedCurrency],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                  value={value as string}
                  maxLength={3}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value.toUpperCase())}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 27 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Compensation payable basis recorded', compensationInterestPayableBasisRecorded, setCompensationInterestPayableBasisRecorded],
                ['Claim sent in writing', compensationInterestClaimSentInWriting, setCompensationInterestClaimSentInWriting],
                ['Legal proceedings instituted', compensationInterestLegalProceedingsInstituted, setCompensationInterestLegalProceedingsInstituted],
                ['Five percent per annum applied', compensationInterestFivePercentApplied, setCompensationInterestFivePercentApplied],
                ['Amounts not expressed in payment claim currency', compensationInterestAmountsNotPaymentCurrency, setCompensationInterestAmountsNotPaymentCurrency],
                ['Payment claim currency country basis recorded', compensationInterestPaymentClaimCurrencyCountryBasis, setCompensationInterestPaymentClaimCurrencyCountryBasis],
                ['Payment place basis recorded', compensationInterestPaymentPlaceBasis, setCompensationInterestPaymentPlaceBasis],
                ['Exchange rate applicable at payment place and date', compensationInterestExchangeRateApplicable, setCompensationInterestExchangeRateApplicable],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationInterestCalculationDetail">
            Interest calculation detail
            <textarea
              id="compensationInterestCalculationDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationInterestCalculationDetail}
              onChange={(event) => setCompensationInterestCalculationDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="compensationInterestExchangeRateDetail">
            Exchange rate detail
            <textarea
              id="compensationInterestExchangeRateDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationInterestExchangeRateDetail}
              onChange={(event) => setCompensationInterestExchangeRateDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="compensationInterestClaimDetail">
            Article 27 claim detail
            <textarea
              id="compensationInterestClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={compensationInterestClaimDetail}
              onChange={(event) => setCompensationInterestClaimDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record compensation interest/conversion
          </button>
        </form>

        <form onSubmit={handleLiabilityLimitation} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Liability Limitation / Exception</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="liabilityLimitationRecordType">
              Record type
              <select
                id="liabilityLimitationRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={liabilityLimitationRecordType}
                onChange={(event) => {
                  const nextType = event.target.value as LiabilityLimitationRecordType;
                  setLiabilityLimitationRecordType(nextType);
                  setLiabilityExtraContractualClaim(nextType === 'extra_contractual_claim_defense');
                  setLiabilityCmrLimitInvoked(nextType === 'extra_contractual_claim_defense');
                  setLiabilityLimitationProvisionInvoked(nextType === 'wilful_misconduct_limitation_exception');
                  setLiabilityWilfulMisconductAlleged(nextType === 'wilful_misconduct_limitation_exception');
                  setLiabilityEquivalentDefaultAlleged(false);
                  setLiabilityCourtLawBasisRecorded(false);
                  setLiabilityDisapplicationBasisRecorded(nextType === 'wilful_misconduct_limitation_exception');
                }}
              >
                {liabilityLimitationRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="liabilityLimitationPersonRole">
              Liable person role
              <select
                id="liabilityLimitationPersonRole"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={liabilityLimitationPersonRole}
                onChange={(event) => setLiabilityLimitationPersonRole(event.target.value as LiabilityLimitationPersonRole)}
              >
                {liabilityLimitationPersonRoles.map((role) => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </label>
          </div>
          <label className="mb-3 mt-3 block text-sm font-bold text-gray-700" htmlFor="liabilityLimitationLinkedIncidentId">
            Linked incident reference
            <input
              id="liabilityLimitationLinkedIncidentId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityLimitationLinkedIncidentId}
              onChange={(event) => setLiabilityLimitationLinkedIncidentId(event.target.value)}
              required
            />
          </label>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Articles 28-29 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Claim arises from carriage under CMR', liabilityClaimArisesFromCarriage, setLiabilityClaimArisesFromCarriage],
                ['Loss, damage or delay claim', liabilityLossDamageOrDelayClaim, setLiabilityLossDamageOrDelayClaim],
                ['Extra-contractual claim under applicable law', liabilityExtraContractualClaim, setLiabilityExtraContractualClaim],
                ['CMR exclusion or limit invoked', liabilityCmrLimitInvoked, setLiabilityCmrLimitInvoked],
                ['Article 3 responsible person involved', liabilityArticle3PersonInvolved, setLiabilityArticle3PersonInvolved],
                ['Article 3 person scope recorded', liabilityArticle3ScopeRecorded, setLiabilityArticle3ScopeRecorded],
                ['Acting within scope of employment', liabilityActingWithinScope, setLiabilityActingWithinScope],
                ['Limitation or burden-shift provision invoked', liabilityLimitationProvisionInvoked, setLiabilityLimitationProvisionInvoked],
                ['Wilful misconduct alleged', liabilityWilfulMisconductAlleged, setLiabilityWilfulMisconductAlleged],
                ['Equivalent default alleged', liabilityEquivalentDefaultAlleged, setLiabilityEquivalentDefaultAlleged],
                ['Court or tribunal law basis recorded', liabilityCourtLawBasisRecorded, setLiabilityCourtLawBasisRecorded],
                ['Limitation disapplication basis recorded', liabilityDisapplicationBasisRecorded, setLiabilityDisapplicationBasisRecorded],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="liabilityClaimDetail">
            Claim detail
            <textarea
              id="liabilityClaimDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityClaimDetail}
              onChange={(event) => setLiabilityClaimDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="liabilityApplicableLawDetail">
            Applicable law detail
            <textarea
              id="liabilityApplicableLawDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityApplicableLawDetail}
              onChange={(event) => setLiabilityApplicableLawDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="liabilityArticle3PersonDetail">
            Article 3 person detail
            <textarea
              id="liabilityArticle3PersonDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityArticle3PersonDetail}
              onChange={(event) => setLiabilityArticle3PersonDetail(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="liabilityMisconductDetail">
            Misconduct detail
            <textarea
              id="liabilityMisconductDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityMisconductDetail}
              onChange={(event) => setLiabilityMisconductDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="liabilityCourtLawDetail">
            Court law detail
            <textarea
              id="liabilityCourtLawDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={liabilityCourtLawDetail}
              onChange={(event) => setLiabilityCourtLawDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record liability limitation/exception
          </button>
        </form>

        <form onSubmit={handleClaimReservation} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Claim Reservation / Check</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="claimReservationRecordType">
            Record type
            <select
              id="claimReservationRecordType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={claimReservationRecordType}
              onChange={(event) => {
                const nextType = event.target.value as ClaimReservationRecordType;
                setClaimReservationRecordType(nextType);
                setClaimLossOrDamageApparent(nextType === 'apparent_loss_or_damage_reservation');
                setClaimLossOrDamageNotApparent(nextType === 'non_apparent_loss_or_damage_reservation');
                setClaimDelayCompensationClaim(nextType === 'delay_compensation_reservation');
                setClaimReservationSentAtDelivery(nextType === 'apparent_loss_or_damage_reservation');
                setClaimReservationSentInWriting(nextType === 'non_apparent_loss_or_damage_reservation');
                setClaimReservationWithinSevenDays(nextType === 'non_apparent_loss_or_damage_reservation');
                setClaimConditionCheckedWithCarrier(nextType === 'condition_check_facility');
                setClaimContraryEvidenceReservationWritten(false);
                setClaimDelayReservationWritten(nextType === 'delay_compensation_reservation');
                setClaimReservationWithinTwentyOneDays(nextType === 'delay_compensation_reservation');
                setClaimRelevantStartDateExcluded(
                  nextType === 'non_apparent_loss_or_damage_reservation'
                  || nextType === 'delay_compensation_reservation',
                );
                setClaimReasonableFacilitiesProvided(nextType === 'condition_check_facility');
              }}
            >
              {claimReservationRecordTypes.map((recordType) => (
                <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            {[
              ['Linked delivery reference', claimReservationLinkedDeliveryId, setClaimReservationLinkedDeliveryId],
              ['Linked claim reference', claimReservationLinkedClaimId, setClaimReservationLinkedClaimId],
              ['Linked condition check reference', claimReservationLinkedConditionCheckId, setClaimReservationLinkedConditionCheckId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            {[
              ['Delivery at', claimReservationDeliveryAt, setClaimReservationDeliveryAt],
              ['Condition checked at', claimReservationConditionCheckedAt, setClaimReservationConditionCheckedAt],
              ['Goods available at', claimReservationGoodsAvailableAt, setClaimReservationGoodsAvailableAt],
              ['Reservation sent at', claimReservationSentAt, setClaimReservationSentAt],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          {claimReservationRecordType === 'non_apparent_loss_or_damage_reservation' && (
            <label className="mt-3 block text-sm font-bold text-gray-700" htmlFor="claimReservationExcludedPublicHolidayDates">
              Excluded public holiday dates
              <input
                id="claimReservationExcludedPublicHolidayDates"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={claimReservationExcludedPublicHolidayDates}
                onChange={(event) => setClaimReservationExcludedPublicHolidayDates(event.target.value)}
              />
            </label>
          )}
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 30 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Loss or damage apparent', claimLossOrDamageApparent, setClaimLossOrDamageApparent],
                ['Loss or damage not apparent', claimLossOrDamageNotApparent, setClaimLossOrDamageNotApparent],
                ['Delay compensation claim', claimDelayCompensationClaim, setClaimDelayCompensationClaim],
                ['Reservation sent at delivery', claimReservationSentAtDelivery, setClaimReservationSentAtDelivery],
                ['Reservation sent in writing', claimReservationSentInWriting, setClaimReservationSentInWriting],
                ['Reservation within seven days excluding holidays recorded', claimReservationWithinSevenDays, setClaimReservationWithinSevenDays],
                ['Condition checked with carrier', claimConditionCheckedWithCarrier, setClaimConditionCheckedWithCarrier],
                ['Contrary evidence reservation sent in writing', claimContraryEvidenceReservationWritten, setClaimContraryEvidenceReservationWritten],
                ['Delay reservation sent in writing', claimDelayReservationWritten, setClaimDelayReservationWritten],
                ['Reservation within twenty-one days recorded', claimReservationWithinTwentyOneDays, setClaimReservationWithinTwentyOneDays],
                ['Relevant start date excluded from limit calculation', claimRelevantStartDateExcluded, setClaimRelevantStartDateExcluded],
                ['Reasonable facilities provided', claimReasonableFacilitiesProvided, setClaimReasonableFacilitiesProvided],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="claimReservationDetail">
              Reservation detail
              <textarea
                id="claimReservationDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={claimReservationDetail}
                onChange={(event) => setClaimReservationDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="claimConditionCheckDetail">
              Condition check detail
              <textarea
                id="claimConditionCheckDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={claimConditionCheckDetail}
                onChange={(event) => setClaimConditionCheckDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="claimDelayClaimDetail">
              Delay claim detail
              <textarea
                id="claimDelayClaimDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={claimDelayClaimDetail}
                onChange={(event) => setClaimDelayClaimDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="claimInvestigationFacilityDetail">
              Investigation facility detail
              <textarea
                id="claimInvestigationFacilityDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={claimInvestigationFacilityDetail}
                onChange={(event) => setClaimInvestigationFacilityDetail(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record claim reservation/check
          </button>
        </form>

        <form onSubmit={handleDisputeProcedure} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Dispute Procedure / Limitation</h2>
          <div className="grid gap-3 sm:grid-cols-4">
            <label className="block text-sm font-bold text-gray-700" htmlFor="disputeProcedureRecordType">
              Record type
              <select
                id="disputeProcedureRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={disputeProcedureRecordType}
                onChange={(event) => {
                  const nextType = event.target.value as DisputeProcedureRecordType;
                  setDisputeProcedureRecordType(nextType);
                  setDisputePartyDesignatedCourt(false);
                  setDisputeDefendantResidenceBasis(nextType === 'jurisdiction_basis_notice');
                  setDisputeContractBranchBasis(false);
                  setDisputeTakeoverPlaceBasis(false);
                  setDisputeDeliveryPlaceBasis(false);
                  setDisputeContractingCountryCourt(nextType === 'jurisdiction_basis_notice');
                  setDisputeSecurityForCostsNotRequired(false);
                  setDisputeSamePartiesAndGrounds(nextType === 'pending_or_judgment_bar_notice');
                  setDisputePendingCompetentProceeding(nextType === 'pending_or_judgment_bar_notice');
                  setDisputeJudgmentEnteredByCompetentCourt(false);
                  setDisputeFreshProceedingOnlyIfNotEnforceable(false);
                  setDisputeJudgmentFormalitiesOnly(false);
                  setDisputeMeritsReopeningProhibited(false);
                  setDisputeWilfulMisconductEquivalent(false);
                  setDisputeLimitationPeriodRecorded(nextType === 'limitation_period_record');
                  setDisputeLimitationStartDateExcluded(nextType === 'limitation_period_record');
                  setDisputeClaimBarredNoCounterclaim(false);
                  setDisputeCourtLawExtensionBasis(false);
                  setDisputeWrittenClaimReceived(nextType === 'written_claim_suspension_record');
                  setDisputeCarrierRejectedClaimInWriting(false);
                  setDisputeDocumentsReturned(false);
                  setDisputeClaimPartlyAdmitted(false);
                  setDisputeRemainingPartStillInDispute(false);
                  setDisputeReceiptReplyReturnProofBurden(nextType === 'written_claim_suspension_record');
                  setDisputeFurtherSameObjectNoSuspend(nextType === 'written_claim_suspension_record');
                  setDisputeArbitrationClausePresent(nextType === 'arbitration_clause_record');
                  setDisputeArbitrationTribunalCompetence(nextType === 'arbitration_clause_record');
                  setDisputeTribunalAppliesCmr(nextType === 'arbitration_clause_record');
                }}
              >
                {disputeProcedureRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="disputeClaimCategory">
              Claim category
              <select
                id="disputeClaimCategory"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={disputeClaimCategory}
                onChange={(event) => setDisputeClaimCategory(event.target.value as DisputeProcedureClaimCategory)}
              >
                {disputeProcedureClaimCategories.map((category) => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="disputeLimitationPeriodBasis">
              Limitation period basis
              <select
                id="disputeLimitationPeriodBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={disputeLimitationPeriodBasis}
                onChange={(event) => setDisputeLimitationPeriodBasis(event.target.value as DisputeProcedureLimitationPeriodBasis)}
              >
                {disputeProcedureLimitationPeriodBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="disputeLimitationStartBasis">
              Limitation start basis
              <select
                id="disputeLimitationStartBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={disputeLimitationStartBasis}
                onChange={(event) => setDisputeLimitationStartBasis(event.target.value as DisputeProcedureLimitationStartBasis)}
              >
                {disputeProcedureLimitationStartBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-5">
            {[
              ['Linked claim reference', disputeLinkedClaimId, setDisputeLinkedClaimId],
              ['Linked proceeding reference', disputeLinkedProceedingId, setDisputeLinkedProceedingId],
              ['Linked judgment reference', disputeLinkedJudgmentId, setDisputeLinkedJudgmentId],
              ['Linked written claim reference', disputeLinkedWrittenClaimId, setDisputeLinkedWrittenClaimId],
              ['Linked arbitration clause reference', disputeLinkedArbitrationClauseId, setDisputeLinkedArbitrationClauseId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-4">
            {[
              ['Action filed at', disputeActionFiledAt, setDisputeActionFiledAt],
              ['Judgment entered at', disputeJudgmentEnteredAt, setDisputeJudgmentEnteredAt],
              ['Judgment enforceable at', disputeJudgmentEnforceableAt, setDisputeJudgmentEnforceableAt],
              ['Delivery at', disputeDeliveryAt, setDisputeDeliveryAt],
              ['Takeover at', disputeTakeoverAt, setDisputeTakeoverAt],
              ['Agreed time limit expires at', disputeAgreedTimeLimitExpiresAt, setDisputeAgreedTimeLimitExpiresAt],
              ['Contract made at', disputeContractMadeAt, setDisputeContractMadeAt],
              ['Limitation started at', disputeLimitationStartedAt, setDisputeLimitationStartedAt],
              ['Limitation expires at', disputeLimitationExpiresAt, setDisputeLimitationExpiresAt],
              ['Written claim received at', disputeWrittenClaimReceivedAt, setDisputeWrittenClaimReceivedAt],
              ['Claim rejected at', disputeClaimRejectedAt, setDisputeClaimRejectedAt],
              ['Documents returned at', disputeDocumentsReturnedAt, setDisputeDocumentsReturnedAt],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Articles 31-33 flags</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Party designated court or tribunal', disputePartyDesignatedCourt, setDisputePartyDesignatedCourt],
                ['Defendant residence or business place basis', disputeDefendantResidenceBasis, setDisputeDefendantResidenceBasis],
                ['Contract branch or agency basis', disputeContractBranchBasis, setDisputeContractBranchBasis],
                ['Takeover place basis', disputeTakeoverPlaceBasis, setDisputeTakeoverPlaceBasis],
                ['Delivery place basis', disputeDeliveryPlaceBasis, setDisputeDeliveryPlaceBasis],
                ['Contracting country court or tribunal', disputeContractingCountryCourt, setDisputeContractingCountryCourt],
                ['Security for costs not required recorded', disputeSecurityForCostsNotRequired, setDisputeSecurityForCostsNotRequired],
                ['Same parties and grounds recorded', disputeSamePartiesAndGrounds, setDisputeSamePartiesAndGrounds],
                ['Pending competent proceeding recorded', disputePendingCompetentProceeding, setDisputePendingCompetentProceeding],
                ['Judgment entered by competent court', disputeJudgmentEnteredByCompetentCourt, setDisputeJudgmentEnteredByCompetentCourt],
                ['Fresh proceeding only if judgment not enforceable recorded', disputeFreshProceedingOnlyIfNotEnforceable, setDisputeFreshProceedingOnlyIfNotEnforceable],
                ['Judgment enforceability formalities only recorded', disputeJudgmentFormalitiesOnly, setDisputeJudgmentFormalitiesOnly],
                ['Merits reopening prohibited recorded', disputeMeritsReopeningProhibited, setDisputeMeritsReopeningProhibited],
                ['Wilful misconduct or equivalent default alleged', disputeWilfulMisconductEquivalent, setDisputeWilfulMisconductEquivalent],
                ['Limitation period recorded', disputeLimitationPeriodRecorded, setDisputeLimitationPeriodRecorded],
                ['Limitation start date excluded', disputeLimitationStartDateExcluded, setDisputeLimitationStartDateExcluded],
                ['Claim barred not available by counterclaim or setoff recorded', disputeClaimBarredNoCounterclaim, setDisputeClaimBarredNoCounterclaim],
                ['Court law extension or fresh accrual basis recorded', disputeCourtLawExtensionBasis, setDisputeCourtLawExtensionBasis],
                ['Written claim received', disputeWrittenClaimReceived, setDisputeWrittenClaimReceived],
                ['Carrier rejected claim in writing', disputeCarrierRejectedClaimInWriting, setDisputeCarrierRejectedClaimInWriting],
                ['Documents returned', disputeDocumentsReturned, setDisputeDocumentsReturned],
                ['Claim partly admitted', disputeClaimPartlyAdmitted, setDisputeClaimPartlyAdmitted],
                ['Remaining part still in dispute', disputeRemainingPartStillInDispute, setDisputeRemainingPartStillInDispute],
                ['Receipt reply return proof burden recorded', disputeReceiptReplyReturnProofBurden, setDisputeReceiptReplyReturnProofBurden],
                ['Further same object claims do not suspend recorded', disputeFurtherSameObjectNoSuspend, setDisputeFurtherSameObjectNoSuspend],
                ['Arbitration clause present', disputeArbitrationClausePresent, setDisputeArbitrationClausePresent],
                ['Arbitration tribunal competence recorded', disputeArbitrationTribunalCompetence, setDisputeArbitrationTribunalCompetence],
                ['Tribunal applies CMR Convention', disputeTribunalAppliesCmr, setDisputeTribunalAppliesCmr],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Jurisdiction detail', disputeJurisdictionDetail, setDisputeJurisdictionDetail, 'disputeJurisdictionDetail'],
              ['Party agreement detail', disputePartyAgreementDetail, setDisputePartyAgreementDetail, 'disputePartyAgreementDetail'],
              ['Takeover delivery place detail', disputeTakeoverDeliveryPlaceDetail, setDisputeTakeoverDeliveryPlaceDetail, 'disputeTakeoverDeliveryPlaceDetail'],
              ['Pending action detail', disputePendingActionDetail, setDisputePendingActionDetail, 'disputePendingActionDetail'],
              ['Judgment detail', disputeJudgmentDetail, setDisputeJudgmentDetail, 'disputeJudgmentDetail'],
              ['Limitation calculation detail', disputeLimitationCalculationDetail, setDisputeLimitationCalculationDetail, 'disputeLimitationCalculationDetail'],
              ['Written claim detail', disputeWrittenClaimDetail, setDisputeWrittenClaimDetail, 'disputeWrittenClaimDetail'],
              ['Rejection detail', disputeRejectionDetail, setDisputeRejectionDetail, 'disputeRejectionDetail'],
              ['Arbitration clause detail', disputeArbitrationClauseDetail, setDisputeArbitrationClauseDetail, 'disputeArbitrationClauseDetail'],
              ['Court or tribunal detail', disputeCourtOrTribunalDetail, setDisputeCourtOrTribunalDetail, 'disputeCourtOrTribunalDetail'],
            ].map(([label, value, setter, id]) => (
              <label key={id as string} className="block text-sm font-bold text-gray-700" htmlFor={id as string}>
                {label as string}
                <textarea
                  id={id as string}
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record dispute procedure
          </button>
        </form>

        <form onSubmit={handleMultimodalCarriage} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Multimodal Carriage</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="multimodalRecordType">
              Record type
              <select
                id="multimodalRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={multimodalRecordType}
                onChange={(event) => {
                  const nextType = event.target.value as MultimodalCarriageRecordType;
                  setMultimodalRecordType(nextType);
                  if (nextType === 'article14_unloading_impediment_basis') {
                    setMultimodalGoodsRemainedLoaded(false);
                    setMultimodalGoodsUnloaded(true);
                    setMultimodalArticle14BasisRecorded(true);
                    return;
                  }
                  setMultimodalGoodsRemainedLoaded(true);
                  setMultimodalGoodsUnloaded(false);
                  setMultimodalArticle14BasisRecorded(false);
                }}
              >
                {multimodalCarriageRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="multimodalOtherModeType">
              Other mode type
              <select
                id="multimodalOtherModeType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={multimodalOtherModeType}
                onChange={(event) => setMultimodalOtherModeType(event.target.value as CmrArticle2OtherModeType)}
              >
                {article2OtherModeTypes.map((modeType) => (
                  <option key={modeType.value} value={modeType.value}>{modeType.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Other-mode carriage document reference', multimodalLinkedOtherModeDocumentId, setMultimodalLinkedOtherModeDocumentId, true],
              ['Article 14 impediment reference', multimodalLinkedArticle14ImpedimentId, setMultimodalLinkedArticle14ImpedimentId, multimodalRecordType === 'article14_unloading_impediment_basis'],
              ['Multimodal declaration reference', multimodalLinkedDeclarationId, setMultimodalLinkedDeclarationId, false],
            ].map(([label, value, setter, required]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  required={Boolean(required)}
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 2 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Vehicle carried by other mode', multimodalVehicleCarriedByOtherMode, setMultimodalVehicleCarriedByOtherMode],
                ['Goods remained loaded on vehicle', multimodalGoodsRemainedLoaded, setMultimodalGoodsRemainedLoaded],
                ['Goods unloaded from vehicle', multimodalGoodsUnloaded, setMultimodalGoodsUnloaded],
                ['Article 14 impediment basis recorded', multimodalArticle14BasisRecorded, setMultimodalArticle14BasisRecorded],
                ['Other-mode carriage evidence present', multimodalOtherModeEvidencePresent, setMultimodalOtherModeEvidencePresent],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="multimodalOtherModeDetail">
              Other-mode carriage detail
              <textarea
                id="multimodalOtherModeDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={multimodalOtherModeDetail}
                onChange={(event) => setMultimodalOtherModeDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="multimodalArticle14ImpedimentDetail">
              Article 14 impediment detail
              <textarea
                id="multimodalArticle14ImpedimentDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={multimodalArticle14ImpedimentDetail}
                onChange={(event) => setMultimodalArticle14ImpedimentDetail(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record multimodal carriage
          </button>
        </form>

        <form onSubmit={handleConsignmentNoteCopyAccess} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Consignment Note Copy Access</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="copyRecordType">
            Record type
            <select
              id="copyRecordType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={copyRecordType}
              onChange={(event) => setCopyRecordType(event.target.value as ConsignmentNoteCopyRecordType)}
            >
              {consignmentNoteCopyRecordTypes.map((recordType) => (
                <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
              ))}
            </select>
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Copy access reference', copyLinkedAccessRecordId, setCopyLinkedAccessRecordId],
              ['Handover reference', copyLinkedHandoverRecordId, setCopyLinkedHandoverRecordId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 5.1 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Electronic copy access available', copyElectronicAccessAvailable, setCopyElectronicAccessAvailable],
                ['Entitled party access available', copyEntitledPartyAccessAvailable, setCopyEntitledPartyAccessAvailable],
                ['Sender first copy available', copySenderFirstCopyAvailable, setCopySenderFirstCopyAvailable],
                ['Goods-accompanying second copy available', copyGoodsAccompanyingSecondCopyAvailable, setCopyGoodsAccompanyingSecondCopyAvailable],
                ['Carrier retained third copy available', copyCarrierRetainedThirdCopyAvailable, setCopyCarrierRetainedThirdCopyAvailable],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div>
            <label className="block text-sm font-bold text-gray-700" htmlFor="copyAccessDetail">
              Copy access detail
              <textarea
                id="copyAccessDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={copyAccessDetail}
                onChange={(event) => setCopyAccessDetail(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record consignment note copy access
          </button>
        </form>

        <form onSubmit={handleEcmrReceiptAccessRequest} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">eCMR Article 6.1 Sender Request</h2>
          <label className="block text-sm font-bold text-gray-700" htmlFor="ecmrSenderRequestDetail">
            Sender request detail
            <textarea
              id="ecmrSenderRequestDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={ecmrSenderRequestDetail}
              onChange={(event) => setEcmrSenderRequestDetail(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record eCMR Article 6.1 sender request
          </button>
        </form>

        <form onSubmit={handleEcmrReceiptAccess} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">eCMR Article 6.1 Receipt/Access Handover</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="ecmrReceiptHandoverAt">
            Handover at
            <input
              id="ecmrReceiptHandoverAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={ecmrReceiptHandoverAt}
              onChange={(event) => setEcmrReceiptHandoverAt(event.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Goods receipt reference', ecmrLinkedGoodsReceiptId, setEcmrLinkedGoodsReceiptId],
              ['Shipment identification reference', ecmrLinkedShipmentIdentificationRecordId, setEcmrLinkedShipmentIdentificationRecordId],
              ['eCMR access information reference', ecmrLinkedAccessInformationRecordId, setEcmrLinkedAccessInformationRecordId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">eCMR Protocol Article 6.1 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Sender request received', ecmrSenderRequestReceived, setEcmrSenderRequestReceived],
                ['Receipt for goods provided', ecmrReceiptForGoodsProvided, setEcmrReceiptForGoodsProvided],
                ['Shipment identification information provided', ecmrShipmentIdentificationInformationProvided, setEcmrShipmentIdentificationInformationProvided],
                ['Electronic consignment note access information provided', ecmrElectronicConsignmentNoteAccessInformationProvided, setEcmrElectronicConsignmentNoteAccessInformationProvided],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="ecmrReceiptDetail">
              Goods receipt detail
              <textarea
                id="ecmrReceiptDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={ecmrReceiptDetail}
                onChange={(event) => setEcmrReceiptDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="ecmrShipmentIdentificationDetail">
              Shipment identification detail
              <textarea
                id="ecmrShipmentIdentificationDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={ecmrShipmentIdentificationDetail}
                onChange={(event) => setEcmrShipmentIdentificationDetail(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="ecmrAccessInformationDetail">
              eCMR access information detail
              <textarea
                id="ecmrAccessInformationDetail"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={ecmrAccessInformationDetail}
                onChange={(event) => setEcmrAccessInformationDetail(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record eCMR Article 6.1 receipt/access evidence
          </button>
        </form>

        <form onSubmit={handleSenderParticularsResponsibility} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Sender Particulars Responsibility</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="senderParticularsRecordType">
              Record type
              <select
                id="senderParticularsRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={senderParticularsRecordType}
                onChange={(event) => setSenderParticularsRecordType(event.target.value as SenderParticularsResponsibilityRecordType)}
              >
                {senderParticularsResponsibilityRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="senderParticularsCategory">
              Affected category
              <select
                id="senderParticularsCategory"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={senderParticularsCategory}
                onChange={(event) => setSenderParticularsCategory(event.target.value as SenderParticularsResponsibilityCategory)}
              >
                {senderParticularsResponsibilityCategories.map((category) => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="senderParticularsReviewedAt">
              Reviewed at
              <input
                id="senderParticularsReviewedAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={senderParticularsReviewedAt}
                onChange={(event) => setSenderParticularsReviewedAt(event.target.value)}
              />
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Particular reference', senderParticularsLinkedParticularRecordId, setSenderParticularsLinkedParticularRecordId],
              ['Instruction reference', senderParticularsLinkedInstructionId, setSenderParticularsLinkedInstructionId],
              ['Claim reference', senderParticularsLinkedClaimId, setSenderParticularsLinkedClaimId],
              ['Expense reference', senderParticularsLinkedExpenseRecordId, setSenderParticularsLinkedExpenseRecordId],
              ['Carrier entry reference', senderParticularsLinkedCarrierEntryRecordId, setSenderParticularsLinkedCarrierEntryRecordId],
              ['Consignment-note review reference', senderParticularsLinkedReviewId, setSenderParticularsLinkedReviewId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 7 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Inaccuracy or inadequacy recorded', senderParticularsInaccuracyRecorded, setSenderParticularsInaccuracyRecorded],
                ['Expense/loss/damage claimed', senderParticularsExpenseLossDamageClaimed, setSenderParticularsExpenseLossDamageClaimed],
                ['Carrier entered at sender request', senderParticularsCarrierEnteredAtRequest, setSenderParticularsCarrierEnteredAtRequest],
                ['Sender behalf presumption recorded', senderParticularsSenderBehalfPresumption, setSenderParticularsSenderBehalfPresumption],
                ['CMR statement omission recorded', senderParticularsCmrStatementOmissionRecorded, setSenderParticularsCmrStatementOmissionRecorded],
                ['Carrier liability basis recorded', senderParticularsCarrierLiabilityBasisRecorded, setSenderParticularsCarrierLiabilityBasisRecorded],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Particular detail', senderParticularsParticularDetail, setSenderParticularsParticularDetail, 'senderParticularsParticularDetail'],
              ['Instruction detail', senderParticularsInstructionDetail, setSenderParticularsInstructionDetail, 'senderParticularsInstructionDetail'],
              ['Expense/loss/damage detail', senderParticularsExpenseLossDamageDetail, setSenderParticularsExpenseLossDamageDetail, 'senderParticularsExpenseLossDamageDetail'],
              ['Carrier entry detail', senderParticularsCarrierEntryDetail, setSenderParticularsCarrierEntryDetail, 'senderParticularsCarrierEntryDetail'],
              ['CMR statement omission detail', senderParticularsCmrStatementOmissionDetail, setSenderParticularsCmrStatementOmissionDetail, 'senderParticularsCmrStatementOmissionDetail'],
            ].map(([label, value, setter, fieldId]) => (
              <label key={fieldId as string} className="block text-sm font-bold text-gray-700" htmlFor={fieldId as string}>
                {label as string}
                <textarea
                  id={fieldId as string}
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record sender particulars responsibility
          </button>
        </form>

        <form onSubmit={handleSuccessiveCarrier} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Successive Carriers</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700" htmlFor="successiveCarrierRecordType">
              Record type
              <select
                id="successiveCarrierRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={successiveCarrierRecordType}
                onChange={(event) => setSuccessiveCarrierRecordType(event.target.value as SuccessiveCarrierRecordType)}
              >
                {successiveCarrierRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="successiveAllocationBasis">
              Allocation basis
              <select
                id="successiveAllocationBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={successiveAllocationBasis}
                onChange={(event) => setSuccessiveAllocationBasis(event.target.value as SuccessiveCarrierAllocationBasis)}
              >
                {successiveCarrierAllocationBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="successiveRecoveryStartBasis">
              Recovery limitation start
              <select
                id="successiveRecoveryStartBasis"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={successiveRecoveryStartBasis}
                onChange={(event) => setSuccessiveRecoveryStartBasis(event.target.value as SuccessiveCarrierRecoveryLimitationStartBasis)}
              >
                {successiveCarrierRecoveryStartBases.map((basis) => (
                  <option key={basis.value} value={basis.value}>{basis.label}</option>
                ))}
              </select>
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Successive carrier reference', successiveLinkedSuccessiveCarrierId, setSuccessiveLinkedSuccessiveCarrierId],
              ['Previous carrier reference', successiveLinkedPreviousCarrierId, setSuccessiveLinkedPreviousCarrierId],
              ['Next carrier reference', successiveLinkedNextCarrierId, setSuccessiveLinkedNextCarrierId],
              ['Consignment note copy reference', successiveLinkedConsignmentNoteCopyId, setSuccessiveLinkedConsignmentNoteCopyId, successiveAcceptanceRecordSelected],
              ['Reservation reference', successiveLinkedReservationId, setSuccessiveLinkedReservationId],
              ['Claim reference', successiveLinkedClaimId, setSuccessiveLinkedClaimId],
              ['Compensation payment reference', successiveLinkedCompensationPaymentId, setSuccessiveLinkedCompensationPaymentId],
              ['Compensation assessment reference', successiveLinkedCompensationAssessmentId, setSuccessiveLinkedCompensationAssessmentId, successiveRecoveryRecordSelected],
              ['Proceeding reference', successiveLinkedProceedingId, setSuccessiveLinkedProceedingId],
              ['Carrier agreement reference', successiveLinkedCarrierAgreementId, setSuccessiveLinkedCarrierAgreementId],
            ].map(([label, value, setter, required]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  required={Boolean(required)}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Carrier accepted at', successiveCarrierAcceptedAt, setSuccessiveCarrierAcceptedAt],
              ['Receipt issued at', successiveReceiptIssuedAt, setSuccessiveReceiptIssuedAt],
              ['Compensation paid at', successiveCompensationPaidAt, setSuccessiveCompensationPaidAt],
              ['Final judicial decision at', successiveFinalJudicialDecisionAt, setSuccessiveFinalJudicialDecisionAt],
              ['Recovery claim filed at', successiveRecoveryClaimFiledAt, setSuccessiveRecoveryClaimFiledAt],
              ['Agreement recorded at', successiveAgreementRecordedAt, setSuccessiveAgreementRecordedAt],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  type="datetime-local"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Articles 34-40 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Article 8 reservations entered where applicable', successiveArticle8ReservationsEntered, setSuccessiveArticle8ReservationsEntered],
                ['Action against first carrier', successiveActionAgainstFirstCarrier, setSuccessiveActionAgainstFirstCarrier],
                ['Action against last carrier', successiveActionAgainstLastCarrier, setSuccessiveActionAgainstLastCarrier],
                ['Action against event carrier', successiveActionAgainstEventCarrier, setSuccessiveActionAgainstEventCarrier],
                ['Counterclaim or setoff in same contract', successiveCounterclaimOrSetoff, setSuccessiveCounterclaimOrSetoff],
                ['Multiple permitted carriers joined', successiveMultipleCarriersJoined, setSuccessiveMultipleCarriersJoined],
                ['Payment validity dispute bar recorded', successivePaymentValidityDisputeBar, setSuccessivePaymentValidityDisputeBar],
                ['All concerned carriers may be defendants', successiveAllConcernedCarriersMayBeDefendants, setSuccessiveAllConcernedCarriersMayBeDefendants],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Receipt detail', successiveReceiptDetail, setSuccessiveReceiptDetail, 'successiveReceiptDetail'],
              ['Carrier identity entry detail', successiveCarrierIdentityEntryDetail, setSuccessiveCarrierIdentityEntryDetail, 'successiveCarrierIdentityEntryDetail'],
              ['Reservation detail', successiveReservationDetail, setSuccessiveReservationDetail, 'successiveReservationDetail'],
              ['Action scope detail', successiveActionScopeDetail, setSuccessiveActionScopeDetail, 'successiveActionScopeDetail'],
              ['Allocation detail', successiveAllocationDetail, setSuccessiveAllocationDetail, 'successiveAllocationDetail'],
              ['Insolvency detail', successiveInsolvencyDetail, setSuccessiveInsolvencyDetail, 'successiveInsolvencyDetail'],
              ['Proceeding detail', successiveProceedingDetail, setSuccessiveProceedingDetail, 'successiveProceedingDetail'],
              ['Agreement detail', successiveAgreementDetail, setSuccessiveAgreementDetail, 'successiveAgreementDetail'],
              ['Notice opportunity detail', successiveNoticeOpportunityDetail, setSuccessiveNoticeOpportunityDetail, 'successiveNoticeOpportunityDetail'],
            ].map(([label, value, setter, fieldId]) => (
              <label key={fieldId as string} className="block text-sm font-bold text-gray-700" htmlFor={fieldId as string}>
                {label as string}
                <textarea
                  id={fieldId as string}
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record successive carrier
          </button>
        </form>

        <form onSubmit={handleProhibitedClause} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Article 41 Prohibited Clauses</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm font-bold text-gray-700" htmlFor="prohibitedClauseRecordType">
              Record type
              <select
                id="prohibitedClauseRecordType"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={prohibitedClauseRecordType}
                onChange={(event) => setProhibitedClauseRecordType(event.target.value as ProhibitedClauseRecordType)}
              >
                {prohibitedClauseRecordTypes.map((recordType) => (
                  <option key={recordType.value} value={recordType.value}>{recordType.label}</option>
                ))}
              </select>
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="prohibitedClauseReviewedAt">
              Clause reviewed at
              <input
                id="prohibitedClauseReviewedAt"
                type="datetime-local"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={prohibitedClauseReviewedAt}
                onChange={(event) => setProhibitedClauseReviewedAt(event.target.value)}
              />
            </label>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            {[
              ['Contract term reference', prohibitedLinkedContractTermId, setProhibitedLinkedContractTermId],
              ['Clause review reference', prohibitedLinkedClauseReviewId, setProhibitedLinkedClauseReviewId],
              ['Insurance record reference', prohibitedLinkedInsuranceRecordId, setProhibitedLinkedInsuranceRecordId],
              ['Claim reference', prohibitedLinkedClaimId, setProhibitedLinkedClaimId],
              ['Article 40 agreement reference', prohibitedLinkedArticle40AgreementId, setProhibitedLinkedArticle40AgreementId],
            ].map(([label, value, setter]) => (
              <label key={label as string} className="block text-sm font-bold text-gray-700">
                {label as string}
                <input
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <fieldset className="mb-3 mt-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 41 controls</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {[
                ['Article 40 exception assessed', prohibitedArticle40ExceptionAssessed, setProhibitedArticle40ExceptionAssessed],
                ['Article 40 exception not applicable', prohibitedArticle40ExceptionNotApplicable, setProhibitedArticle40ExceptionNotApplicable],
                ['Nullity and severability recorded', prohibitedNullitySeverabilityRecorded, setProhibitedNullitySeverabilityRecorded],
                ['Review outcome recorded', prohibitedReviewOutcomeRecorded, setProhibitedReviewOutcomeRecorded],
              ].map(([label, checked, setter]) => (
                <label key={label as string} className="flex items-center gap-2 text-sm font-bold text-gray-700">
                  <input
                    type="checkbox"
                    checked={checked as boolean}
                    onChange={(event) => (setter as React.Dispatch<React.SetStateAction<boolean>>)(event.target.checked)}
                  />
                  {label as string}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              ['Clause detail', prohibitedClauseDetail, setProhibitedClauseDetail, 'prohibitedClauseDetail'],
              ['Review detail', prohibitedReviewDetail, setProhibitedReviewDetail, 'prohibitedReviewDetail'],
              ['Insurance benefit detail', prohibitedInsuranceBenefitDetail, setProhibitedInsuranceBenefitDetail, 'prohibitedInsuranceBenefitDetail'],
              ['Burden shift detail', prohibitedBurdenShiftDetail, setProhibitedBurdenShiftDetail, 'prohibitedBurdenShiftDetail'],
              ['Article 40 exception detail', prohibitedArticle40ExceptionDetail, setProhibitedArticle40ExceptionDetail, 'prohibitedArticle40ExceptionDetail'],
            ].map(([label, value, setter, fieldId]) => (
              <label key={fieldId as string} className="block text-sm font-bold text-gray-700" htmlFor={fieldId as string}>
                {label as string}
                <textarea
                  id={fieldId as string}
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={value as string}
                  onChange={(event) => (setter as React.Dispatch<React.SetStateAction<string>>)(event.target.value)}
                />
              </label>
            ))}
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record Article 41 clause
          </button>
        </form>

        <form onSubmit={handleReplacementByOtherMeans} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Replacement by Other Means</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="replacementMedium">
            Replacement medium
            <select
              id="replacementMedium"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={replacementMedium}
              onChange={(event) => setReplacementMedium(event.target.value as ReplacementByOtherMeansMedium)}
            >
              {replacementMedia.map((medium) => (
                <option key={medium.value} value={medium.value}>{medium.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="replacementReason">
            Replacement reason
            <textarea
              id="replacementReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={replacementReason}
              onChange={(event) => setReplacementReason(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="replacementReplacedAt">
            Replaced at
            <input
              id="replacementReplacedAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={replacementReplacedAt}
              onChange={(event) => setReplacementReplacedAt(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="replacementExternalReference">
            External reference
            <input
              id="replacementExternalReference"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={replacementExternalReference}
              onChange={(event) => setReplacementExternalReference(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record replacement evidence
          </button>
        </form>

        <form onSubmit={handleDisposalInstruction} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Disposal Instruction</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalInstructionType">
            Instruction type
            <select
              id="disposalInstructionType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalInstructionType}
              onChange={(event) => setDisposalInstructionType(event.target.value as DisposalInstructionType)}
            >
              {disposalInstructionTypes.map((instructionType) => (
                <option key={instructionType.value} value={instructionType.value}>{instructionType.label}</option>
              ))}
            </select>
          </label>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Article 12 conditions</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalRightNotCeased}
                  onChange={(event) => setDisposalRightNotCeased(event.target.checked)}
                />
                Right not ceased
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalFirstCopyProduced}
                  onChange={(event) => setDisposalFirstCopyProduced(event.target.checked)}
                />
                First copy produced
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalCarrierIndemnityAccepted}
                  onChange={(event) => setDisposalCarrierIndemnityAccepted(event.target.checked)}
                />
                Carrier indemnity accepted
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalInstructionFeasible}
                  onChange={(event) => setDisposalInstructionFeasible(event.target.checked)}
                />
                Instruction feasible
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalDoesNotPrejudiceOtherConsignments}
                  onChange={(event) => setDisposalDoesNotPrejudiceOtherConsignments(event.target.checked)}
                />
                No prejudice to other consignments
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={disposalDoesNotDivideConsignment}
                  onChange={(event) => setDisposalDoesNotDivideConsignment(event.target.checked)}
                />
                Does not divide consignment
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700 sm:col-span-2">
                <input
                  type="checkbox"
                  checked={disposalSuccessiveConsigneeInstructionFinal}
                  onChange={(event) => setDisposalSuccessiveConsigneeInstructionFinal(event.target.checked)}
                />
                Successive consignee instruction final
              </label>
            </div>
          </fieldset>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalLinkedConsignmentNoteCopyRecordId">
            Sender first-copy evidence reference
            <input
              id="disposalLinkedConsignmentNoteCopyRecordId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalLinkedConsignmentNoteCopyRecordId}
              onChange={(event) => setDisposalLinkedConsignmentNoteCopyRecordId(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalRequestedExecutionAt">
            Requested execution at
            <input
              id="disposalRequestedExecutionAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalRequestedExecutionAt}
              onChange={(event) => setDisposalRequestedExecutionAt(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNewDeliveryPlace">
            New delivery place
            <textarea
              id="disposalNewDeliveryPlace"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNewDeliveryPlace}
              onChange={(event) => setDisposalNewDeliveryPlace(event.target.value)}
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNewConsignee">
            New consignee
            <textarea
              id="disposalNewConsignee"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNewConsignee}
              onChange={(event) => setDisposalNewConsignee(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="disposalReason">
            Instruction reason
            <textarea
              id="disposalReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalReason}
              onChange={(event) => setDisposalReason(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record disposal instruction
          </button>
        </form>

        <form onSubmit={handleDisposalExecutionNotice} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Disposal Execution Notice</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNoticeInstructionId">
            Disposal instruction reference
            <input
              id="disposalNoticeInstructionId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNoticeInstructionId}
              onChange={(event) => setDisposalNoticeInstructionId(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNoticeSentAt">
            Notification sent at
            <input
              id="disposalNoticeSentAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNoticeSentAt}
              onChange={(event) => setDisposalNoticeSentAt(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNoticeTargetRole">
            Notification target role
            <select
              id="disposalNoticeTargetRole"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNoticeTargetRole}
              onChange={(event) => setDisposalNoticeTargetRole(event.target.value as DisposalExecutionNoticeTargetRole)}
            >
              {disposalExecutionNoticeTargets.map((target) => (
                <option key={target.value} value={target.value}>{target.label}</option>
              ))}
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="disposalNoticeDetail">
            Notification detail
            <textarea
              id="disposalNoticeDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNoticeDetail}
              onChange={(event) => setDisposalNoticeDetail(event.target.value)}
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="disposalNoticeReason">
            Unable-to-execute reason
            <textarea
              id="disposalNoticeReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={disposalNoticeReason}
              onChange={(event) => setDisposalNoticeReason(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record disposal execution notice
          </button>
        </form>

        <form onSubmit={handleConsigneeDeliveryRequest} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Consignee Delivery Request</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="consigneeDeliveryRequestType">
            Request type
            <select
              id="consigneeDeliveryRequestType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={consigneeDeliveryRequestType}
              onChange={(event) => {
                const nextType = event.target.value as ConsigneeDeliveryRequestType;
                setConsigneeDeliveryRequestType(nextType);
                if (nextType === 'rights_enforcement_notice') {
                  setConsigneeArrivalAtDeliveryPlace(false);
                  setConsigneeDeliveryRequested(false);
                  setConsigneeSecondCopyRequested(false);
                  setConsigneeReceiptCommitment(false);
                  setConsigneeChargesPaymentAcknowledged(false);
                  setConsigneeChargesDisputed(false);
                  setConsigneeSecurityFurnished(false);
                  setConsigneeChargesPaidLines([{ amount: '', currency: 'EUR' }]);
                  setConsigneeChargesPaidAt('');
                  setConsigneeChargesPaymentEvidenceAttachmentId('');
                  setConsigneeSecurityFurnishedAt('');
                  setConsigneeChargesSecurityEvidenceAttachmentId('');
                  setConsigneeRightsReservedNotice(true);
                  setConsigneeLossEstablished(true);
                  setConsigneeArticle19DelayPeriodExpired(false);
                  setConsigneeReceiptDetail('');
                  setConsigneeChargesSecurityDetail('');
                  setConsigneeLinkedDeliveryId('');
                  setConsigneeLinkedSecondCopyRecordId('');
                  setConsigneeGoodsReceived(false);
                  setConsigneeSecondCopyReceived(false);
                  setConsigneeReceiptIssued(false);
                  return;
                }
                if (nextType === 'delivery_receipt_and_second_copy_handover') {
                  setConsigneeArrivalAtDeliveryPlace(false);
                  setConsigneeDeliveryRequested(false);
                  setConsigneeSecondCopyRequested(false);
                  setConsigneeReceiptCommitment(false);
                  setConsigneeChargesPaymentAcknowledged(false);
                  setConsigneeChargesDisputed(false);
                  setConsigneeSecurityFurnished(false);
                  setConsigneeChargesPaidLines([{ amount: '', currency: 'EUR' }]);
                  setConsigneeChargesPaidAt('');
                  setConsigneeChargesPaymentEvidenceAttachmentId('');
                  setConsigneeSecurityFurnishedAt('');
                  setConsigneeChargesSecurityEvidenceAttachmentId('');
                  setConsigneeRightsReservedNotice(false);
                  setConsigneeLossEstablished(false);
                  setConsigneeArticle19DelayPeriodExpired(false);
                  setConsigneeGoodsReceived(true);
                  setConsigneeSecondCopyReceived(true);
                  setConsigneeReceiptIssued(true);
                  setConsigneeChargesSecurityDetail('');
                  setConsigneeEnforcementBasisDetail('');
                  return;
                }
                setConsigneeArrivalAtDeliveryPlace(true);
                setConsigneeDeliveryRequested(true);
                setConsigneeSecondCopyRequested(true);
                setConsigneeReceiptCommitment(true);
                setConsigneeChargesPaymentAcknowledged(true);
                setConsigneeChargesDisputed(false);
                setConsigneeSecurityFurnished(false);
                setConsigneeChargesPaidLines([{ amount: '', currency: 'EUR' }]);
                setConsigneeChargesPaidAt('');
                setConsigneeChargesPaymentEvidenceAttachmentId('');
                setConsigneeSecurityFurnishedAt('');
                setConsigneeChargesSecurityEvidenceAttachmentId('');
                setConsigneeRightsReservedNotice(false);
                setConsigneeLossEstablished(false);
                setConsigneeArticle19DelayPeriodExpired(false);
                setConsigneeEnforcementBasisDetail('');
                setConsigneeLinkedDeliveryId('');
                setConsigneeLinkedSecondCopyRecordId('');
                setConsigneeGoodsReceived(false);
                setConsigneeSecondCopyReceived(false);
                setConsigneeReceiptIssued(false);
              }}
            >
              {consigneeDeliveryRequestTypes.map((requestType) => (
                <option key={requestType.value} value={requestType.value}>{requestType.label}</option>
              ))}
            </select>
          </label>
          {consigneeDeliveryRequestType === 'delivery_after_arrival' ? (
            <>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Delivery claim conditions</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeArrivalAtDeliveryPlace}
                  onChange={(event) => setConsigneeArrivalAtDeliveryPlace(event.target.checked)}
                />
                Arrival at delivery place
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeDeliveryRequested}
                  onChange={(event) => setConsigneeDeliveryRequested(event.target.checked)}
                />
                Delivery requested
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeSecondCopyRequested}
                  onChange={(event) => setConsigneeSecondCopyRequested(event.target.checked)}
                />
                Second copy requested
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeReceiptCommitment}
                  onChange={(event) => setConsigneeReceiptCommitment(event.target.checked)}
                />
                Receipt commitment
              </label>
            </div>
          </fieldset>
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Charge and security conditions</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeChargesPaymentAcknowledged}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setConsigneeChargesPaymentAcknowledged(checked);
                    if (checked) {
                      setConsigneeChargesDisputed(false);
                      setConsigneeSecurityFurnished(false);
                      setConsigneeSecurityFurnishedAt('');
                      setConsigneeChargesSecurityEvidenceAttachmentId('');
                      setConsigneeChargesSecurityDetail('');
                    }
                  }}
                />
                Charges acknowledged
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeChargesDisputed}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setConsigneeChargesDisputed(checked);
                    setConsigneeChargesPaymentAcknowledged(!checked);
                    if (checked) {
                      setConsigneeChargesPaidLines([{ amount: '', currency: 'EUR' }]);
                      setConsigneeChargesPaidAt('');
                      setConsigneeChargesPaymentEvidenceAttachmentId('');
                    } else {
                      setConsigneeSecurityFurnished(false);
                      setConsigneeSecurityFurnishedAt('');
                      setConsigneeChargesSecurityEvidenceAttachmentId('');
                      setConsigneeChargesSecurityDetail('');
                    }
                  }}
                />
                Charges disputed
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeSecurityFurnished}
                  disabled={!consigneeChargesDisputed}
                  onChange={(event) => setConsigneeSecurityFurnished(event.target.checked)}
                />
                Security furnished
              </label>
            </div>
          </fieldset>
          {consigneeChargesPaymentAcknowledged ? (
            <fieldset className="mb-3">
              <legend className="mb-2 block text-sm font-bold text-gray-700">Consignment-note charge payment</legend>
              <div className="space-y-2">
                {consigneeChargesPaidLines.map((line, index) => (
                  <div key={`consignee-charge-${index}`} className="grid gap-2 sm:grid-cols-[1fr_8rem_auto]">
                    <label className="block text-sm font-bold text-gray-700">
                      Amount
                      <input
                        aria-label={`Charge amount ${index + 1}`}
                        inputMode="decimal"
                        className="mt-1 block w-full rounded border border-gray-300 p-2"
                        value={line.amount}
                        onChange={(event) => setConsigneeChargesPaidLines((currentLines) => currentLines.map(
                          (currentLine, currentIndex) => currentIndex === index
                            ? { ...currentLine, amount: event.target.value }
                            : currentLine
                        ))}
                      />
                    </label>
                    <label className="block text-sm font-bold text-gray-700">
                      Currency
                      <input
                        aria-label={`Charge currency ${index + 1}`}
                        maxLength={3}
                        className="mt-1 block w-full rounded border border-gray-300 p-2 uppercase"
                        value={line.currency}
                        onChange={(event) => setConsigneeChargesPaidLines((currentLines) => currentLines.map(
                          (currentLine, currentIndex) => currentIndex === index
                            ? { ...currentLine, currency: event.target.value.toUpperCase() }
                            : currentLine
                        ))}
                      />
                    </label>
                    <button
                      type="button"
                      className="self-end rounded border border-gray-300 px-3 py-2 text-sm font-bold text-gray-700 disabled:opacity-50"
                      disabled={consigneeChargesPaidLines.length === 1}
                      aria-label={`Remove charge line ${index + 1}`}
                      onClick={() => setConsigneeChargesPaidLines((currentLines) => currentLines.filter(
                        (_, currentIndex) => currentIndex !== index
                      ))}
                    >
                      -
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="mt-2 rounded border border-gray-300 px-3 py-2 text-sm font-bold text-gray-700"
                onClick={() => setConsigneeChargesPaidLines((currentLines) => [
                  ...currentLines,
                  { amount: '', currency: '' },
                ])}
              >
                Add charge line
              </button>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeChargesPaidAt">
                  Charges paid at
                  <input
                    id="consigneeChargesPaidAt"
                    type="datetime-local"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={consigneeChargesPaidAt}
                    onChange={(event) => setConsigneeChargesPaidAt(event.target.value)}
                    required
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeChargesPaymentEvidenceAttachmentId">
                  Payment evidence attachment ID
                  <input
                    id="consigneeChargesPaymentEvidenceAttachmentId"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={consigneeChargesPaymentEvidenceAttachmentId}
                    onChange={(event) => setConsigneeChargesPaymentEvidenceAttachmentId(event.target.value)}
                    required
                  />
                </label>
              </div>
            </fieldset>
          ) : null}
          {consigneeChargesDisputed ? (
            <div className="mb-3 grid gap-3 sm:grid-cols-2">
              <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeSecurityFurnishedAt">
                Security furnished at
                <input
                  id="consigneeSecurityFurnishedAt"
                  type="datetime-local"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={consigneeSecurityFurnishedAt}
                  onChange={(event) => setConsigneeSecurityFurnishedAt(event.target.value)}
                  required
                />
              </label>
              <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeChargesSecurityEvidenceAttachmentId">
                Security evidence attachment ID
                <input
                  id="consigneeChargesSecurityEvidenceAttachmentId"
                  className="mt-1 block w-full rounded border border-gray-300 p-2"
                  value={consigneeChargesSecurityEvidenceAttachmentId}
                  onChange={(event) => setConsigneeChargesSecurityEvidenceAttachmentId(event.target.value)}
                  required
                />
              </label>
            </div>
          ) : null}
            </>
          ) : null}
          {consigneeDeliveryRequestType === 'rights_enforcement_notice' ? (
          <fieldset className="mb-3">
            <legend className="mb-2 block text-sm font-bold text-gray-700">Rights enforcement conditions</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeRightsReservedNotice}
                  onChange={(event) => setConsigneeRightsReservedNotice(event.target.checked)}
                />
                Rights reserved notice
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeLossEstablished}
                  onChange={(event) => setConsigneeLossEstablished(event.target.checked)}
                />
                Loss established
              </label>
              <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                <input
                  type="checkbox"
                  checked={consigneeArticle19DelayPeriodExpired}
                  onChange={(event) => setConsigneeArticle19DelayPeriodExpired(event.target.checked)}
                />
                Article 19 delay period expired
              </label>
            </div>
          </fieldset>
          ) : null}
          {consigneeDeliveryRequestType === 'delivery_receipt_and_second_copy_handover' ? (
            <>
              <div className="mb-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeLinkedDeliveryId">
                  Linked delivery ID
                  <input
                    id="consigneeLinkedDeliveryId"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={consigneeLinkedDeliveryId}
                    onChange={(event) => setConsigneeLinkedDeliveryId(event.target.value)}
                  />
                </label>
                <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeLinkedSecondCopyRecordId">
                  Linked second-copy record ID
                  <input
                    id="consigneeLinkedSecondCopyRecordId"
                    className="mt-1 block w-full rounded border border-gray-300 p-2"
                    value={consigneeLinkedSecondCopyRecordId}
                    onChange={(event) => setConsigneeLinkedSecondCopyRecordId(event.target.value)}
                  />
                </label>
              </div>
              <fieldset className="mb-3">
                <legend className="mb-2 block text-sm font-bold text-gray-700">Receipt and handover evidence</legend>
                <div className="grid gap-2 sm:grid-cols-3">
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={consigneeGoodsReceived}
                      onChange={(event) => setConsigneeGoodsReceived(event.target.checked)}
                    />
                    Goods received
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={consigneeSecondCopyReceived}
                      onChange={(event) => setConsigneeSecondCopyReceived(event.target.checked)}
                    />
                    Second copy received
                  </label>
                  <label className="flex items-center gap-2 text-sm font-bold text-gray-700">
                    <input
                      type="checkbox"
                      checked={consigneeReceiptIssued}
                      onChange={(event) => setConsigneeReceiptIssued(event.target.checked)}
                    />
                    Receipt issued
                  </label>
                </div>
              </fieldset>
            </>
          ) : null}
          {consigneeDeliveryRequestType !== 'rights_enforcement_notice' ? (
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="consigneeReceiptDetail">
            Receipt detail
            <textarea
              id="consigneeReceiptDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={consigneeReceiptDetail}
              onChange={(event) => setConsigneeReceiptDetail(event.target.value)}
            />
          </label>
          ) : null}
          {consigneeDeliveryRequestType === 'delivery_after_arrival' && consigneeChargesDisputed ? (
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="consigneeChargesSecurityDetail">
            Charges security detail
            <textarea
              id="consigneeChargesSecurityDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={consigneeChargesSecurityDetail}
              onChange={(event) => setConsigneeChargesSecurityDetail(event.target.value)}
            />
          </label>
          ) : null}
          {consigneeDeliveryRequestType === 'rights_enforcement_notice' ? (
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="consigneeEnforcementBasisDetail">
            Enforcement basis detail
            <textarea
              id="consigneeEnforcementBasisDetail"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={consigneeEnforcementBasisDetail}
              onChange={(event) => setConsigneeEnforcementBasisDetail(event.target.value)}
            />
          </label>
          ) : null}
          <label className="block text-sm font-bold text-gray-700" htmlFor="consigneeReason">
            Request reason
            <textarea
              id="consigneeReason"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={consigneeReason}
              onChange={(event) => setConsigneeReason(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record consignee delivery request
          </button>
        </form>

        <form onSubmit={handleDeliveryConfirmation} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Proof of Delivery</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveredAt">
            Delivered at
            <input
              id="deliveredAt"
              type="datetime-local"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryDeliveredAt}
              onChange={(event) => setDeliveryDeliveredAt(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="recipientName">
            Recipient name
            <input
              id="recipientName"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryRecipientName}
              onChange={(event) => setDeliveryRecipientName(event.target.value)}
              required
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block text-sm font-bold text-gray-700 sm:col-span-3" htmlFor="deliveryLocation">
              Delivery location
              <input
                id="deliveryLocation"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryLocationName}
                onChange={(event) => setDeliveryLocationName(event.target.value)}
                required
              />
            </label>
            <label className="block text-sm font-bold text-gray-700 sm:col-span-2" htmlFor="deliveryCity">
              Delivery city
              <input
                id="deliveryCity"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryCity}
                onChange={(event) => setDeliveryCity(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryCountry">
              Delivery country
              <input
                id="deliveryCountry"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryCountry}
                onChange={(event) => setDeliveryCountry(event.target.value)}
              />
            </label>
          </div>
          <div className="mt-4 border-t pt-4">
            <h3 className="mb-3 font-bold">Consignee reservation</h3>
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryReservationCategory">
              Delivery reservation category
              <select
                id="deliveryReservationCategory"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryReservationCategory}
                onChange={(event) => setDeliveryReservationCategory(event.target.value as DeliveryReservationCategory)}
              >
                {deliveryCategories.map((category) => (
                  <option key={category.value} value={category.value}>{category.label}</option>
                ))}
              </select>
            </label>
            <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryReservationGrounds">
              Delivery reservation grounds
              <textarea
                id="deliveryReservationGrounds"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryReservationGrounds}
                onChange={(event) => setDeliveryReservationGrounds(event.target.value)}
              />
            </label>
            <label className="block text-sm font-bold text-gray-700" htmlFor="deliveryPackageReference">
              Delivery package reference
              <input
                id="deliveryPackageReference"
                className="mt-1 block w-full rounded border border-gray-300 p-2"
                value={deliveryPackageReference}
                onChange={(event) => setDeliveryPackageReference(event.target.value)}
              />
            </label>
          </div>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Confirm proof of delivery
          </button>
        </form>

        <form onSubmit={handleDeliveryReservationAcknowledgement} className="bg-white shadow-md rounded p-4">
          <h2 className="mb-3 text-lg font-bold">Delivery Reservation Acknowledgement</h2>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryAcknowledgementDeliveryId">
            Delivery reference
            <input
              id="deliveryAcknowledgementDeliveryId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryAcknowledgementDeliveryId}
              onChange={(event) => setDeliveryAcknowledgementDeliveryId(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryAcknowledgementReservationId">
            Delivery reservation reference
            <input
              id="deliveryAcknowledgementReservationId"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryAcknowledgementReservationId}
              onChange={(event) => setDeliveryAcknowledgementReservationId(event.target.value)}
              required
            />
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryAcknowledgementDecision">
            Delivery acknowledgement decision
            <select
              id="deliveryAcknowledgementDecision"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryAcknowledgementDecision}
              onChange={(event) => setDeliveryAcknowledgementDecision(event.target.value as DeliveryReservationAcknowledgementDecision)}
            >
              <option value="acknowledged">Acknowledge</option>
              <option value="rejected">Reject</option>
            </select>
          </label>
          <label className="mb-3 block text-sm font-bold text-gray-700" htmlFor="deliveryAcknowledgementComment">
            Delivery acknowledgement comment
            <textarea
              id="deliveryAcknowledgementComment"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={deliveryAcknowledgementComment}
              onChange={(event) => setDeliveryAcknowledgementComment(event.target.value)}
            />
          </label>
          <button type="submit" className="mt-4 rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
            Record delivery reservation acknowledgement
          </button>
        </form>
      </div>

      <section aria-label="Supplementary documents" className="mt-4 bg-white shadow-md rounded p-4">
        <h2 className="mb-3 text-lg font-bold">Supplementary Documents</h2>
        <form onSubmit={handleSupplementaryUpload} className="mb-5 grid gap-3 md:grid-cols-2">
          <label className="block text-sm font-bold text-gray-700" htmlFor="attachmentFile">
            Attachment file
            <input
              id="attachmentFile"
              type="file"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              onChange={(event) => setAttachmentFile(event.target.files?.[0] || null)}
              required
            />
          </label>
          <label className="block text-sm font-bold text-gray-700" htmlFor="attachmentType">
            Document type
            <input
              id="attachmentType"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={attachmentType}
              onChange={(event) => setAttachmentType(event.target.value)}
              placeholder="customs_invoice"
            />
          </label>
          <label className="block text-sm font-bold text-gray-700 md:col-span-2" htmlFor="attachmentDescription">
            Document description
            <input
              id="attachmentDescription"
              className="mt-1 block w-full rounded border border-gray-300 p-2"
              value={attachmentDescription}
              onChange={(event) => setAttachmentDescription(event.target.value)}
            />
          </label>
          <div className="md:col-span-2">
            <button type="submit" className="rounded bg-blue-600 px-4 py-2 font-bold text-white hover:bg-blue-700">
              Upload supplementary document
            </button>
          </div>
        </form>

        {loadingAttachments ? (
          <p>Loading supplementary documents...</p>
        ) : attachments.length === 0 ? (
          <p>No supplementary documents recorded for this API document.</p>
        ) : (
          <div>
            <p className="mb-3 text-sm text-gray-700">
              {attachments.length} {attachments.length === 1 ? 'documento suplementario disponible' : 'documentos suplementarios disponibles'} · metadatos privados retenidos en backend
            </p>
            <ul className="divide-y divide-gray-200">
              {attachments.map((attachment, index) => (
                <li key={attachment.attachmentId} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <p className="font-medium text-gray-900">Documento suplementario {index + 1}</p>
                    <p className="text-sm text-gray-600">
                      {attachment.documentType ? 'tipo documental disponible' : 'tipo documental no especificado'} · {attachment.mimeType ? 'MIME documentado' : 'MIME no documentado'} · {attachment.digestAvailable ? 'digest backend disponible' : 'digest backend no disponible'}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleDownload(attachment)}
                    className="rounded bg-gray-100 px-3 py-2 text-blue-700 hover:bg-gray-200"
                  >
                    Abrir documento suplementario {index + 1}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
};

export default OperationalEvidenceWorkflows;
